/* eslint-disable max-lines */
/* eslint-disable @typescript-eslint/naming-convention */
import { ResourceLanguage } from 'i18next';
import { MRT_Localization_EN } from 'material-react-table/locales/en';
import { ATLunchDayListElementDTOPlaceEnum, ATLunchLineErrorDTOLineErrorsEnum } from 'probonio-shared-ui/api';

const translation: ResourceLanguage = {
  authModule: {
    errorMessage: 'An error occurred. Please login again.',
    processingLogin: {
      title: 'Logging in',
      text: 'Login details are being processed, please wait...',
    },
    registrationExpired: {
      title: 'Registration link has expired',
      text: 'Your registration link has expired. Your employer can generate and resend a new one in the user management page.',
    },
    socialLoginError: {
      title: 'Login method not linked',
      text: 'The selected login method is not linked to a Probonio account. Please use the login method your account was created with, or contact Probonio support.',
    },
  },
  departmentModule: {
    all: 'All',
  },
  errorModule: {
    defaultMessage: 'An error occurred.',
    forbiddenMessage: 'Missing authorization',
    wafBlockedMessage: 'Your request was blocked. Please try again later.',
    wafCountryBlockedMessage: 'Access from your country is not available.',
    causeId: 'Cause-ID:',
    form: {
      min: 'Value must be higher or equal to {{value}}',
      max: 'Value must be lower or equal to {{value}}',
      pattern: 'Please enter correct data type',
      positive: 'Value must be higher than zero',
      required: 'Value is required',
    },
    goHome: 'Back to dashboard',
    unhandled: {
      headlineLarge: 'Oh no...',
      headlineSmall: 'Technical error',
      message: 'A technical problem has occurred. Please try again later.',
    },
    notFound: {
      headlineLarge: '404',
      headlineSmall: 'An error occurred.',
      message: 'Please check your URL, or tap here to go back to the homepage.',
    },
    unauthorized: {
      headlineLarge: '403',
      headlineSmall: 'You do not have access to the specified resource',
      message: 'Please check your credentials.',
    },
    pageSizeToTiny: {
      header: 'Mobile view not supported',
      text: 'The employer portal does not currently support mobile view. Please use a laptop/desktop to use the portal',
      button: 'To the employee app',
    },
    incompleteProfile: {
      header: 'Complete your profile',
      text: 'To use Probonio, you must first complete your profile. Please fill in the missing information to continue.',
      button: 'Complete profile',
    },
    accountMerged: {
      header: 'Your login has changed',
      text: 'Bikeleasing and Probonio are now united. Your Probonio account has been merged with your Bikeleasing account.<br/><br/>You can now manage all benefits with this account. Please log out and log in again with your Bikeleasing email address.',
      button: 'Go to login',
    },
    wrongPreferredPortal: {
      header: 'This portal is not enabled for your company',
      text: 'This portal is not enabled for your company. Your company currently uses the Bikeleasing Portal for benefit management. If you would like to change this, please contact the Bikeleasing Support Team.',
      button: 'Go to the Bikeleasing portal',
    },
  },
  maintenanceModeModule: {
    maintenancePage: {
      headlineLarge: 'Maintenance mode active',
      headlineSmall: 'The employer portal is currently undergoing maintenance. Please try again later.',
      message: '',
    },
  },
  navigationModule: {
    brand: 'Probonio',
    title: 'Employer Portal',
    titleFull: 'Probonio Employer Portal',
    login: 'Login',
    logout: 'Logout',
    linkToApp: 'To the employee app',
    manual: 'Show manual',
    home: 'Dashboard',
    toHome: 'To dashboard',
    openDrawerMenu: 'Show Menu',
    closeDrawerMenu: 'Close Menu',
    referralCode: {
      dialogTitle: 'Recommend Probonio',
      dialogDescription: 'Recommend Probonio to friends or relatives now and receive:',
      code: 'Referral code',
      howItWorks: 'How it works',
      bonusOneTitle: '€50 gift card',
      bonusOneDesc: 'For you',
      bonusTwoTitle: '30% Discount',
      bonusTwoDesc: 'For the new company in the first 3 months',
      dialogHowTo: 'Simply pass on this code:',
      dialogFooter: '* Only applies to companies with at least 10 employees',
    },
    unsupportedBrowserModal: {
      buttonTitle: 'Please update your browser',
      buttonSubTitle: 'We kindly ask you to update it, as otherwise unexpected errors may occur',
      dialogTitle: 'Browser not supported',
      description_one: 'Unfortunately, your browser version is no longer supported, which may cause unexpected errors.',
      description_two: 'We kindly ask you to update your browser version.',
      actions: {
        noReopen: 'Hide',
        close: 'Close',
      },
    },
    userEdit: {
      dialogTitle: 'Settings',
      title: 'Title',
      salutation: { title: 'Salutation', MR: 'Mr.', MS: 'Ms.', DIVERS: 'Diverse', COMPANY: 'Company', notSet: 'Not set' },
      success: 'Changes saved!',
      error: 'Failed to save changes!',
      firstName: 'First name',
      lastName: 'Last name',
      mail: 'E-Mail',
      locale: 'Language',
      localeSwitch: {
        en: 'English',
        de: 'German',
      },
    },
    missingEmployeeInformation: {
      title: 'Almost there!',
      description: 'Enter your personal details.',
    },
    changeTenantDialog: {
      title: 'Switch company',
      text: 'Select the desired company to open the employer portal for this company.',
      select: 'Company',
      submit: 'Switch',
      myCompanies: 'My companies',
      otherCompanies: 'Other',
      searchPlaceholder: 'Search tenant...',
      searchResultCount: '{{from}}–{{to}} of {{total}} results',
      previousPage: 'Previous page',
      nextPage: 'Next page',
      linkedTenantNotAccessible: 'The linked company could not be opened.',
    },
    menuHeader: {
      overview: 'Overview',
      employees: 'Employees',
      benefits: 'Benefits',
      customBenefits: 'Custom Benefits',
      lunch: 'Lunch vouchers',
      application: 'Portal',
      discountCode: 'Discount Codes',
    },
    menuItem: {
      manageEmployees: 'Employee data',
      manageBenefits: 'Manage benefits',
      exploreBenefits: 'Find new benefits',
      reviewReceipts: 'Evaluation',
      siteNotice: 'Imprint',
      dataPrivacy: 'Privacy',
      gdprInformation: 'GDPR Information',
      tenantSettings: 'Settings',
      manual: 'Show manual',
      account: 'Account settings',
      referral: 'Recommend Probonio',
      userProfile: 'My profile',
      contactSupport: 'Contact support',
    },
    appearance: {
      label: 'Appearance',
      light: 'Light',
      dark: 'Dark',
      system: 'System',
    },
    badgeLabel: {
      new: 'New',
    },
  },
  firstLoginModule: {
    title: 'Welcome to the Probonio employer portal',
    textIntroduction:
      'Our employer portal combines all the important functions in just one tool and ensures that you can easily and effectively manage your benefits and adjust them at any time.',
    textHandbook:
      'We have created a Probonio user manual for you so that you can use your employer portal to the full and start directly. Here you will find information and tips on how to:',
    listSettings: 'Manage your settings',
    createEmployees: 'Create employees',
    modifyBenefits: 'Adapt benefits individually according to your wishes',
    handBookButton: 'Open Handbook',
    support: 'If you have any further questions, please contact us - we will be happy to support you!',
    team: 'Your Probonio-Team',
    doNotShowAgain: 'Do not show again',
    close: 'Use employer portal',
  },
  filterModule: {
    department: 'Department',
    costCenter: 'Cost center',
    workLocations: 'Work locations',
    all: 'All',
  },
  usersModule: {
    title: 'Employee Master data',
    createNewUser: 'Add new User',
    createNewUserFields: {
      lead: 'You can also invite your employees via a registration link. Employees who register via this link appear in the employer portal.',
      putEmail: 'Please enter one or more E-Mails',
      helperEmail:
        'You can upload email addresses directly from any source (e.g. Word, Excel, PDF). To do this, select your entire document (CTRL + A), copy the selection (CTRL + C) and paste the selection into the lower field (CTRL + V).',
      emailAddress: 'E-Mail addresses',
      duplicatedMails: 'One or more E-Mails have already been added:',
      notValidMail: 'Please remove invalid E-Mails!',
      notMoreThanThree: 'Please do not insert more than three E-Mails!',
      inviteButton: 'Invite immediately',
      createButton: 'Save for later configuration',
      sendMail: 'Invite employee now',
      sendMailTooltip:
        'We recommend that you only send the invitation once all benefits have been set up. Otherwise, no benefits will initially be visible in the employee app. <0>Details</0>',
      inviteByCode: 'Invite employees via registration code',
      registrationCode: 'Registration code',
      codeHelper: 'Alternatively, your employees can enter this code during registration',
      registrationLink: 'Registration link',
      linkHelper: 'Just copy this link and send it to your employees',
      orDivider: 'or',
      codeChange: 'Change registration code',
      codeWarning: 'Attention! The previous registration code becomes invalid and can no longer be used!',
      codeChangeConfirm: 'I want to change the registration code anyway',
      template: 'Template for benefit configuration',
      templateHelper:
        'Optionally, you can assign a predefined benefit configuration to all new employees. Select a template here for this purpose.',
      accountServiceErrors: {
        emailAlreadyExists: 'This email address is already used by another employee.',
        parentCompanyNotFound: "We couldn't assign your company. Please contact support.",
        parentCompanyDeleted: "We couldn't assign your company. Please contact support.",
      },
    },
    profile: 'PROFILE',
    success: 'Successfully invited',
    userDetails: 'Employee: {{name}}',
    userMenu: {
      settings: 'Profile settings',
      editSettings: 'Edit Profile',
      profile: 'Profile',
    },
    duplicates: {
      alert: {
        message_one:
          '{{count}} employee has a duplicate email address or personnel number. This can cause problems in payroll exports. Please review the entry.',
        message_other:
          '{{count}} employees have duplicate email addresses or personnel numbers. This can cause problems in payroll exports. Please review the entries.',
        action: 'Show affected only ({{count}})',
      },
      tooltip: {
        CLAIMABLE: {
          mail: '{{value}} is intended for this employee but not assigned to any account yet. The profile lets you decide who gets it.',
          employeeNumber:
            '{{value}} is intended for this employee but not assigned to any account yet. The profile lets you decide who gets it.',
        },
        MISMATCH: {
          mail: 'During the migration this address would be replaced by {{value}}. The profile lets you decide which one applies.',
          employeeNumber: 'During the migration this number would be replaced by {{value}}. The profile lets you decide which one applies.',
        },
        CONFLICT: {
          mail: '{{value}} is already assigned to another employee. A different contact email has to be entered in the profile.',
          employeeNumber:
            '{{value}} is already assigned to another employee. A different employee number has to be entered in the profile.',
        },
      },
      mismatchLabel: {
        mail: 'Replaced during migration',
        employeeNumber: 'Replaced during migration',
      },
      differsLabel: {
        mail: 'Duplicate',
        employeeNumber: 'Duplicate',
      },
      conflictLabel: {
        mail: 'Email already taken',
        employeeNumber: 'Employee number already taken',
      },
    },
    userForm: {
      lead: `<p>Please enter all required data to add a new employee to your company account.</p>
      <p>After clicking on "Save", an invitation will be sent to the employee by email, through which he can set up his access.</p>`,
      section: {
        employeeInfo: 'Core data',
        role: 'Role',
        benefits: 'Benefits',
      },
      row: {
        personal: {
          title: 'Personal Information',
          info: 'The contact email address is the email address used for communication with the employee. Separately, the employee can freely choose a login email address during registration. This email can only be changed by the employee themselves or by our support team.<br/><br/>Marital status and children are required for calculating the recreation allowance.<br/><br/>The private address is needed when ordering credit cards to set up the associated credit account.',
        },
        professional: {
          title: 'Professional Information',
          info: 'The personal employee number is collected to facilitate the execution of tax export processes. Similar to the email address, it also serves as a unique identifier. Providing the department and hire date simplifies the filtering of employees to display information and data specifically for certain groups of employees.',
        },
      },

      field: {
        status: {
          label: 'Status',
        },
        salutation: {
          label: 'Salutation',
          MR: 'Mr.',
          MS: 'Ms.',
          DIVERS: 'Diverse',
          COMPANY: 'Company',
          notSet: 'Not set',
        },
        title: 'Title',
        firstName: 'First name',
        lastName: 'Last name',
        mail: 'Email',
        loginMail: 'Login Email',
        mailDisabledTooltip:
          'The login email address can be edited by the employee themselves or by our support. Please contact us with the new and old email addresses: support@probonio.de',
        phone: 'Telephone number',
        phoneMobile: 'Mobile number',
        department: 'Department',
        costCenter: 'Cost center',
        workLocations: 'Work location(s)',
        workLocationsPlaceholder: 'enter a location',
        employeeNumber: 'Employee number',
        employeeNumberInvalid: 'The employee number must not be 0.',
        employeeNumberTooLong: 'The employee number must not be longer than {{value}} characters.',
        employeeTypeLabel: 'Type of employment',
        hireDate: 'Hire date',
        birthDate: 'Birthdate',
        role: 'User roles',
        activeBenefits: 'Available benefits',
        statusLabel: 'Status',
        terminationDate: {
          label: 'Termination date',
          value: '{{date,monthAndYear}}',
        },
        isSyncedFromHRSystem: '(imported from HR system)',
        homeAddressStreet: 'Street and house number',
        homeAddressRegion: 'State',
        homeAddressStreetLine2: 'Address line 2',
        homeAddressPostalCode: 'Postal code',
        homeAddressCity: 'City',
        homeAddressCountry: 'Country',
        isAddressEnabled: 'Employee address',
      },
      validation: {
        invalidCountry: 'Please choose a valid country from the dropdown list.',
      },
      registrationCodeMask: 'Show registration code',
      copyRegistrationLink: 'Copy registration link',
      mailDeliveryErrorInfo:
        '<p>Unfortunately, we could not successfully deliver the mail.</p>In most cases this is due to a typo in the mail address, please check it. If the mail address is wrong, delete the employee and create it again with the correct mail address.',
      duplicate: {
        chip: {
          mail: { CLAIMABLE: 'Duplicate email', MISMATCH: 'Email will be replaced', CONFLICT: 'Email already taken' },
          employeeNumber: {
            CLAIMABLE: 'Duplicate employee number',
            MISMATCH: 'Employee number will be replaced',
            CONFLICT: 'Employee number already taken',
          },
        },
        claim: {
          title: {
            mail: 'Duplicate email – confirmation required',
            employeeNumber: 'Duplicate employee number – confirmation required',
            both: 'Duplicate email and employee number – confirmation required',
          },
          body: {
            mail: 'The email <strong>{{mail}}</strong> is intended for several employees but may only be assigned to one. Confirm below if it belongs to this employee – the others will then need a new address. Afterwards it can only be changed via our support.',
            employeeNumber:
              'The employee number <strong>{{employeeNumber}}</strong> is intended for several employees but may only be assigned to one. Confirm below if it belongs to this employee – the others will then need a new number. Afterwards it can only be changed via our support.',
            both: 'The email <strong>{{mail}}</strong> and the employee number <strong>{{employeeNumber}}</strong> are intended for several employees but may only be assigned to one. Confirm below what belongs to this employee – the others will then need new values.',
          },
          checkbox: {
            mail: 'I confirm that <strong>{{mail}}</strong> should be used for this account',
            employeeNumber: 'I confirm that the employee number <strong>{{employeeNumber}}</strong> should be used for this account',
          },
          checkboxDisabledHint: {
            mail: 'Only available as long as no different contact email is entered.',
            employeeNumber: 'Only available as long as no different employee number is entered.',
          },
        },
        mismatch: {
          title: {
            mail: 'Contact email will be replaced during the migration',
            employeeNumber: 'Employee number will be replaced during the migration',
            both: 'Contact email and employee number will be replaced during the migration',
          },
          body: {
            mail: 'During the migration the stored contact email would be replaced by <strong>{{mail}}</strong>. Confirm below if the address entered here should apply instead – it is then kept permanently.',
            employeeNumber:
              'During the migration the stored employee number would be replaced by <strong>{{employeeNumber}}</strong>. Confirm below if the number entered here should apply instead – it is then kept permanently.',
            both: 'During the migration the stored contact email would be replaced by <strong>{{mail}}</strong> and the employee number by <strong>{{employeeNumber}}</strong>. Confirm below if the values entered here should apply instead – they are then kept permanently.',
          },
          checkbox: {
            mail: 'I confirm that the contact email entered here applies and is not replaced by <strong>{{mail}}</strong>',
            employeeNumber:
              'I confirm that the employee number entered here applies and is not replaced by <strong>{{employeeNumber}}</strong>',
          },
        },
        fieldHint: {
          MISMATCH: {
            mail: 'Will be replaced by <strong>{{value}}</strong> during the migration – confirm above if this address should apply.',
            employeeNumber:
              'Will be replaced by <strong>{{value}}</strong> during the migration – confirm above if this number should apply.',
          },
          CLAIMABLE: {
            mail: 'During the migration the contact email will be replaced by <strong>{{value}}</strong>. Confirm above to adopt it, or enter a different address here.',
            employeeNumber:
              'During the migration the employee number will be replaced by <strong>{{value}}</strong>. Confirm above to adopt it, or enter a different number here.',
          },
          CONFLICT: {
            mail: 'During the migration the contact email is meant to be replaced by <strong>{{value}}</strong> – but that address is already assigned to another employee. Please enter a different address here.',
            employeeNumber:
              'During the migration the employee number is meant to be replaced by <strong>{{value}}</strong> – but that number is already assigned to another employee. Please enter a different number here.',
          },
        },
        peers: {
          mail: 'This email address is also intended for:',
          employeeNumber: 'This personnel number is also intended for:',
        },
        syncBlocked: {
          title: 'Master data temporarily locked',
          body: 'The same email address or personnel number is currently intended for another employee. While that is the case the master data of this employee cannot be saved. Please resolve the entry on the other employee.',
          peers: 'Employees involved:',
        },
        conflict: {
          title: {
            mail: 'Email no longer available',
            employeeNumber: 'Employee number no longer available',
            both: 'Email and employee number no longer available',
          },
          body: {
            mail: 'The email <strong>{{mail}}</strong> is already assigned to another employee. Please enter a new contact email to continue.',
            employeeNumber:
              'The employee number <strong>{{employeeNumber}}</strong> is already assigned to another employee. Please enter a new employee number to continue.',
            both: 'The email <strong>{{mail}}</strong> and the employee number <strong>{{employeeNumber}}</strong> are already assigned to other employees. Please enter new values to continue.',
          },
        },
        deleteAlternative:
          'If you no longer need this account, you can delete it instead. To do so, first terminate the employment at the bottom of this page and then delete the account.',
        saveDisabledTooltip:
          'Please resolve the entry at the top of the form first. While it is open the remaining master data cannot be saved.',
      },
      terminateEmployment: {
        button: 'Terminate employment',
        terminateNow: 'Terminate employment now',
        planTerminate: 'Plan termination',
        activate: 'Reactivate employee',
        toBeTerminated: 'Terminates on {{date,date}}',
        reset: 'Reset planned termination',
        dialogTitle: 'Terminate employment',
        infoText:
          "If you terminate your employee's employment, he or she can still log in to the employee app to redeem any vouchers he or she has already received. However, all active benefits will be deactivated on the selected date.",
        planInfo:
          'If termination is planned for the future, optionally enter the desired month at the end of which the employment should end.',
        reactivateTitle: 'Reactivate employee',
        reactivateText: 'Do you really want to reactivate the employee? Afterwards, configure the desired benefits again.',
        reactivateSuccess: 'Employee activated',
        deactivateSuccess: 'Termination saved',
        terminationNotPossibleBenefit:
          'The employment cannot be terminated at the selected date because a voucher invoice has already been created for the employee. Please select another date.',
      },
      sourceFilter: {
        hide: 'Hide from employee list',
        show: 'Show in employee list',
      },
      button: {
        save: 'Save',
        cancel: 'Discard',
        changeAvatar: 'Change profile picture',
      },
      slider: {
        toggleInfo: 'Activate and deactivate user. You can only delete inactive users!',
      },
      deleteUser: {
        button: 'Delete employee',
        title: 'Delete employee',
        message: 'This will irreversibly delete all user data from {{userName}} .',
        securityQuestion: 'Please enter the word "{{securityQuestionValue}}" to confirm.',
        securityQuestionValue: 'Delete',
        warningAlert: 'The employee no longer has access to his app, unredeemed or saved vouchers expire.',
      },
      resendInvitation: {
        button: 'Reinvite',
      },
      applyTemplate: {
        button: 'Apply template',
        dialogTitle: 'Configure benefits from template',
        info: "Select the template you want to apply to the employee's benefit configuration. All benefits configured in the template will start from the date selected below.",
        infoManage: 'Manage the available templates in the <0>Company Settings</0>.',
        noTemplates: 'There are no benefit templates defined.',
        emptySelection: 'No template',
        infoManyChanges: 'These changes affect {{count}} employees.',
        activeWarning: 'Employee benefits that are already active or planned remain unaffected by the change.',
        field: {
          template: 'Template',
        },
      },
      confirmMailDialog: {
        title: 'Confirm contact email change',
        text: "Are you sure you want to change the employee's contact email address to <strong>{{mail}}</strong>?",
        pendingInfo: 'The employee will automatically receive a new registration mail. The previous mail loses its validity.',
      },
      benefit: {
        active: 'Active since {{val, date}}',
        inactive: 'Inactive',
        LUNCH: {
          name: 'Lunch vouchers',
          description: '',
        },
        MOBILITY: {
          name: 'Mobility',
        },
        INTERNET: {
          name: 'Internet benefit',
        },
        KITA: {
          name: 'Daycare subsidy',
        },
        BAV: {
          name: 'Company pension plan',
        },
        COUPONS: {
          name: 'Gift cards',
        },
        COUPONS_V2: {
          name: 'Gift cards flex',
          giftsName: 'Presents',
        },
        GIFTS: {
          name: 'Presents',
          active: 'Next payout on {{val, date}}',
        },
        CUSTOM: {
          name: 'More Benefits',
        },
        BIKE: {
          name: 'Company bike',
        },
        BIKELEASING_V2: {
          name: 'Bikeleasing',
        },
        BIKELEASING: {
          name: 'Bikeleasing',
        },
        NEWS: {
          name: 'Employee communication',
        },
        FLEX: {
          name: 'Flex Budget',
        },
        RECREATION: {
          name: 'Recreation allowance',
        },
        FITNESS: {
          name: 'Corporate Fitness',
        },
        WELLPASS: {
          name: 'Corporate Fitness',
        },
        CREDIT_CARD: {
          name: 'Credit Card',
        },
        DISCOUNT_CODE: {
          name: 'Employee Discounts',
        },
        AT_LUNCH: {
          name: 'Lunch vouchers',
        },
        AT_MOBILITY: {
          name: 'Public Transport Ticket',
        },
        CAR: {
          name: 'JobAuto',
        },
        IT_LEASING: {
          name: 'IT-Leasing',
        },
      },
    },
    usersList: {
      header: {
        name: 'Employee',
        employeeNumber: 'Employee number',
        hireDate: 'Hire date',
        roles: 'Role',
        activeBenefits: 'Active Benefits',
        status: 'Status',
        department: 'Department',
        recipients: 'Recipients',
        salutation: 'Salutation',
        birthDate: 'Birthdate',
        date: 'date',
        homeAddress: 'Address',
      },
      noResults: 'No employees found',
      statusFilterLabel: 'Employee status',
      includeHidden: 'Show hidden employees',
      excludeHidden: 'Hide hidden employees',
      hiddenMarker: {
        tooltip: 'This employee is hidden from the list',
        confirmTitle: 'Show employee again',
        confirmText: 'Do you want to show this employee in the employee list again?',
      },
      isSyncedFromHRSystem: 'Employee was imported from connected HR system',
      importedFromBikeleasing: 'Employee was imported from Bikeleasing',
      hrSystemTemplateApplied: "Template: '{{name}}' applied via HR interface",
      exportDialog: {
        title: 'Data export',
        message:
          'Note: activations for the upcoming month can still be changed until the 25th of the current month — the export reflects the current state.',
        startMonth: 'From',
        endMonth: 'To',
        cancel: 'Cancel',
        download: 'Download',
        benefits: 'Benefits',
        benefitsAll: 'All benefits',
        noUsedBenefits: 'No benefits were used in the selected period. There is nothing to export.',
      },
    },
    profileActivationList: {
      title: 'Benefits',
      currentBenefits: 'Current',
      upcomingBenefits: 'Upcoming',
      addBenefit: 'Add Benefit',
      startDate: 'Start Date',
      endDate: 'End Date',
      active: 'Active',
      giftsSelectionGroup: 'Gifts and Tokens',
      managedByTemplate: 'Managed by template',
      hrSystemTemplateApplied: "Template: '{{name}}' applied via HR interface",
      noBenefitsActive:
        'Currently, there are no employee benefits active or planned. You can activate benefits individually using the buttons above or apply your predefined benefit templates.',
      noBenefitsActiveTerminated:
        "It's not possible to assign benefits to this employee as they have left the company. You can reactivate the employee in the profile settings.",
    },
    profileActivationHistory: {
      title: 'Benefit history',
      buttonTooltip: 'View history of benefit activations',
      buttonTooltipHide: 'Hide history of benefit activations',
      tableHeader: 'Benefit activations',
      tableHeaderGifts: 'Gifts',
      tableHeaderGiftsV2: 'Gifts and tokens (flex)',
    },
    notChanged: 'No changes were made!',
    saveChanges: 'Changes saved',
    saveConflictEmployeeNumber: 'The employee number is already in use',
    saveConflictMail: 'The email is already in use',
    deactivationError: 'Deactivation not possible, as vouchers invoice has already been created',
    deletionError: 'Deletion not possible, as vouchers invoice has already been created',
    mailSent: 'Registration mail sent successfully',
    unlockUser: 'Employee unlocked successfully',
    statusTerminated: 'terminated',
    employeeNotFound: 'Employee not found',
    employeeNotFoundInfo: 'The employee may have been deleted or the URL entered is no longer valid.',
  },
  benefitsModule: {
    bulkActivation: {
      noNewActivationsDone: 'No employees were found for whom the benefit can be activated.',
      newActivationsSomeErrors: '{{successCount}} employees activated, {{errorCount}} skipped',
      newActivationsError: 'Benefit could not be activated for selected employees',
      newActivationsSuccess: 'Benefit activated for {{count}} employees',
      activatedCount: '{{count}} activated',
    },
    bulkResult: {
      processed: '{{count}} employees processed:',
      skipped: '{{count}} skipped',
      skippedReason: '{{count}} skipped: {{reason}}',
      adjusted: '{{count}} applied, end date adjusted',
      blocked: '{{count}} blocked',
      cappedSuffix: 'of which {{count}} had their activation capped to the termination date',
      missingCreditCard: 'Activation is only possible with a valid credit card',
    },
    conflictError: 'The selected benefits are already active.',
    nonCashBenefits: {
      available: 'Available',
      unavailable: 'Unavailable',
      infoTitle: 'Possible payout types for the non-cash benefit',
      activationDialog: { error: 'Please select at least one option!' },
      COUPONS: {
        name: 'Vouchers',
        requirement: 'Available in €5 increments',
        infoText: `To offer vouchers to your employees, you can top up the voucher budget with just a few clicks and adjust it flexibly at any time. With the default automatic top-up setting, you will automatically receive an invoice each month for the costs incurred in the following month.
          <br><br>
          For tax reasons, vouchers must always be credited to employees in the corresponding month; otherwise, the balance would expire for your employees. To prevent this, an automatic voucher credit is enabled by default. If an active employee has not selected a voucher by the end of the month and no automatic choice has been set, the employee will automatically receive a voucher you have predefined.
          <br><br>
          <strong>To make use of the tax advantages of non-cash vouchers, the following rules apply:</strong>
          <ul>
          <li>Your employees must select their voucher in the corresponding month in the Probonio-app. You can configure the deadline for selection at any time in the settings. You will receive the tax report the next day or at the latest on the 1st of the following month.</li>
          <li>Your employees can save up vouchers—which are generally valid for 3 years—and redeem them later.</li>
          </ul>
          `,
      },
      FITNESS: {
        name: 'Company fitness',
        requirement: 'Available amounts: €25, €40 and €50',
        infoText: `With the company fitness benefit, you give your employees flexible access to a wide range of sports, wellness, and health offerings at participating studios nationwide. Depending on their individual needs, employees can use the offerings flexibly, which supports not only physical health but also mental balance and job satisfaction. For your company, there are long-term benefits: healthier employees tend to be ill less often, more productive, and more committed to the company.
          <br><br>
          <strong>To benefit from the tax advantages of company fitness as a non-cash benefit, the following rules apply:</strong>
          <ul>
          <li>No cash may be paid to employees; subsidies for memberships must be provided directly (e.g. by granting access to memberships).</li>
          <li>The offering must be equally accessible to all employees, not only to selected groups.</li>
          <li>The membership or contribution must not be paid out in cash or as a cash equivalent (e.g. vouchers).</li>
          <li>Participation in the fitness offerings must be voluntary.</li>
          </ul>`,
      },
      WELLPASS: {
        name: 'Corporate Fitness',
        requirement: 'Available amount: €{{amount}}',
        infoText: `With the company fitness benefit, you give your employees flexible access to a wide range of sports, wellness, and health offerings at participating studios nationwide. Depending on their individual needs, employees can use the offerings flexibly, which supports not only physical health but also mental balance and job satisfaction. For your company, there are long-term benefits: healthier employees tend to be ill less often, more productive, and more committed to the company.
          <br><br>
          <strong>To benefit from the tax advantages of company fitness as a non-cash benefit, the following rules apply:</strong>
          <ul>
          <li>No cash may be paid to employees; subsidies for memberships must be provided directly (e.g. by granting access to memberships).</li>
          <li>The offering must be equally accessible to all employees, not only to selected groups.</li>
          <li>The membership or contribution must not be paid out in cash or as a cash equivalent (e.g. vouchers).</li>
          <li>Participation in the fitness offerings must be voluntary.</li>
          </ul>`,
      },
      CREDIT_CARD: {
        name: 'Credit card',
        requirement: 'Available for employees with a valid credit card',
        infoText: '$t(benefitsModule:CREDIT_CARD.infoTextFull)',
      },
    },
    bulkApplyTemplate: {
      noNewActivationsDone: 'No employees were found for whom the template can be applied.',
      newActivationsSomeErrors: '{{successCount}} employees applied, {{errorCount}} skipped',
      newActivationsError: 'Template could not be applied for selected employees',
      newActivationsSuccess: 'Template applied for {{count}} employees',
      appliedCount: 'Template applied for {{count}} employees',
    },
    fields: {
      date: 'Date',
      dateFrom: 'Activation date',
      dateTill: 'End date',
      benefit: 'Benefit',
      options: 'Options',
      buttons: {
        cancel: 'Cancel',
        save: 'Save',
      },
    },
    labelStartDate: 'From',
    labelEndDate: 'Until',
    pause: 'Pause',
    pauseBenefitLabel: 'Pause Benefit for the following period:',
    planPause: 'Plan pause',
    receipts: {
      from: 'Handed in by',
      date: 'Date',
      amount: 'Amount',
      refund: 'Refund',
      completedDate: 'Submitted on',
      expirationDate: 'Next confirmation until',
      status: 'Status',
      pending: 'Receipt pending',
      approved: 'Receipt approved',
      rejected: 'Receipt rejected',
      timeCardSplit: 'This time card / annual ticket will be refunded partially over the {{validityMonths}} months validity period.',
      validityStart: 'Start',
      duration: 'Duration',
      durationMonthsSingle: '≤ 1 month',
      durationMonths_one: '{{count}} month',
      durationMonths_other: '{{count}} months',
      refundType: 'Refund type',
      refundType_MONTHLY: 'Pro-rata',
      refundType_YEARLY: 'Lump sum',
    },
    helperText: {
      dateFrom: 'Starting current month',
      dateTill: 'Leave empty for no limit',
      dateNextMonth: 'Starting next month',
    },
    hrSystemTemplateOverrideWarning: 'Editing this activation will be overridden on the next synchronization via the HR interface.',
    benefitSplitAlert: {
      default: 'Your changes are only relevant for months in the future. Previous months will remain unchanged',
      COUPONS_V2:
        'Your change only applies to the current month if the employee has not yet received a voucher. Your change will otherwise apply to the following months.',
    },
    noValidActivationPeriod: 'No activation is possible for this employee: the earliest start date is after the termination date.',
    showMoreButton: 'Show more',
    showLessButton: 'Show less',
    dialogCreateTitle: 'Add new Activation',
    dialogEditTitle: 'Manage activation',
    deleteConfirmation: 'Are you sure you want to delete this planned activation? This cannot be undone.',
    deleteActivation: 'Delete planned activation',
    stopTitle: 'Deactivation',
    stopChoice: 'Please choose when the benefit should be deactivated.',
    stopConfirmation1:
      'If the employee has already used it, it will be deactivated at the end of the current month, otherwise at the <strong>end of the last month</strong>.',
    stopConfirmation2: 'The benefit will be deactivated at the <strong>end of the current month</strong>.',
    stopButton: 'Deactivate',
    stopButton1: 'Deactivate as soon as possible',
    stopButton2: 'Plan deactivation',
    nonCashLimitWarning:
      'Amount would exceed the tax-free non-cash benefit limit of €50 per month by {{exceededNonCashLimitAmount, euro}} during the selected period',
    nonCashLimitWarningGeneric:
      'The $t(common:benefit.{{benefit}}) benefit can only be activated for employees who do not exceed the tax-free non-cash benefit remuneration limit of €50 per month.',
    invoiceCreatedWarning: 'This Gifts invoice was already created',
    budgetExceededWarning:
      'Warning: Not enough gift card budget available. Gift cards must reach the employee by the end of the calendar month. This can only be ensured if the payment reaches us by the end of the month.',
    successCreate: 'Activation successfully created',
    successUpdate: 'Activation successfully updated',
    terminationDateCapped: 'Activation updated. The end date was capped to the termination date ({{date,monthAndYear}}).',
    successDelete: 'Activation successfully deleted',
    successStop: 'Benefit deactivated successfully',
    successPause: 'Benefit paused successfully',
    activationError: {
      conflict: 'Selected area already exists',
      activationAlreadyStopped: 'The activation already ends this month and cannot be stopped any earlier',
      flexConflict: 'The Flex Budget can only be activated exclusively',
      missingCreditCard: 'Please order a credit card first',
      terminationDateExceeded: "The activation cannot start after the employee's termination date",
      activeContract: 'Deactivation is not possible while a bikeleasing contract is active. Please end the contracts first.',
    },
    inuseError: {
      default: 'Benefit already used in selected period',
      COUPONS_V2: 'Deactivation only possible for the end of next month, as invoice has already been created',
    },
    userBenefitPage: {
      activations: 'Activations',
      employeeData: 'Employee data',
      activeSince: 'Active since {{date,date}} ',
    },
    userGiftsPage: {
      payouts: 'Payouts',
      payoutDate: 'Payout date',
      occasion: 'Occasion',
      recurring: 'Recurring',
      amount: 'Amount',
      status: 'Status',
      employee: 'Employee',
      active: 'Next payment on {{val, date}}',
      deleteConfirmation: 'Are you sure you want to delete the scheduled payout?',
    },
    report: {
      couponDeadlineWarning:
        'You will receive the report on Gift cards separately by e-mail. You can send this again by clicking on "Show history".',
      reportDialogMessage:
        'This report serves you in case of a tax or social security audit as documentation of all received receipts and the amounts paid for them.',
      receiptExportButton: 'Export receipts to CSV',
      receiptExportTooltip: 'Download detailed report',
      tooltip: 'Download once to activate automatic exports',
      lastExport: 'Last export: {{date,date}}',
      lodas: {
        title: 'Status {{reportMonth,monthAndYearShort}}',
        button: 'Tax Data-Export',
      },
      confirmDialog: {
        header: 'E-Mail report deactivation?',
        body: 'Would you like to deactivate the E-Mail report?',
      },
      mailCheckbox: 'Receive the E-Mail report monthly',
      employeeList: {
        header: {
          name: 'Employee',
          employeeNumber: 'Employee number',
          department: 'Department',
          validated: 'Validated',
          sum: 'Sum',
        },
      },
      reportDownloadDialog: {
        title: 'Detail report information',
        actionSuccess: 'Download',
        exportMonth: 'Export invoices for',
        exportMonthHelper: 'Choose a month to export only the invoices of this month',
      },
      successExportDialog: {
        header: 'Mobility Data-Export',
        title: 'Payroll data was successfully exported!',
        question: 'Would you like to receive the next report in your E-Mail?',
        checkbox: 'Send me the payroll reports for each month by e-mail',
        detailCheckbox: 'Automatically send me the detailed reports for each month by e-mail',
        submitButton: 'Confirm',
      },
      tabs: {
        export: 'Export',
        documents: 'Documents',
      },
      noDocuments: 'No documents available',
    },
    settingsDialog: {
      title: 'Settings for benefit {{benefit}}',
      settings: 'Settings',
      moreSettings: 'More settings',
      exportType: 'Payroll accounting tool',
      exportTypeLong: 'Selected payroll accounting tool',
      exportTypeHint: 'Please contact support to change your selected payroll accounting tool.',
      salaryTypeInvalidCharacters: 'The salary type must not contain semicolons, quotation marks, tabs or line breaks',
      salaryTypeNumeric: 'The salary type must be a number with 1 to 6 digits (no leading 0)',
      exportSuccess:
        'Payroll data was successfully exported!\n\nActivate automatic delivery on the right to receive the next report in your e-mail.',
      exportEmployeeNumberWarning:
        "One or more active employees did not have a valid numeric personnel number stored at the time the report was created. Instead, the employee's e-mail address has been entered in the report.\n\nPlease store the personnel numbers in the employee master data.",
      selectExportType: 'Set payroll accounting tool',
    },
    benefitSelector: {
      benefit: 'Benefit',
      subheader: 'Benefits',
      all: 'All',
    },
    employeeTabs: {
      configureNow_zero: 'Configure for employees now',
      configureNow_one: 'Configure for {{count}} employee now',
      configureNow: 'Configure for {{count}} employees now',
      configureNowAll: 'Configure for all employees now',
      editConfiguration_zero: 'Change configuration for employees',
      editConfiguration_one: 'Change configuration for {{count}} employee',
      editConfiguration: 'Change configuration for {{count}} employees',
      editConfigurationAll: 'Change configuration for all employees',
      noRecordsToDisplay: 'This benefit has not been activated for any employees yet.',
      confirmed: 'confirmed',
      pleaseSelect: 'Please select the employees to configure in the table below.',

      tab: {
        active: 'Benefit active',
        inactive: 'Benefit inactive',
        all: 'All',
      },
      header: {
        nonCashBenefits: 'Non-cash benefit',
        status: 'Benefit active',
        notInterested: 'No interest',
        creditCardStatus: 'Status',
      },
      status: {
        active: 'Since {{startDate,monthAndYearShort}}',
        inactive: 'No',
        activeFrom: 'Starting {{startDate,monthAndYearShort}}',
      },
      creditCardStatus: {
        ACTIVE: 'Valid until {{expirationDate,monthAndYearShort}}',
        ORDERED: 'Ordered on {{orderDate,date}}',
        EXPIRED: 'Expired',
      },
    },
    subPage: {
      news: 'Messages',
      employees: 'Employees',
      receipts: 'Receipts',
      payrollReport: 'Payroll export',
      documents: 'Documents',
      settings: 'Settings',
    },
    bulkEditDialog: {
      title: 'Change configuration',
      noEditable: 'No employees were found for whom the benefit can be edited.',
      employeeListError: 'The list of employees could not be loaded. Please try again.',
      deactivateError: '{{count}} employees could not be deactivated',
      deactivateError_one: 'One employee could not be deactivated',
      deactivateSomeErrors: '{{successCount}} employees deactivated, {{errorCount}} skipped',
      deactivateSuccess: '{{count}} employees deactivated',
      deactivateSuccess_one: 'One employee deactivated',
      pauseError: '{{count}} employees could not be paused',
      pauseError_one: 'One employee could not be paused',
      pauseSomeErrors: '{{successCount}} employees paused, {{errorCount}} skipped',
      pauseSuccess: '{{count}} employees paused',
      pauseSuccess_one: 'One employee paused',
      updateError: '{{count}} employees could not be updated',
      updateSomeErrors: '{{successCount}} employees edited, {{errorCount}} skipped',
      updateSuccess: '{{count}} employees updated',
      updateSuccess_one: '{{count}} employee updated',
      updatedCount: '{{count}} updated',
      tabs: {
        deactivate: 'Deactivate benefit',
        changeOptions: 'Change options',
        pauseBenefit: 'Pause benefit',
      },
      deactivate: {
        mode: {
          info: 'Choose on which date you want to deactivate the benefit:',
          nearestDate: {
            label: 'As soon as possible',
            hint: 'If the employee has already used the benefit, it will be deactivated at the end of the current month, otherwise at the end of the last month.',
          },
          endOfMonth: {
            label: 'End of the month',
            hint: 'The benefit will be deactivated at the end of the current month.',
          },
          custom: {
            label: 'Set your own end date',
            hint: 'Select below the last month in which the employee can still use the benefit.',
          },
        },
        endDate: 'End date',
      },
      changeOptions: {
        info: 'Select the new options of the current benefit below. These apply from the next possible date for all selected employees.',
      },
      errorModal: {
        title: 'Change could not be applied',
        text: 'Unfortunately, the change cannot be applied to the following employees, please remove them from the selection and try again:',
      },
      pauseForm: {
        hint: 'If the employee has already used the benefit, it can be paused at the end of the current month at the earliest, otherwise it can also be paused at the end of the last month. The end date is included in the pause.',
        text: 'Select the period during which you would like to pause the benefit:*',
      },
      editActivationsResultDialog: {
        messageTitle: 'Attention',
        downloadButton: 'Download list',
        csvHeaderFirstName: 'First name',
        csvHeaderLastName: 'Last name',
        csvHeaderMail: 'Email',
        csvHeaderEmployeeNumber: 'Employee number',
        csvHeaderDepartment: 'Department',
        csvHeaderExternalId: 'External ID',
        csvHeaderStartDate: 'Contract start date',
        csvHeaderEndDate: 'Contract end date',
        contractUntil: 'Contract until {{date}}',
      },
      deactivateResultDialog: {
        title: 'Notice: Adjusted Deactivation Dates',
        message:
          '{{count}} employees have an active contract. The deactivation date has been changed to the contract end date. Download the list of affected employees.',
        csvFilename: 'adjusted-deactivation-dates.csv',
      },
      deactivateBlockedDialog: {
        title: 'Deactivation not possible',
        message:
          '{{count}} employees have an active contract, therefore deactivation was not possible. Download the list of affected employees.',
        csvFilename: 'deactivation-blocked.csv',
      },
      pauseResultDialog: {
        title: 'Pausing not possible',
        message:
          '{{count}} employees have an active contract, therefore pausing was not possible. Download the list of affected employees. Deactivation can be done in the "Deactivate benefit" tab.',
        csvFilename: 'pause-blocked.csv',
      },
    },
    editDialog: {
      title: 'Change configuration',
      noEditable: 'No employee was found for whom the benefit can be edited.',
      deactivateError: 'Benefit could not be deactivated',
      deactivateSomeErrors: 'Benefit deactivated, {{errorCount}} skipped',
      deactivateSuccess: 'Benefit successfully deactivated',
      pauseError: 'Benefit could not be paused',
      pauseSomeErrors: 'Benefit paused, {{errorCount}} skipped',
      pauseSuccess: 'Benefit successfully paused',
      updateError: 'Employee could not be updated',
      updateSomeErrors: 'Employee edited, {{errorCount}} skipped',
      updateSuccess: 'Employee updated',
      tabs: {
        deactivate: 'Deactivate benefit',
        changeOptions: 'Change options',
        pauseBenefit: 'Pause benefit',
      },
      resultMessageTitle: 'Attention',
      deactivateResultDialog: {
        title: 'Notice: Adjusted Deactivation Date',
        message: 'The employee has an active contract. The deactivation date has been changed to {{date,monthAndYear}}.',
      },
      deactivateBlockedDialog: {
        title: 'Deactivation not possible',
        message: 'The employee has an active contract, therefore deactivation was not possible.',
      },
      pauseResultDialog: {
        title: 'Pausing not possible',
        message:
          'The employee has an active contract, therefore pausing was not possible. Deactivation can be done in the "Deactivate benefit" tab.',
      },
    },
    LUNCH: {
      title: 'Lunch vouchers',
      since: 'Since',
      unlimited: 'Unlimited',
      overview: '$t(benefitsModule:LUNCH.title) Overview',
      receipts: 'Receipts',
      maxDailyLimitError: 'Fiscal limit of {{value,euro}}',
      infoTextFull: `You can subsidize your employees' workday lunches on a tax-deferred basis. Your employees can submit receipts of purchased meals and supermarket purchases, have meals delivered together as a team, or cook on-site and receive reimbursement with their paycheck.
      <br><br>
      Probonio reviews all lunch vouchers for tax requirements, calculates reimbursements and archives all receipts. Your employees receive the net meal allowance reimbursement along with their salary payment for the following month. The transfer of the payroll data is automated to your desired payroll program, such as Datev LODAS or Lexware.
      <br><br>
      <strong>To take advantage of the tax benefits of the lunch vouchers, the following rules apply:</strong>
      <ul>
      <li>A supplemental agreement to the employment contract must be made for the lunch vouchers.</li>
      <li>The lunch vouchers can be granted for a maximum of 15 working days through the 15-day rule. In the course of the 15-day rule, the entitlement of part-time employees may be lower.</li>
      <li>The maximum tax-free reimbursement amount per day is {{lunchDailyLimit, euro}} (= {{lunchMonthlyLimit, euro}} per month).</li>
      <li>For reimbursement, employees must submit a complete and legible receipt of their lunch (e.g., photo of a supermarket receipt).</li>
      <li>The meal allowance for employees who do not carry out their work on site for more than 3 days per month on average or up to 3 months per year is subject to the conditions of the additional meal allowance. However, Probonio cannot be used for this purpose.</li>
      </ul>`,
      infoTextVideos: {
        managerSpecific: 'Employer Portal Lunch',
        appSpecific: 'App Lunch',
      },
      fields: {
        mealsPerMonth: 'Meals per month',
        perMeal: '€ per meal',
        maxAmount: 'Maximum amount',
        flatRateTaxation: 'Maximum refund',
        flatRateTaxationOn: 'Yes',
        flatRateTaxationOff: 'No',
        taxationType: {
          SIMPLE_TAXATION: 'Completely avoid flat-rate taxation',
          FLAT_RATE_TAXATION: 'Use maximum refund (recommended)',
        },
        salaryConversation: 'Salary Conversation',
      },
      settings: {
        title: 'Settings for receipt upload',
        info: 'Here you can specify up to which day of the month your employees can upload receipts. Select a day before the end of the current month if you need to finalize your payroll at the end of the current month.<br/><br/>The payroll data report is sent automatically 4 days after the selected day due to the document check. If this date falls on a weekend, the report will be sent on the second following business day.',
        successToast: 'Changes have been saved',
        periodCloseDelay: {
          title: 'Latest date of receipt upload',
          info: 'Depending on your requirements, select a day at the end of the current month or the start of the following month. The receipt upload is always possible up to the end of the business day (EOB).',
          currentMonth_one: '{{count}} day before end of month',
          currentMonth_other: '{{count}} days before end of month',
          endOfMonth: 'At the end of the month',
          nextMonth: 'Until {{count}}st day of following month',
          nextMonth_ordinal_one: 'Until {{count}}st day of following month',
          nextMonth_ordinal_two: 'Until {{count}}nd day of following month',
          nextMonth_ordinal_few: 'Until {{count}}rd day of following month',
          nextMonth_ordinal_other: 'Until {{count}}th day of following month',
        },
      },
    },
    MOBILITY: {
      title: 'Mobility',
      subTitle: 'Overview',
      overview: '$t(benefitsModule:MOBILITY.title) Overview',
      receipts: 'Receipts',
      infoTextFull: `You can support your employees in their daily mobility with a tax-free or flexible mobility budget. To use the mobility budget, your employees can submit invoice receipts for the use of various forms of mobility.
      <br><br>
      Your employees will receive reimbursement for their mobility costs together with their salary payment for the following month. The transfer of the accounting data is automated to your desired payroll program, such as Datev LODAS or Lexware.
      <br><br>
      <strong>The following regulations apply to the tax-free or flexible use of the mobility budget:</strong>
      <ul>
      <li>With the tax-free mobility budget, employees can use local public transportation for private and business trips and long-distance public transportation for business trips tax-free.</li>
      <li>Employers can also reimburse Germany ticket tax-free.</li>
      <li>With the flexible mobility budget, employees can use all types of mobility flexibly, but not tax-free.</li>
      <li>The mobility budget can generally be provided both monthly and annually.</li>
      <li>To be reimbursed, employees must submit a complete and legible receipt of their used mobility, which we will check for correctness on your behalf.</li>
      </ul>`,
      infoTextVideos: {
        managerSpecific: 'Employer Portal Mobility',
        appSpecific: 'App Mobility',
      },
      fields: {
        monthlyLimit: 'Budget',
        taxationType: {
          TAXABLE: 'Flexible mobility budget',
          TAX_FREE: 'Tax-free mobility grant',
        },
        taxationTypeColumn: 'Flexible mobility',
      },
    },
    INTERNET: {
      title: 'Internet benefit',
      subTitle: 'Overview',
      overview: '$t(benefitsModule:INTERNET.title) Overview',
      receipts: 'Usage',
      downloadReceipt: 'Download confirmation',
      infoTextFull: `You can support your employees with their private ongoing Internet costs at home. It doesn't matter whether your employees work from home or directly on site or for what purposes the Internet is used. While the internet allowance is taxed at a flat rate of 25% by employers, employees do not have to pay tax or social security contributions for the allowance.
      <br><br>
      Up to the monthly amount of €50, it is sufficient for employees to submit a declaration of charges for their ongoing Internet costs. Annual confirmation of this declaration is obtained automatically via the Probonio-app, which means there is no administrative work for employers here.
      <br><br>
      As employees do not have to pay any taxes for the allowance, they receive the net reimbursement of the Internet allowance together with their salary payment for the following month. The payroll data is automatically transferred to your desired payroll program, e.g. Datev LODAS or Lexware.
      <br><br>
      <strong>In order to take advantage of the tax benefits of the Internet allowance, the following rules apply:</strong>
      <ul>
      <li>Reimbursement of Internet costs is in addition to salary.</li>
      <li>Employees do not have to pay tax or social security contributions for the allowance, which is why it is completely tax-free for them.</li>
      <li>Employers tax current internet costs at a flat rate of 25% up to a monthly allowance of €50 or €600 per year. Employers can also transfer the flat-rate taxation to the employee, which you can configure in your payroll accounting system.</li>
      <li>The costs can only be taxed by the employer at a flat rate up to the amount actually incurred.</li>
      <li>The Internet subsidy can only be granted if the costs for the Internet connection are borne by the employee himself and not by other persons.</li>
      <li>The employer must be informed in the event of changes to the contract.</li>
      </ul>`,
      infoTextVideos: {
        managerSpecific: 'Employer Portal Internet',
        appSpecific: 'App Internet',
      },
      fields: {
        monthlyBudget: 'Max. Budget',
      },
    },
    KITA: {
      title: 'Daycare Subsidy',
      fields: {
        monthlyBudget: 'Amount',
      },
      infoTextVideos: {
        managerSpecific: 'Employer Portal Daycare Subsidy',
        appSpecific: 'App Daycare Subsidy',
      },
      infoTextFull: `<strong>Support your employees financially with the ongoing costs of daycare.</strong> It does not matter whether the care is provided in a daycare center, or in a similar facility. Employees are only required to confirm their actual monthly daycare costs directly in the app. Additional proof of daycare costs – such as contracts or payment receipts – can be uploaded in the Probonio-app for employees or via the employer portal if needed.       
      <br><br>
      The tax-free allowance is paid out net together with the salary of the following month. Transmission to your payroll software (e.g., DATEV LODAS or Lexware) happens automatically.
      <br><br>
      <strong>To take advantage of the tax benefits of the daycare allowance, the following rules apply:</strong>
      <ul>
      <li>Reimbursement of daycare costs is provided in addition to the salary.</li>
      <li>The allowance is completely tax- and social security-free for employees.</li>
      <li>The daycare allowance can only be granted if the daycare costs are actually paid by the employee themselves.</li>
      <li>If the daycare costs change, the employer must be informed.</li>
    </ul>`,
      intervals: 'Usage',
      documentTypes: {
        CONTRACT: 'Contract and cost verification',
        PAYMENT_OVERVIEW: 'Payment verification',
        OTHER: 'Other',
      },
      menu: {
        info: 'Upload relevant documents here for your employees’ daycare subsidy.',
        button: 'Upload document',
        dialog: {
          title: 'Daycare Subsidy Document Upload',
          upload: 'Select file',
          employee: 'Employee *',
          documentName: 'Document name',
          documentType: 'Document type',
          success: 'Upload successful',
          tooltip: {
            CONTRACT: 'e.g. daycare contract, contract addenda, cost confirmation',
            PAYMENT_OVERVIEW: 'e.g. receipts for paid fees, bank transfer confirmations, annual statements of paid fees',
            OTHER: 'e.g. proof of public partial subsidies, proof of employer subsidy for direct payment to the daycare provider',
          },
        },
      },

      tables: {
        intervals: {
          title: 'Usage',
          employee: 'Employee',
          startDate: 'Applied on',
          refund: 'Subsidy',
        },
        documents: {
          title: 'Documents',
          employee: 'Employee',
          documentType: 'Document type',
          documentName: 'Document name',
          uploadDate: 'Uploaded on',
          actions: {
            download: 'Download',
            delete: 'Delete',
            deleteTooltip: 'Only documents you uploaded yourself can be deleted',
            deleteConfirmTitle: 'Delete document',
            deleteConfirmHint: 'Are you sure you want to delete the selected document?',
            deleteSuccess: 'Document has been deleted',
          },
        },
      },
    },
    BAV: {
      title: 'Company pension plan',
      subTitle: 'Employee configuration',
      contracts: 'Contracts',
      contractDetails: 'Contract of {{name}}',
      overview: '$t(benefitsModule:BAV.title) configuration',
      infoTextFull: `As an employer, you can support your employees with a tax-privileged subsidy for a company pension. With this company pension, your employees receive an additional contribution at retirement age and benefit from tax advantages.
      <br><br>
      Through Probonio, your employees can inform themselves about the benefits of the bAV and book a provider-independent consultation with our bAV experts via app. Employees can calculate the costs for the bAV directly in the app and thus receive an exact list of the financial conditions. In addition, all information and documents relating to the current contract can be viewed at any time in the Probonio-app.
      <br><br>
      <strong>The following regulations apply to the offer of a company pension plan:</strong>
      <ul>
      <li>Employers are required by law to conclude a contract for a company pension in the form of deferred compensation for their employees if they express a desire to do so.</li>
      <li>In the case of deferred compensation, employers are obliged to subsidize at least 15% of their employees' contributions.</li>
      <li>Since 2023, an additional social security-free subsidy of a maximum of €292 per month, or €3,504 per year, can be granted per employee in the form of deferred compensation.</li>
      <li>No wage tax is payable up to a monthly contribution to the company pension scheme of €584 per month, i.e. €7,008 per year.</li>
      <li>The amount of tax- and social security-free contributions varies annually, as it is adjusted to the contribution assessment ceiling for pension insurance.</li>
      </ul>`,
      infoTextVideos: {
        managerSpecific: 'Employer Portal Company Pension',
        appSpecific: 'App Company Pension',
      },
      fields: {
        additionalPaymentPercent: '% additional payment',
        vwlConversion: 'VWL conversion',
      },
    },
    COUPONS: {
      availableTitle: '{{count}} voucher provider(s) available for this configuration',
      availableTitleWithAmount: '{{count}} gift cards available for {{amount,euro-float}}',
      available: 'Available:',
      unavailable: 'Not available:',
      split: 'Available when combined:',
      title: 'Gift cards',
      infoTitle: 'Gift Cards',
      infoTextFull: `You can provide your employees with up to 50 euros per month through the non-cash benefit. The budget can be redeemed with a wide range of Probonio's partners.
      <br><br>
      <strong>Gifts on Special Occasions</strong> 
      <br/>
      With the gifts module, you can offer your employees a tax-free gift for special personal occasions through the non-cash benefit. Employees can select a desired voucher from our partner providers. These vouchers can then be redeemed in the Probonio-app for various products and offers from the selected partner.
      <br/><br/>
      <strong>To take advantage of the tax benefits for gifts, the following regulations apply:</strong>
      <ul>
      <li>The gift must be provided in addition to the salary and must not be part of a salary conversion.</li>
      <li>The gift must be provided promptly around the time of the personal occasion.</li>
      <li>The occasion must be a significant personal life event for the employee, such as birthdays, anniversaries, or the birth of a child. Christmas and other public holidays do <strong>not</strong> qualify as personal occasions.</li>
      <li>No more than 3 gifts should be given per employee per year.</li>
      <li>Multiple gifts can be given within the same month.</li>
      <li>Employees can save and redeem the vouchers later, typically valid for 3 years.</li>
      </ul>
      `,
      infoTextVideos: {
        managerSpecific: 'Employer Portal Gift Cards',
        appSpecific: 'App Gift Cards',
      },
      infoTextFullWithFitness: `
      <strong>Vouchers</strong>
      <br/>You can provide your employees with up to 50 euros per month as a voucher budget through the non-cash benefit. This budget can be redeemed at a variety of Probonio's voucher partners.
      <br/><br/>
      To offer your employees vouchers, the voucher budget can be topped up with just a few clicks and adjusted flexibly at any time. With the standard automatic top-up setting, you will automatically receive an invoice each month for the costs incurred in the following month.
      <br><br>
      For tax reasons, vouchers must always be allocated to employees in the corresponding month. Otherwise, the balance for your employees would expire. To avoid this, an automatic voucher allocation is set as the default for your employees. If an active employee has not selected a voucher by the end of the month and no automatic selection has been made, they will automatically receive a voucher specified by you.
      <br/><br/>
      <strong>To take advantage of the tax benefits of non-cash vouchers, the following regulations apply:</strong>
      <ul>
      <li>The vouchers must be provided in addition to salary and cannot be issued in the form of a salary conversion.</li>
      <li>Your employees must select their voucher in the Probonio-app within the corresponding month. You can configure the deadline for voucher selection at any time in the settings. The tax report will be available the following day or by the 1st of the following month at the latest.</li>
      <li>Vouchers, which are generally valid for 3 years, can be saved and redeemed later by your employees.</li>
      </ul>
      <br/><br/>`,
      fields: {
        monthlyBudget: 'Budget',
        nonCashBenefits: 'Non-cash benefit',
      },
      deadlineActivationWarning:
        'The order date for vouchers has already passed. It is recommended that you wait until the following month to activate them.',
    },
    FITNESS: {
      title: 'Corporate Fitness',
      grantBenefit: 'Employee is allowed to use Corporate Fitness',
      amountHint: 'Corporate fitness is only available for a minimum amount of €25, €40, or a maximum of €50',
    },
    AT_LUNCH: {
      title: 'Lunch vouchers',
      maxDailyLimitError: 'Fiscal limit of {{value,euro}}',
      changePlaceInfo: 'Please note that these changes do not apply to existing workdays.',
      receipts: { title: 'Receipts' },
      payrollReport: {
        info: 'Downloading the payroll data export will make the assignment of workdays to meal vouchers permanent. Please ensure all data is correct before proceeding.',
      },
      fields: {
        place: {
          title: 'Eligible receipts',
          RESTAURANT: 'Receipts for meals from domestic restaurants and associated delivery services (up to {{money, euro}})',
          SUPERMARKET: 'Receipts for groceries from domestic providers (up to {{money, euro}})',
        },
        dailyLimit: 'Amount per working day',
      },
      settings: {
        title: 'Settings',
        info: 'Configure to what extent your employees may collect and redeem workdays.',
        example: 'With the current configuration, the workday can be redeemed from {{day}} until {{expiration}}.',
        successToast: 'Changes have been saved',
        atLunchDayExpiryMonths: {
          title: 'Validity of Workdays',
          info: 'Set after how many months a workday expires. Workdays always expire at the end of the month.',
          unit: 'Months until end of month',
        },
        atLunchFiscalYearEnd: {
          title: 'End of Fiscal Year',
          info: 'Choose a date by which collected workdays for a fiscal year expire at the latest.',
        },
        fiscalYearHint: 'The end of the fiscal year and the receipt upload deadlines can be configured in the <0>company settings</0>.',
      },
      days: {
        title: 'Workdays',
        table: {
          name: 'Name',
          mail: 'Email',
          employeeNumber: 'Emp. No.',
          date: 'Date',
          expirationDate: 'Redeemable Until',
          amount: 'Amount',
          usedAmount: 'Used',
          placeTitle: 'Eligible receipts',
          place: {
            SUPERMARKET: 'Restaurant, diner, supermarket, snack bar',
            RESTAURANT: 'Restaurant, diner',
          } satisfies Record<ATLunchDayListElementDTOPlaceEnum, string>,
        },
        import: {
          upload: 'Upload',
          success: '{{createdCount}} workdays successfully imported',
          noChanges: 'No changes necessary',
          skipMissing: 'Delete workdays not present in the file.',
          template: 'Filled template',
          state: {
            IMPORT_FAILED: 'Import was aborted in $t(benefitsModule:AT_LUNCH.days.import.line). Please check your import file.',
            NO_VALID_LINES: 'No valid rows found',
            SOME_VALID_LINES: 'Invalid rows will be skipped during import',
          },
          dryRun: {
            notDeleted: '{{count}} cannot be deleted',
            createdCount: '{{count}} will be created',
            deletedCount: '{{count}} will be deleted',
          },
          result: {
            notDeleted: '{{count}} were not deleted',
            createdCount: '{{count}} were created',
            deletedCount: '{{count}} were deleted',
          },
          title: 'Import Workdays',
          info: 'Import your employees’ workdays. These days will be used as a budget for the lunch benefit. Only working days for employees who have an active lunch benefit on the date of the working day can be imported.',
          hint: 'Import your employees’ workdays',
          line: 'Line {{line}}',
          summary: 'Summary',
          errorLineCount_one: '{{count}} issue was detected',
          errorLineCount_other: '{{count}} issues were detected',
          errorCount: '{{count}} issues found',
          errorReason: {
            INVALID_DATE: 'Invalid date',
            MISSING_EMPLOYEE: 'No matching employee found',
            MISSING_ACTIVATION: 'Employee has no active meal allowance',
          } satisfies Record<ATLunchLineErrorDTOLineErrorsEnum, string>,
        },
      },
      infoTextFull: `Sharing meals brings people together – including your team! With Probonio’s lunch vouchers, your employees enjoy daily tax-free benefits that they can use anywhere: at restaurants, via delivery services, or when shopping at the supermarket.
<br><br>
As an employer, you can choose between the following two variants:
<ul>
  <li>Up to {{supermarketLimit, euro}} per workday for purchases at grocery retailers.</li>
  <li>Up to {{restaurantLimit, euro}} per workday for meals at restaurants or affiliated delivery services.</li>
</ul>
A tax-free allowance is generated for each workday and can be accumulated like a savings account. Employees pay for their meals or groceries upfront, upload the receipts via the Probonio-app, and after approval, the amount is reimbursed in the next possible payroll run. This allows the balance to be used flexibly – whether for a daily snack or saved up for a larger meal at a restaurant.
<br/><br/>
The lunch voucher balance is flexible and adapts to the daily life of employees within the employer’s guidelines – from restaurant visits and pizza in the home office to weekend grocery shopping.
<br/><br/>
The following items are not eligible for reimbursement: alcohol, tobacco, non-food items, bottle deposits, or tips.`,
    },
    AT_MOBILITY: {
      title: 'Public Transport Ticket',
      receipts: { title: 'Receipts' },
      fields: {
        budgetPeriod: 'Budget period',
        monthlyLimit: 'Monthly limit',
        yearlyLimit: 'Yearly limit',
        refundType: 'Reimbursement type',
        refundTypeOptions: {
          MONTHLY: 'Pro-rata',
          MONTHLY_SHORT: 'month',
          YEARLY: 'One-time',
          YEARLY_SHORT: 'year',
        },
        yearlyRefundWarning:
          'With the "One-off" refund type, the full annual budget is reimbursed in one amount — regardless of whether the employee joined during the year or leaves the company before year-end. Please note the possible payroll tax implications.',
        refundTypeChangeInfo:
          'A change of refund type does not take into account any payments already made in the current year. The employee receives the new budget in full — regardless of what has already been paid out.',
        allowGroupTickets: {
          label: 'Family & group tickets allowed',
        },
      },
      infoTextFull: `You can support your employees in their daily mobility with a tax-free job ticket. To use the mobility budget, your employees can submit invoice receipts for the use of public transportation.
      <br><br>
      Your employees receive reimbursement of their mobility costs together with their salary payment for the following month. The reimbursement can be made either as a lump sum or on a pro-rata basis over the validity period of the ticket.
      <br><br>
      The payroll data is transmitted to your desired payroll program.
      <br><br>
      The legal basis for the tax-free or flexible use of the mobility budget is <risLink>§ 26 Z 5 lit. b EStG 1988</risLink> and the wage tax guidelines <findokLink>LStR Rz 750a ff</findokLink> in their current version.
      <br><br>
      Please note possible payroll tax implications if the employment relationship ends during the validity period of the ticket and a reimbursement has already taken place.`,
      settings: {
        title: 'Settings for receipt upload',
        info: 'Here you can specify up to which day of the month your employees can upload receipts.',
        successToast: 'Changes have been saved',
        refundTypeHint:
          'This setting only has an effect if the refund type "Aliquot" is selected for the respective budget. If "One-off" is selected, the upload deadline is controlled by the end of the fiscal year and the receipt upload deadline, which can be configured in the <0>company settings</0>.',
        periodCloseDelay: {
          title: 'Latest date of receipt upload',
          info: 'Depending on your requirements, select a day at the end of the current month or the start of the following month. The receipt upload is always possible up to the end of the business day (EOB).',
          currentMonth_one: '{{count}} day before end of month',
          currentMonth_other: '{{count}} days before end of month',
          endOfMonth: 'At the end of the month',
          nextMonth: 'Until {{count}}st day of following month',
          nextMonth_ordinal_one: 'Until {{count}}st day of following month',
          nextMonth_ordinal_two: 'Until {{count}}nd day of following month',
          nextMonth_ordinal_few: 'Until {{count}}rd day of following month',
          nextMonth_ordinal_other: 'Until {{count}}th day of following month',
        },
      },
    },
    COUPONS_V2: {
      title: 'Gift cards flex, gifts and incentives pursuant to Section 37b',
      infoTextFull: `
      <strong>Gift cards flex</strong> 
      <0 /><br>
      You can provide your employees with up to 50 euros per month in the form of a voucher budget. The budget can be redeemed at a variety of Probonio voucher partners.
      <br><br>
      The Flex Module allows your employees to save up their credit in the form of Probonio vouchers. This means they don't necessarily have to choose a voucher from our partners every month. They also get exclusive access to other well-known voucher partners.
      <br><br>
      The invoices are created five working days before the end of the month for the coming month and sent to you automatically. It should be mentioned here that the vouchers can only be configured until the 15th day of the month for the current month.
      <br><br>
      <strong>In order to take advantage of the tax benefits of the non-monetary vouchers, the following rules apply: </strong>
      <ul>
      <li>Vouchers must be in addition to salary and, accordingly, may not be issued in the form of deferred compensation.</li>
      <li>Your employees do not have to select their voucher in the corresponding month in the Probonio-app, but can also do so later and thus save for larger purchases.</li>
      <li>Your employees can save up the vouchers, which are generally valid for 3 years, and redeem them later.</li>
      </ul>
      <br><br>
      <strong>Presents</strong> 
      <0 /><br>
      You can give your employees a tax-free present on special personal occasions via the non-cash remuneration with the module for presents. The employees can select a desired voucher from our voucher partners. Your employees can then use the present to redeem the vouchers for various Probonio offers and products from the selected voucher partner.
      <br><br>
      <strong>To take advantage of the tax benefits of the present, the following rules apply:</strong>
      <ul>
      <li>The present must be in addition to the salary and accordingly may not be spent in the form of a salary conversion.</li>
      <li>The present must be timely to the personal occasion.</li>
      <li>The personal occasion must be a special event in the employee's life, such as a birthday, anniversary or the birth of a child. Therefore, Christmas and other holidays cannot be considered a personal occasion.</li>
      <li>No more than 3 gifts per employee should be given annually.</li>
      <li>More than one present may be given in the same month.</li>
      <li>Your employees can save up the vouchers, which are generally valid for 3 years, and redeem them later.</li>
      </ul>
      <br><br>
      <strong>Incentive 37b</strong> 
      <0 /><br>
      Under Section 37b of the German Income Tax Act (EStG), you can give your employees gifts 37b up to a value of 10,000 euros per year and tax them at a flat rate of 30%. This gives employers the opportunity to give larger gifts to employees in excess of 60 euros on a tax-privileged basis. Your employees receive the incentive 37b in the form of Probonio vouchers, which can be redeemed flexibly at any time at our voucher partners.
      <br><br>
      The gifts, which are taxed at a flat rate, can be given in addition to the 50 euro benefit 37b and to presents. In addition, in contrast to gifts of present, no special personal occasion on the part of the employee is required in order to take advantage of the tax benefits.
      <br><br>
      It is important that the non-cash benefit is reported accordingly in the payroll. For this purpose, we will send you a monthly statement as an import file for the payroll program of your choice. Payroll accountants or tax consultants can import the relevant data into the payroll program used with just a few clicks, so that the non-cash benefits automatically appear on the payroll.
      <br><br>
      <strong>The following regulations apply in order to take advantage of the tax benefits of the present: </strong>
      <ul>
      <li>The non-cash benefit must be in addition to the salary and accordingly may not be issued in the form of a salary conversion.</li>
      <li>The non-cash benefit cannot be taxed as a lump sum via another option under Section 40 (2) of the German Income Tax Act (EStG).</li>
      <li>The non-cash benefit is not affected by any other lump-sum taxation rule.</li>
      <li>The benefit 37b does not fall under the regulations of tax-free benefits 37b (50 euros benefit 37b and presents).</li>
      <li>The benefit 37b is not predominantly in the employer's own business interest.</li>
      <li>Your employees can save up the vouchers, which are generally valid for 3 years, and redeem them later.</li>
      </ul>`,
      fields: {
        monthlyBudget: 'Budget',
      },
    },
    GIFTS: {
      title: 'Gift',
      dialogCreateTitle: 'Add new present',
      dialogEditTitle: 'Edit Present',
      completed: 'completed',
      paymentFailed: 'insufficient credit',
      paymentPending: 'planned',
      fields: {
        amount: 'Total',
        occasion: 'Occasion',
        recurring: 'This present should repeat yearly',
        description: 'Message for your employee',
        inviteWhenActive: 'Invite employees as soon as the present is due.',
        sendMailWhenActive: 'Inform employees also by mail',
      },
      placeHolderInput: {
        inputLabel: 'Message to your employee',
        errorMessage: 'Please enter under 1000 characters',
        firstNameChipLabel: 'First name',
        lastNameChipLabel: 'Last name',
      },
    },
    INCENTIVES: {
      title: 'Benefit in Kind 37b',
      dialogCreateTitle: 'Add New Benefit in Kind 37b',
      dialogEditTitle: 'Edit Benefit in Kind 37b',
      completed: 'completed',
      paymentFailed: 'insufficient credit',
      paymentPending: 'planned',
      fields: {
        amount: 'Total',
        occasion: 'Occasion',
        recurring: 'This gift should be repeated annually',
        recurringInfo:
          'Please check with your tax office to see if an annual repeat can be set up for your specific case in order to comply with tax regulations.',
        description: 'Message to your employee',
        inviteWhenActive: 'Invite employees as soon as the present is due.',
        sendMailWhenActive: 'Inform employees also by mail',
      },
      placeHolderInput: {
        inputLabel: 'Message to your employee',
        errorMessage: 'Please enter under 1000 characters',
        firstNameChipLabel: 'First name',
        lastNameChipLabel: 'Last name',
      },
      maxIncentiveAmountWarning: 'A maximum of €10,000 per year and employee may be granted; this payout would exceed the annual limit',
      remainingAmountAvailable: 'Remaining available amount: {{money}}',
    },

    BIKE: {
      title: 'Company Bike',
      subTitle: 'Employee configuration',
      contracts: 'Contracts',
      contractDetails: 'Contract of {{name}}',
      overview: '$t(benefitsModule:BIKE.title) configuration',
      otherProvider: 'Other',
      invalidUrl: 'The URL must be valid and start with https://',
      infoTextFull: `You can rent, buy or lease a company bike for your employees as a sustainable means of mobility. Your employees are free to choose the company bike and use it for both private and professional purposes.
      <br><br>
      The offer of a company bike is tax-advantaged for you and your employees, which can save you some costs depending on the implementation of the offer.
      <br><br>
      <strong>The following rules apply to the offer of a company bike:</strong>
      <ul>
      <li>If the company bike is offered as a salary extra in addition to the salary owed anyway, it is completely tax-free for employees.</li>
      <li>If the company bike is offered as a salary conversion, the social security and income tax contributions are reduced by deducting the leasing rate from the gross salary.</li>
      <li>If employees use the bicycle for private purposes, the company bicycle must be taxed as a non-cash benefit (since 2019: taxable amount = 0.25% of the gross bicycle price per month).</li>
      <li>Bicycles with motor assistance of up to 45 km/h do not fall under the regulations for company bicycles, but for company cars.</li>
      </ul>`,
      infoTextVideos: {
        managerSpecific: 'Employer Portal Bike',
        appSpecific: 'App Bike',
      },
      fields: {
        employerGrant: 'Employer grant',
        employerExtras: 'What additional services do you provide?',
        platformName: 'Provider',
        platformUrl: 'Provider-URL',
      },
      employerExtra: {
        INSPECTION: 'Inspection',
        INSURANCE: 'Insurance',
      },
    },
    NEWS: {
      title: 'Employee communication',
      articleTitle: {
        createTitle: 'Create message',
        modifyTitle: 'Edit message',
        showTitle: 'View message',
      },
      articleSubTitle: {
        createSubTitle: 'New message',
        modifySubTitle: 'Existing message',
      },
      subPage: {
        overview: 'Overview',
        readConfirmation: 'Read confirmation',
        reactions: 'Reactions',
      },
      articles: 'Messages',
      drafts: 'Drafts',
      published: 'Published',
      expired: 'Expired',
      hintText: 'You can create message here that your employees can see in the app.',
      addNewArticlesButton: 'Create new Message',
      articleCreator: {
        successCreate: 'Message created',
        successUpdate: 'Message updated',
        successPublish: 'Message published',
        errorPublishDateInPast: 'The publish date is in the past. Please choose a new date.',
        errorRemovalDateBeforeRelease: 'The removal date must be after the publish date.',
        errorRemovalDateInPast: 'The removal date must be in the future.',
        errorTitle: 'Title is required - must be no longer than 100 characters',
        errorPublishDate: 'Publish date is required',
        imageHeader: 'Cover picture',
        imageDescription: 'This image is displayed to employees at the top of the tile.',
        articleHeader: 'Title and message text',
        reactionsHeader: 'Reactions',
        reactionsDescription: 'Here you can set whether reactions are allowed on the message.',
        recipientGroupHeader: 'Recipient group',
        recipientGroupDescription: 'In this step you can set the recipients of the message.',
        recipientGroupSendingToggle: 'Send to all employees',
        recipientGroupSendingPreContinue: 'Continue with',
        recipientGroupSendingPostContinue: 'Employees',
        recipientGroupSendingSavedRecipientsContinue: 'Continue with saved employees',
        dispatchTimeHeader: 'Dispatch time',
        dispatchTimeDescription: 'Here you can specify the dispatch time.',
        completeHeader1: 'Complete',
        completeHeader2: 'Summary',
        completeHeader3: 'Detailed View',
        completeSubHeader: 'Check all information before publishing your message.',
        completeDescription: 'In this step you can check all the details and publish the message.',
        nextStep: 'Next',
        previousStep: 'Back',
        yes: 'Yes',
        no: 'No',
        receivedFrom: 'Sent to ({{count}})',
        scheduledFor: 'Scheduled for ({{count}})',
        confirmedBy: 'Note: {{count}} employees have already read and confirmed the message.',
        confirmedByOne: 'Note: {{count}} employee has already read and confirmed the message.',
        confirmedByEditing:
          'Note: {{count}} employees have already read and confirmed the message. Updating will reset read confirmations.',
        confirmedByEditingOne:
          'Note: {{count}} employee has already read and confirmed the message. Updating will reset the read confirmation.',
        confirmedByEditingBoth:
          'Note: {{count}} employees have already read and confirmed the message. Updating will reset read confirmations and reactions.',
        confirmedByEditingBothOne:
          'Note: {{count}} employee has already read and confirmed the message. Updating will reset the read confirmation and reactions.',
        confirmedByEditingReactions:
          'Note: {{count}} employees have already read and confirmed the message. Updating will reset reactions.',
        confirmedByEditingReactionsOne:
          'Note: {{count}} employee has already read and confirmed the message. Updating will reset reactions.',
        resetConfirmations: 'Reset read confirmations',
        resetConfirmationsSuccess: 'Read confirmations have been reset',
        resetReactions: 'Reset reactions',
        resetReactionsSuccess: 'Reactions have been reset',
        resetPartialFailure: 'Article was saved, but the reset failed.',
        showMore: 'Show more employees',
        showLess: 'Show less employees',
        usedFilters: 'Filters applied',
        searchTextFilter: 'Searched Text',
        costCenterFilter: 'Cost Center',
        departmentFilter: 'Department',
        workLocationFilter: 'Work Location',
        benefitFilter: 'Benefit',
        articleDescription: 'Here you can set the title and text of your message.',
        settingsHeader: 'Message settings',
        settingsDescription:
          'General settings for publishing and withdrawing a message. If the publishing time is not changed, the message will be published immediately.',
        publishArticle: 'Publish message',
        saveAsDraft: 'Save as draft',
        planArticle: 'Plan message',
        updateArticle: 'Update message',
        clearStartDateTooltip: 'Remove start date',
        clearEndDateTooltip: 'Remove end date',
        attachments: {
          dialogTitle: 'Add attachment',
          header: 'Attachments',
          description: 'Upload files to share with this message (max. 10 files, max. 10 MB per file).',
          removeTooltip: 'Remove attachment',
          fileSizeError: 'File size must not exceed 10 MB.',
          limitReached: 'Maximum 10 attachments allowed.',
        },
      },
      readConfirmationTab: {
        ALL: 'All',
        SEEN: 'Not confirmed yet',
        CONFIRMED_SEEN: 'Confirmed',
        NOT_SEEN: 'Not seen yet',
        confirmationStatus: 'Confirmation status',
        downloadConfirmationStatus: 'Download read receipts',
        downloadSuccess: 'CSV export completed successfully',
        filterLabel: 'Status',
        refresh: 'Refresh',
      },
      reactionsTab: {
        columns: {
          reaction: {
            label: 'Reactions',
          },
          createdAt: {
            label: 'Created at',
          },
        },
      },
      fields: {
        title: 'Title',
        releaseDateTime: 'Publishing date & time',
        removalDateTime: 'Date & Time of expiry',
        articleStatus: 'Message status',
        PUBLISHED: 'Published',
        AVAILABLE: 'Available',
        DRAFT: 'Draft',
        EXPIRED: 'Expired',
        noResults: 'No messages available',
        editPublishedDisabled: 'Published messages cannot be edited',
        deleteButton: 'Delete message',
        confirmDelete: 'Are you sure that you want to delete the selected message?',
        expireButton: 'Expire message',
        confirmExpire:
          'Are you sure you want to withdraw the selected message? It will be marked as expired and will no longer be visible to employees in the app.',
        deleteArticleSuccess: 'Message has been deleted',
        expireArticleSuccess: 'Message has expired',
      },

      labels: {
        image: 'Upload image',
        title: 'Title',
        description: 'Text',
        releaseDate: 'Release date & time',
        removalDate: 'Removal date & time',
        toBePublished: 'To be published',
        readConfirmation: 'Read confirmation',
        enableReactions: 'Allow reactions',
        enableReactionsTooltip: 'When enabled, employees can react to the message with an emoji in the app.',
        readConfirmationReminder: 'Notification frequency',
        confirmationNotificationWEEKLY: 'Weekly reminder',
        confirmationNotificationMONTHLY: 'Monthly reminder',
        confirmationNotificationNONE: 'No reminder',
        publishNow: 'Publish now',
      },
    },
    FLEX: {
      title: 'Flex Budget',
      infoTextFull: `You can support your employees with maximum flexibility in the use of benefits via the Probonio Flex Budget. Within the fixed Flex Budget, your employees can use the meal vouchers, the internet allowance and the flexible mobility budget and thus allocate the available budget to the various benefits according to their individual needs. 
        <br/><br/>
        Your employees receive the reimbursement of their costs together with their salary payment for the following month. The payroll data is automatically transferred to your desired payroll program, such as Datev LODAS or Lexware. 
        <br/><br/>
        <strong>The following rules apply to the use of the Flex Budget:</strong>
        <ul>
          <li>The meal vouchers can be granted on a maximum of 15 working days due to the 15-day rule. The maximum tax-free reimbursement amount per day is {{lunchDailyLimit, euro}} (= {{lunchMonthlyLimit, euro}} per month).</li>
          <li>Employers can tax current internet costs at a flat rate of 25% up to a monthly allowance of €50 or €600 per year.</li>
          <li>With the flexible mobility budget, employees can use all types of mobility flexibly, but not tax-free.</li>
          <li>In order to benefit from the tax advantages, the defined exemption limits for the meal allowance and the internet allowance must be adhered to. Probonio ensures compliance with the exemption limits.</li>
        </ul>`,
      infoTextVideos: {
        managerSpecific: 'Employer Portal Flex Budget',
        appSpecific: 'App Flex Budget',
      },
      cardSubtitle: 'Employees can flexibly choose and combine the following benefits:',
      fields: {
        monthlyBudget: 'Monthly budget',
        nonCashBenefits: 'Type of non-cash benefit',
      },
    },
    RECREATION: {
      title: 'Recreation allowance',
      infoTextFull: `The recreation allowance is support that employers can grant their employees for recuperation.
      It can take the form of cash allowances or benefits in kind, e.g. if the employer concludes a contract with a hotel.
      <br/><br/>
      However, the tax treatment of recreational allowances is complex. As a rule, they are taxable wages, although they are tax-privileged to a limited extent thanks to the option of lump-sum taxation.
      Taxable recreational allowances can be taxed at a fixed flat rate of 25 percent if the allowances do not exceed a total of 156 euros for the individual employee, 104 euros for their spouse and 52 euros for each child in a calendar year.
      <br/><br/>
      The employer must pay the flat-rate tax, but it is permissible to pass it on.
      Flat-rate taxed recreational allowances are exempt from social security contributions.`,
      infoTextVideos: {
        managerSpecific: 'Employer Portal Recreation',
        appSpecific: 'App Recreation',
      },
      maxBudgetHint:
        'The maximum amount is calculated from {{recreationBaseAmount,euro-float}} + {{recreationPartnerAmount,euro-float}} for the partner (married or registered civil partnership) + {{recreationChildAmount,euro-float}} per child. If the maximum increases (e.g. due to a change in marital status or number of children), you can adjust the budget here manually. If the tax maximum decreases (e.g. due to a change in marital status) and the currently granted yearly budget exceeds it, the adjustment is applied automatically to the new maximum.',
      maxBudgetHintMulti:
        'The maximum amount is calculated from {{recreationBaseAmount,euro-float}} + {{recreationPartnerAmount,euro-float}} for the partner (married or registered civil partnership) + {{recreationChildAmount,euro-float}} per child. On activation, each employee is automatically assigned their individual maximum as the yearly budget. You can then adjust the budget individually in the details of the respective employee.',
      individualBudget: 'individually det.',
      fields: {
        civilStatus: 'Civil status',
        numberOfKids: 'Number of kids',
        yearlyBudget: 'Yearly budget',
      },
    },
    CUSTOM: {
      fields: {
        monthlyGrant: 'Monthly budget',
        supplier: 'Supplier',
        comment: 'More information',
      },
    },
    BIKELEASING_V2: {
      title: 'Bikeleasing',
      infoTextVideos: {
        managerSpecific: 'Bikeleasing guide in Probonio',
        appSpecific: 'How Bikeleasing works',
      },
      bulkEditDialog: {
        deactivateBlockedDialog: {
          title: 'Deactivation not possible',
          message:
            '{{count}} employees have an active contract. Deactivation is only possible after the contracts have been ended. Download the list of affected employees.',
          csvFilename: 'deactivation-blocked.csv',
        },
        pauseResultDialog: {
          title: 'Pausing not possible',
          message:
            '{{count}} employees have an active contract, therefore pausing was not possible. The contracts must be ended first. Download the list of affected employees.',
          csvFilename: 'pausing-blocked.csv',
        },
      },
      editDialog: {
        deactivateBlockedDialog: {
          title: 'Deactivation not possible',
          message: 'The employee has an active contract. Deactivation is only possible after the contract has been ended.',
        },
        pauseResultDialog: {
          title: 'Pausing not possible',
          message: 'The employee has an active contract, therefore pausing was not possible. The contract must be ended first.',
        },
      },
    },
    BIKELEASING: {
      title: 'Bikeleasing',
      infoTextFull: `With the Bikeleasing integration, you can connect the Probonio portal with the Bikeleasing portal.
    <br><br>
    Here’s how the connection works:
    <ul>
      <li>After linking the accounts, you can enable the Bikeleasing benefit for your employees.</li>
      <li>Employees who have the benefit enabled can see the selected conditions in the Probonio app.</li>
      <li>Probonio regularly imports employee data from the Bikeleasing portal.</li>
      <li>For correct assignment, the employee numbers must be maintained in both portals.</li>
      <ul>
        <li>In the Probonio portal, you can manage these under the "Employees" section.</li>
        <li>In the Bikeleasing portal, this is possible under "Contracts" - alternatively, employees can manage these themselves.</li>
      </ul>
      <li>To trigger the synchronization again, click the "Sync Now" button under the "Integration" section.</li>
    </ul>`,
      infoTextVideos: {
        managerSpecific: 'Employer Portal Bikeleasing',
        appSpecific: 'App Bikeleasing',
      },
      fields: {
        condition: 'Bikeleasing Conditions',
        bikeleasingSecret: {
          label: 'Bikeleasing Secret',
          connectButton: 'Connect',
          conflictMessage: 'Bikeleasing Account already connected.',
          successMessage: 'Connection added',
        },
      },
      addCondition: 'Add new Connection',
      deleteCondition: 'Disconnect',
      confirmDeleteCondition:
        'Are you sure that you want to disconnect the selected connection to Bike Leasing??<br/><br/>This will remove the associated information from the Probonio system and terminate existing employee benefit activations at the end of the previous month.',
      lastSync: '<strong>Last sync</strong>: {{date , datetime}}',
      syncErrorDescription: {
        AUTH: 'Probonio could not log in to Bikeleasing. Please check the bike leasing portal to see if the connection is still available.',
        UNAVAILABLE: "Couldn't reach Bikeleasing. Please try again later",
        INTERNAL: 'An error occurred during synchronization.',
      },
      syncError: 'Sync successful',
      syncSuccess: 'Sync successful',
      integrationSubTitle: 'Connections',
      integration: {
        title: 'Integration',
        info: "This integration allows you to transfer your employees' master data and conditions directly from Bikeleasing to Probonio",
        guide: {
          title: 'You already have a Bikeleasing account',
          info: {
            title: 'Instructions',
            description: 'Simply connect Probonio with Bikeleasing. The integration transfers data stored in Bikeleasing to Probonio',
          },

          howTo: {
            title: 'How to connect your bike leasing account with the Probonio employer portal',
            description: `To connect the Bikeleasing portal to the Probonio employer portal, you need to follow these few steps:`,
            stepByStep: `<ol>
            <li>Log in on <0>this Bikeleasing page</0> and navigate to <strong>More Benefits</strong></li>
            <li>On this page you must accept the privacy policy and click on <strong>Link account</strong>.</li>
            <li>You will now receive a <strong>company code</strong> and a <strong>client secret</strong>, which you can copy to your clipboard and enter on this page below. If you then click on connect, all Bikeleasing conditions and contracts will be synchronised to Probonio.</li>
            </ol>`,
          },
        },
        bikeleasingPortal: 'Become a customer now',
        form: {
          companyCode: 'Company-Code',
          externalId: 'BL-User-ID',
          bikeleasingSecret: 'Client Secret',
          connect: 'connect',
          connectionError: 'Login to Bikeleasing API failed',
        },
      },
      connectNow: 'Just connect your Bikeleasing account!',
      noAccount: {
        title: "You don't have a Bikeleasing account yet",
        info1: 'As a Probonio customer, you know how to inspire your team with tax-free benefits and strengthen your employer branding',
        info2:
          'A particularly popular benefit is company Bikeleasing, which is especially attractive thanks to tax incentives and the option of salary conversion. With the Bikeleasing-Service, you and your team can benefit from these advantages easily and without risk.',
      },

      advantages: {
        title: 'Your benefits of company Bikeleasing with the Bikeleasing-Service',
        lessCosts: {
          title: 'Lower costs',
          description: 'Employers and employees save thanks to tax incentives and deferred compensation.',
        },
        climateFriendly: {
          title: 'More sustainability',
          description: 'With company Bikeleasing, you promote environmentally friendly and healthy mobility in your company.',
        },
        security: {
          title: 'Comprehensive protection',
          description: 'Termination, illness or parental leave of your employees are covered, as are theft, accidental damage, etc.',
        },
      },
      createNow: {
        title: 'Become a Bikeleasing customer now',
        description: 'Register now to inspire your team soon with the company bike offer of the Bikeleasing-Service.',
      },
      questions: {
        title: 'You have questions about the Bikeleasing-Service?',
        description:
          'Feel free to contact the Bikeleasing team by email at {{mail}} or by phone at {{tel}}. You can find more information about company bike leasing at <0>{{web}}</0>.',
      },
      companyCode: 'Employer code',
      conditionDetailDialog: {
        title: 'Employer code : {{companyCode}} - Terms and conditions',
        alertAction: 'Connections',
        alert: 'You need at least one Bikeleasing connection ',
        noOptions: 'No conditions available',
      },
      leasingDurationInMonths: 'Leasing period in months',
      allowedBikeTypes: 'Bike types',
      maxNumberOfBikesPerEmployee: 'Max. number of bikes/employee',
      minPurchasePrice: 'Minimum bike value/employee (gross)',
      maxPurchasePrice: 'Maximum bike value/employee (gross)',
      employerContribution: 'Employer contribution',
      priceRestrictions: {
        maxTotalPurchasePriceForAllBikes: 'Maximum amount/employee (gross)',
      },
      contracts: 'Contracts',
      contractIntro: 'The details of the concluded Bikeleasing contract are displayed below.',
      contractAppInfo: 'The data can be viewed by the employee in the Probonio app.',
      contractDetails: 'Contract from {{name}}',
      contractsTable: {
        noResults: 'No results found',
        header: {
          name: 'Employee',
          startDate: 'Start of Leasing',
          endDate: 'End of Leasing',
          contractNumber: 'Contract Number',
          bike: 'Bike',
          status: 'Status',
        },
      },
      contract: {
        status: {
          EmployeeApprovalRequired: 'Your approval required',
          EmployerApprovalRequired: 'Employer approval required',
          ChangeRequested: 'Change requested',
          CancelledOrRejected: 'Cancelled/rejected',
          CancellationPending: 'Cancellation pending',
          EmployerApprovalDone: 'Employer approval done',
          BikeTransferRequired: 'Bike transfer required',
          BikeTransferDone: 'Bike transfer done',
          Active: 'Active',
          Expiring: 'Expiring',
          Finished: 'Finished',
          Requested: 'Requested',
          BikeShipped: 'Bike dispatched',
          fallback: 'Unknown status',
        },
        contractNumber: 'Contract Number',
        trailerRecommendedRetailPrice: 'Trailer RRP',
        recommendedRetailPrice: 'RRP',
        purchasePrice: 'Purchase price',
        employerContribution: 'Employer contribution',
        salaryConversion: 'Salary conversion',
        leasingDuration: 'Leasing duration',
        benefitInKind: 'Benefit in kind',
        insuranceCoverage: 'Insurance coverage',
        startDate: 'Start',
        transferDate: 'Date of handover',
        leasingRate: 'Leasing rate',
        endDate: 'End',
        brand: 'Brand',
        model: 'Model',
        frameSize: 'Frame size',
        items: 'Accessory',
        lockBrand: 'Lock brand',
      },
      section: {
        contract: 'Contract details',
        leasing: 'Leasing',
        bike: 'Bike data',
        benefits: 'Your services',
      },
    },
    CREDIT_CARD: {
      title: 'Credit Card',
      subTitle: 'Credit Cards',
      infoTextFull: `With the credit card, you can provide your employees with a monthly budget of up to 50 euros. The card can be used flexibly for purchases at all Mastercard acceptance points within the selected region.
    <br/><br/>
    The following applies:
    <ul>
    <li>The card is an additional benefit to salary (not salary conversion) and can be chosen as an alternative to digital vouchers or corporate fitness.</li>
    <li>The balance on the card can be saved indefinitely and does not have to be spent by the respective billing date.</li>
    <li>The card can be used flexibly at over 1.2 million Mastercard acceptance points nationwide within the defined area of use.</li>
    <li>The card is ordered by the employer. The card and PIN are sent by mail to the employees.</li>
    <li>The card can be added to Apple Pay and Google Wallet.</li>
    <li>Cash withdrawal is excluded. The card is a prepaid card.</li>
    <li>Usable as a physical card and digitally (Apple/Google Pay) – also suitable for employees without the app (using the code on the back of the physical card, the card can be digitized and saved on the phone via a separate app).</li>
    </ul>`,
      tab: {
        activeCards: 'Active Cards',
        allCards: 'All Cards',
        charging: 'Budget Top-Up',
      },
      cardsTable: {
        noResults: 'No credit cards available',
        header: {
          cardIBAN: 'IBAN',
          status: 'Status',
          balance: 'Balance',
          createdAt: 'Ordered on',
          expiryDate: 'Expiry Date',
          deliveryMode: 'Delivery Mode',
        },
        status: {
          ACTIVE: 'Active',
          ORDERED: 'Ordered',
          EXPIRED: 'Expired',
          PENDING: 'Pending',
          ERROR: 'Error',
        },
        deliveryMode: {
          COMPANY_ADDRESS: 'Company Address',
          PERSONAL_ADDRESS: 'Personal Address',
        },
      },
      chargeNotificationsTable: {
        noResults: 'No top-ups have been made yet',
        header: {
          createdAt: 'Date',
          billingMonth: 'Billing Month',
          totalAmount: 'Top-Up Amount',
          chargeItemsCount: 'Number of Employees',
          chargeItems: 'Individual Amounts',
        },
        column: {
          billingMonth: '{{date,monthAndYear}}',
        },
      },
      chargeAccountHint: {
        title: 'Budget Top-Up for Credit Cards',
        info: `Please transfer the due amount at the end of each month to ensure timely top-up of cards for the following month.
      You will also be notified of the due top-up by email. Please note that posting your top-up may take up to 5 business days.
      <br/><br/>
      Please use the following details for the transfer:
      <ul>
      <li><b>IBAN</b>: {{chargingAccountIBAN}}</li>
      <li><b>BIC</b>: {{chargingAccountBIC}}</li>
      <li><b>Bank</b>: {{chargingAccountBankName}}</li>
      <li><b>Amount</b>: {{amountText}}</li>
      <li><b>Purpose</b>: {{billingMonthText}}</li>
      </ul>`,
        infoAmount: '{{amount,euro}}',
        infoAmountMissing: 'Top-up amount provided in your notification email',
        infoBillingMonth: '{{billingMonth,monthAndYear}}',
        infoBillingMonthMissing: '(Month and Year)',
      },
      orderCards: {
        title: 'Order Credit Cards',
        description:
          'Here you can order credit cards for your employees. The cards are usually delivered within 5 business days. The cost per card is 10 euros, which will be invoiced to you after the order. <br/><br/>Please note that cards can only be ordered for employees with an employee ID and a valid address.<br/><br/>First, set the usage option before selecting employees. <br/> The usage option determines in which postal code areas the credit card can be used. This regional restriction is necessary to correctly apply the tax-free benefit.',
        stepLabel: {
          employees: 'Select Employees',
          deliveryMode: 'Delivery Address',
          summary: 'Complete Order',
        },
        searchLabel: 'Select Usage Option',
        searchPlaceholder: 'Search by region or postal code area',
        noOptions: 'No results',
        pleaseSearch: 'Enter search term',
        selectVariantError: 'Please select a usage option',
        selectableEmployeesHint:
          'Note that only employees with a registered employee number, valid address, and date of birth, for whom no credit card has yet been ordered, can be selected.',
        summaryInfo:
          'Below you will find a summary of your order.<br/><br/>Click <strong>Complete Order</strong> to place a binding order for the credit cards.<br/><br/>Note that card activation can take up to 5 business days.',
        summary: 'Summary',
        noEmployeesWarning:
          'No employees were found for the order. Please note that an employee number, valid address, and date of birth, are required to order.<br/><br/>Please correct your selection in the previous step.',
        summaryFields: {
          employeeCount: 'Number of Credit Cards',
          deliveryMode: 'Delivery Address',
        },
        finished: {
          someErrors: 'Credit cards have been ordered for {{successCount}} employees, {{errorCount}} were skipped.',
          error: 'Your order could not be placed. Please try again or contact our support.',
          success: 'Your order was placed successfully.',
        },
        actions: {
          next: 'Next',
          previous: 'Back',
          order: 'Complete Order',
        },
        deliveryMode: {
          label: 'Delivery Address',
          PERSONAL_ADDRESS: 'Employee Home Address',
          PERSONAL_ADDRESS_description: "The card will be sent directly to the employee's home address.",
          COMPANY_ADDRESS: 'Company Address',
          COMPANY_ADDRESS_description: 'The card will be sent to the company address.',
        },
      },
      budgetPanel: {
        cards: 'Cards',
        budget: 'Budget',
        orderCardsButton: 'Order Cards',
        activeCards: 'Active Cards',
        currentBudget: 'Current balance',
        openCharges: 'Open charges',
        remainingBudget: 'Remaining balance',
        missingBudget: 'Missing balance',
        nextMonthForecast: 'Forecasted balance',
        lastSync: '<strong>Updated:</strong> {{date,datetime}}',
        infoButtonLabel: 'Account & top-ups',
        infoDialog: {
          title: 'Account & top-ups',
          chargeOverviewTab: 'Charge overview',
          accountInfoTab: 'Account information',
          currentMonthTitle: 'This month',
          nextMonthTitle: 'Next month (Forecasted)',
          accountHolder: 'Account holder',
          iban: 'IBAN',
          ibanMissing: 'Not provided',
          amountNextCycle: 'Amount next cycle',
          deadlineNextCycle: 'Deadline next cycle',
          openAmount: 'Open amount',
          escrowInfo:
            'This account is an escrow account. Your company is the recipient of the funds. The money is held in trust and allocated to your company.',
        },
      },
    },
    DISCOUNT_CODE: {
      title: 'Employee Discounts',
      activatedTitle: 'Employee discounts that excite – free for employers.',
      activatedDescription:
        'Your employees get access to exclusive discount codes from over 200 top brands – from well-known names like RTL+, Tchibo, or New Balance to sustainable brands like Flixtrain, Koro, or Avocadostore. Over €1,000 in savings per year for your employees – activation is completely free and requires no additional administrative effort on your part as an employer.<br><br>Get started easily. Create value. Inspire your employees.',
      header: 'The new Probonio Benefit – Exclusive discounts for your employees, completely free for employers',
      discountAmountTitle: '5% to 50% discount',
      discountAmountDescription: 'Flexible discounts, fixed amounts, or free trial months',
      freeForEmployersTitle: 'Free for employers',
      freeForEmployersDescription: 'Completely free of charge. Easy integration into your existing Probonio system',
      allInTheAppTitle: 'All in the app',
      allInTheAppDescription: 'Employees can view all discounts and providers directly in their Probonio-app',
      howItWorks: 'How it works',
      automaticRedemptionTitle: 'Automatic redemption',
      automaticRedemptionDescription: 'Discounts are automatically applied via available links or redeemed with a code',
      fairUseTitle: 'Fair use',
      fairUseDescription: 'Employees can retrieve a maximum of 5 codes per day',
      discountTypes: 'Types of discounts',
      fixedAmounts: 'Fixed amounts',
      freeTrials: 'Free trial months',
      popularBrands: 'Popular brands available',
      plus100More: '+ 100 more',
    },
    CAR: {
      title: 'JobAuto',
      settings: {
        title: 'Settings',
      },
      activate: {
        alert: {
          infoTitle: 'Adjust Company ID',
          infoDescription:
            'If you would like to adjust the Company ID from your JobAuto portal in the Probonio portal, you can do so in the <settings>JobAuto Settings</settings>.',
          title: 'Activation not possible',
          description:
            'Activation is currently not possible because no Company ID has been set. You can change this under <settings>JobAuto Settings</settings>.',
        },
      },
      infoTextFull:
        '<p>With FINN JobAuto, you can offer your employees a car subscription as an attractive and climate-friendly benefit. The car subscription is enabled through salary conversion, allowing employees to save up to 40% compared to a regular car subscription.</p><p><strong>How it works:</strong></p><ul><li>Employers sign a framework agreement with FINN and set the conditions in the FINN company portal.</li><li>Employees then choose their vehicle and sign the individual usage contract directly with FINN.</li><li>The monthly cost of the car subscription is financed from employees’ gross salary via salary conversion.</li><li>The benefit is cost-neutral for employers in the Probonio portal.</li><li>With Care+, employers are additionally protected in special events.</li><li>Employers can freely decide whether to offer only electric vehicles or other drive types as well.</li></ul>',
      benefitTemplates: {
        editItemDialog: {
          info: 'If you want to adjust the Company ID from your JobAuto portal in the Probonio portal, you can do this under <settings>Settings</settings>.',
        },
      },
      bulkEditDialog: {
        changeOptions: {
          info: ' ',
        },
        pauseForm: {
          text: '<strong>Note:</strong> Deactivation only affects the display in the employee app and does not affect the active contract. <br>$t(benefitsModule:bulkEditDialog.pauseForm.text)',
        },
        deactivate: {
          mode: {
            info: '<strong>Note:</strong> Deactivation only affects the display in the employee app and does not affect the active contract. <br>$t(benefitsModule:bulkEditDialog.deactivate.mode.info)',
          },
        },
      },
      info: {
        title: 'JobAuto for Employers',
        description: 'JobAuto - the affordable car subscription through salary conversion',
        learnMore: 'Learn more now',
        connected: 'My company is already on JobAuto',
      },
      learnMore: {
        title: 'Excite your team with FINN | JobAuto',
        description:
          'As a Probonio customer, you know how to motivate your team with tax-free benefits and strengthen your employer branding. One especially popular benefit is FINN JobAuto, which is particularly attractive thanks to tax incentives and salary conversion options. With JobAuto, you and your team can enjoy these benefits easily and without risk.',
        save: 'Up to 40% savings on the monthly subscription',
        convert: 'Cost-neutral thanks to salary conversion',
        select: 'Wide selection of quickly available vehicles',
        reserve: 'Booking in under 5 minutes with home delivery',
        time: 'Flexible terms between 6-24 months',
        include: 'Includes insurance, taxes, GEZ, maintenance, and wear',
        package: 'Extensive coverage for unexpected events thanks to Care+ package',
        part: '~30% EV share & CO₂ compensation',
      },
      hint: {
        where: {
          title: 'Where can I find my Company ID?',
          description: 'In your FINN JobAuto portal under Settings > Invoice',
          button: 'Go to portal',
        },
        customer: {
          title: 'Become a JobAuto customer now',
          description: 'Register now to excite your team with FINN JobAuto.',
          button: 'Become a customer now',
        },
        faq: {
          title: 'Do you have questions about JobAuto service?',
          description:
            'Contact the FINN team via email at <email>jobauto@finn.com</email>. For more information about JobAuto, visit <site>finn.com/jobauto</site> or the <faq>FAQs</faq>.',
        },
        description: 'JobAuto - the affordable car subscription through salary conversion',
      },
      connect: {
        title: 'Edit Company ID',
        description:
          'Here you can change your FINN JobAuto Company ID. The ID will automatically be included in the registration link for employees so they are directly assigned to your company.',
        form: {
          name: 'Company ID',
          hint: 'Format: integer, 10-12 digits',
          error: 'Only 10-12 digit numbers are allowed.',
          success: 'Company ID saved',
        },
        alert: {
          title: 'Note',
          description:
            'After saving, your employees will receive the updated link with the new Company ID when registering. Make sure the ID is correct before saving changes.',
        },
      },
    },
    IT_LEASING: {
      title: 'IT-Leasing',
      info: {
        title: 'IT leasing for you as an employer',
        description: 'Do you already have an OwnIT@home account?',
        learnMore: 'Learn more now',
        connected: 'My company is already with IT Leasing',
      },
      activate: {
        alert: {
          infoTitle: 'Customize registration link',
          infoDescription:
            'If you want to customize the registration link from your OwnIT@home portal in the Probonio portal, you can do so in the <settings>IT leasing settings</settings>.',
          title: 'Activation not possible',
          description:
            'Activation is not yet possible because no registration link has been stored. You can change this under <settings>IT leasing settings</settings>.',
        },
      },
      infoTextFull:
        '<strong>IT leasing with OwnIT@home</strong><br>With OwnIT@home, you can offer your employees high-quality IT devices such as laptops, tablets, smartphones, and much more at a tax advantage—savings of up to 30% are possible.<br><br><strong>How it works:</strong><ul><li>As an employer, you conclude a leasing and framework agreement as the basis for providing the benefit. </li><li>Your employees then select their desired devices in the shop. </li><li>The monthly costs are financed from the gross salary via tax-free deferred compensation in accordance with § 3 No. 45 EStG (German Income Tax Act). </li><li>For you as an employer, the benefit is cost-neutral with minimal administrative effort. </li></ul>',
      bulkEditDialog: {
        changeOptions: {
          info: ' ',
        },
        pauseForm: {
          text: '<strong>Please note:</strong> The pause only affects the display in the employee app and has no impact on the current contract. <br>$t(benefitsModule:bulkEditDialog.pauseForm.text)',
        },
        deactivate: {
          mode: {
            info: '<strong>Please note:</strong> Deactivation only affects the display in the employee app and has no effect on the current contract. <br>$t(benefitsModule:bulkEditDialog.deactivate.mode.info)',
          },
        },
      },
      settings: {
        title: 'Settings',
      },
      connect: {
        title: 'Connect your OwnIT@home account',
        description: 'Enter your registration link from the OwnIT@home portal so that your employees can take advantage of this benefit.',
        form: {
          name: 'Registration link',
          hint: 'Format: https://...',
          error: 'Please enter the correct link',
          success: 'Employer link saved',
        },
        saveButton: 'Add link',
        alert: {
          title: 'Note',
          description:
            'After saving, your employees will receive the updated link when they register. Make sure the link is correct before saving the changes.',
        },
      },
      learnMore: {
        about: {
          title: 'What is OwnIT@home?',
          description:
            'OwnIT@home offers companies a cost-neutral and innovative way to promote the sustainable use of modern technologies. It enables your employees to use high-quality IT devices such as laptops, tablets, smartphones, and much more at a tax advantage—with savings of up to 30% possible.',
        },
        howItWorks: {
          title: 'How does it work?',
          description:
            'The program is based on the principle of deferred compensation, whereby part of the gross salary is used to finance the devices. The big advantage: private use of these devices remains tax-free in accordance with Section 3 No. 45 of the German Income Tax Act (EStG), so that both companies and employees benefit.',
        },
        advantages: {
          title: 'Benefits for employers',
          first: {
            title: 'Attractiveness as an employer:',
            description:
              'Present yourself as a progressive employer who offers its employees innovative IT benefits and thus specifically promotes their digital development.',
          },
          second: {
            title: 'No additional costs',
            description:
              'OwnIT@home is a cost-neutral solution, as the devices are financed through a <strong>tax-free deferred compensation plan</strong>. There are also no costs for implementation and service.',
          },
          third: {
            title: 'Minimal administrative effort',
            description: 'The setup and administration of OwnIT@home is done entirely digitally via our user-friendly portal.',
          },
        },
      },
      hint: {
        where: {
          title: 'Where can I find the registration link?',
          description: '<ul><li>In your OwnIT@home portal under “Home” → “Probonio connection”</li></ul>',
        },
        customer: {
          title: "Don't have OwnIT@home yet?",
          description: 'Register your company and inspire your team with the IT benefit soon.',
          button: 'Become a customer now',
        },
        faq: {
          title: 'Questions about OwnIT@home?',
          description: 'Feel free to contact the OwnIT team by email at <email>hr-support@own-it.de</email>.',
        },
        support: {
          title: 'Are you having problems?',
          description: 'Contact OwnIT Support <supportEmail>hr-support@own-it.de</supportEmail>',
        },
      },
    },
    WELLPASS: {
      title: 'EGYM Wellpass',
      subTitle: 'EGYM Wellpass Fitness Benefit',
      settings: {
        title: 'Settings',
      },
      connect: {
        title: 'Transfer existing Wellpass memberships',
        description:
          'If you already use EGYM Wellpass, you can transfer existing memberships via CSV export to Probonio. We match the data with your employees and save the Wellpass email address.',
        steps: {
          export: {
            title: 'Fill out the Wellpass form',
            description: 'Open the Wellpass form via the link and complete the onboarding process at Wellpass.',
            button: 'Open the Wellpass form',
            imageAlt: 'Screenshot: CSV export in the Wellpass portal',
            imageCaption: 'Example: „Export overview as CSV“ in the Wellpass portal.',
          },
          importEmployees: {
            title: 'Import memberships',
            description: 'Upload the exported CSV to assign employees (Name → Birthdate → Email).',
            sections: {
              loginPortal: {
                title: 'Open login portal',
                description: 'Open the login portal via the link and log in with your Wellpass login credentials.',
                button: 'Open login portal',
              },
              upload: {
                title: 'Upload CSV and import',
                description: 'Upload the CSV exported from Wellpass. Probonio automatically matches employees by name and date of birth.',
                button: 'Upload CSV',
                importedBadge: 'Last import on {{date}}',
                importedSummary:
                  'Last import result: {{total}} rows, {{updated}} updated, {{unchanged}} unchanged, {{notFound}} not found, {{notPossible}} not possible.',
                tableHeaderStartDate: 'Start date',
                tableHeaderActivationStartDate: 'Activation start',
                tableHeaderEmployeeName: 'Employee',
                tableHeaderWellpassEmail: 'Wellpass Email',
                tableHeaderProbonioEmail: 'Probonio Email',
                tableHeaderNewBenefits: 'New benefits',
                tableHeaderStatus: 'Status',
              },
              activate: {
                title: 'Activate synchronization',
                description:
                  'In order to activate the synchronization to EGYM Wellpass and use the benefit, you must activate the synchronization. <strong>This step cannot be undone.</strong>',
                button: 'Start synchronization',
                startConfirmTitle: 'Activate synchronization now?',
                startConfirmDescription:
                  'This action starts the sync process with EGYM Wellpass. <strong>This step cannot be undone.</strong>',
                startConfirmButton: 'Start sync now',
                confirmTitle: 'You have employees that could not be activated',
                confirmDescription:
                  'The following {{count}} employees could not be activated during the last import. Please review the data before starting the synchronization.',
                confirmButton: 'Still activate',
                tableHeaderName: 'Name',
                tableHeaderEmail: 'Email',
                success: 'Synchronization activated successfully.',
                error: 'Activation of synchronization failed. Please try again.',
              },
            },
          },
        },
        importedEmployees: {
          title: 'Imported employees',
          description: '{{count}} employees were imported and can now be activated for benefits.',
          empty: 'No employees were imported.',
        },
        activateImported: {
          button: 'Activate benefits for imported employees',
          result: '{{success}} activated, {{error}} could not be activated',
        },
        sync: {
          enableButton: 'Activate synchronization',
          enabled: 'Synchronization activated',
        },
        enrich: {
          title: 'Upload Wellpass CSV',
          description:
            'Upload the exported CSV from Wellpass (first name, last name, date of birth). Probonio will automatically look up the Probonio email address based on name and date of birth. Missing addresses can be entered manually.',
          rowsLoaded: '{{count}} rows loaded.',
          matchButton: 'Look up email addresses automatically',
          rematchButton: 'Re-match',
          proceedButton: 'Proceed to import',
          allMatched: 'All {{matched}} employees were matched automatically.',
          partialMatch: '{{matched}} matched, {{ambiguous}} ambiguous, {{notFound}} not found — please fill in manually.',
          error: 'Matching failed. Please try again.',
          table: {
            firstName: 'First name',
            lastName: 'Last name',
            birthDate: 'Date of birth',
            startDate: 'Start date',
            probonioMail: 'Probonio email',
            status: 'Status',
          },
          status: {
            MATCHED: 'Found',
            AMBIGUOUS: 'Ambiguous',
            NOT_FOUND: 'Not found',
          },
        },
        import: {
          title: 'Link Wellpass memberships',
          steps: {
            upload: 'Upload CSV',
            enrich: 'Match employees',
            preview: 'Preview',
            done: 'Done',
          },
          next: 'Next',
          fileSelected: '{{name}} selected.',
          importSuccess: 'Import completed.',
          cancel: 'Cancel',
          close: 'Close',
          preview: 'Check results',
          importNow: 'Link now',
          importDisabledAfterSettingsChange: 'Benefit settings were changed after the last check. Use "Check again" before linking.',
          previewOutdated: 'Benefit settings changed — the preview below no longer matches. Use "Check again" to refresh before linking.',
          statusPreviewOutdated: 'Preview outdated — check again',
          ready: '{{count}} rows detected. You can start the import.',
          carrierSettingsPreview: 'Benefit activation settings (this preview)',
          carrierSettingsHint:
            'These options apply when the import creates a new benefit activation or adds Wellpass to the carrier benefit. They match your normal activation settings.',
          noNewActivationRequired: 'No new benefit activations will be created for this import.',
          previewAgain: 'Check again',
          summary: {
            updated: 'Updated',
            unchanged: 'Unchanged',
            notFound: 'Not found',
            notPossible: 'Not possible (incl. budget & usage blocks)',
            ignoredFutureStart: 'Ignored (start in 2+ months)',
          },
          errors: {
            parseWithLine: 'Error in line {{line}}:',
            missingRequiredField: 'Required field missing: {{field}}',
            invalidDateFormat: 'Invalid date format. Please use e.g. 30.01.2025.',
            duplicateMailInFile:
              '{{column}} "{{email}}" appears in more than one row (rows {{lines}}). Remove or correct duplicates before importing.',
            unknown: 'File could not be read.',
            previewFailed: 'Check failed. Please try again.',
            submitFailed: 'Import failed. Please try again.',
          },
          table: {
            startDate: 'Start date',
            wellpassMail: 'Wellpass Email',
            probonioMail: 'Probonio Email',
            employee: 'Employee',
            activationStartDate: 'Activation start',
            newBenefits: 'New Benefits',
            status: 'Status',
            empty: 'No rows in this preview.',
          },
          status: {
            UPDATED: 'Updated',
            UNCHANGED: 'Unchanged',
            NOT_FOUND: 'Not found',
            NOT_POSSIBLE: 'Not possible',
            NOT_ENOUGH_BUDGET: 'Not enough budget',
            BLOCKED_BY_USAGE: '{{benefit}} was already used for this month.',
            IGNORED_FUTURE_START: 'Ignored — membership starts two or more calendar months from now.',
          },
        },
      },
      info: {
        title: 'EGYM Wellpass for you as an employer',
        description: 'Offer your employees access to an extensive sports and wellness network with EGYM Wellpass.',
        learnMore: 'Learn more',
        alreadyCustomer: 'Already a customer of EGYM Wellpass',
      },
      learnMore: {
        title: 'Inspire your team with EGYM Wellpass',
        description:
          'With EGYM Wellpass, your employees gain flexible access to a network of over 13,500 sports and health facilities. Users can also access online courses, live sessions, and yoga and meditation apps via the accompanying app.',
        liked: 'Popular benefit for strengthening employer branding',
        config: 'Easy management via the Probonio portal',
        flexible: 'Flexible: numerous locations nationwide, cancelable monthly',
        fitness:
          'Access to thousands of sports and wellness offerings: gyms, swimming pools, yoga studios, bouldering halls, and much more',
        health: 'Promotion of health and well-being of your employees',
        team: 'Boosts the satisfaction of your team',
      },
      registered: {
        title: 'Are you already an EGYM Wellpass customer?',
        description:
          'If you are already an EGYM Wellpass customer, we will guide you through this migration process to transfer existing memberships to Probonio.',
      },
      hint: {
        register: {
          title: 'Register with EGYM Wellpass',
          description: 'Register your company with EGYM Wellpass to give your team access to an extensive sports and wellness network.',
          button: 'Register now',
        },
      },
      process: {
        title: 'How it works',
        step1: {
          titles: {
            title: 'Request now',
            done: 'Requested',
          },
          description:
            'Click the "Request now" button to register with EGYM Wellpass. You will be redirected directly to EGYM Wellpass to start your registration. You can track the status of the process here.',
        },
        step2: {
          titles: {
            title: 'Complete KYB process',
            done: 'KYB process completed',
          },
          description:
            'After registration, EGYM Wellpass will conduct a business identification process. EGYM Wellpass will request relevant company data and documents.',
        },
        step3: {
          titles: {
            title: 'Configuration is being synchronized',
            done: 'Configuration synchronized',
          },
          description:
            'Once your registration with EGYM Wellpass has been approved, the configuration will be synchronized with Probonio for you.',
        },
        step4: {
          titles: {
            title: 'Manage employees',
            done: 'Manage employees',
          },
          description:
            'Once the configuration is complete, you will see the message "EGYM Wellpass is active" here. You can then activate EGYM Wellpass for selected or all employees.',
        },
      },
      activated: {
        title: 'Connection successfully completed',
        description: 'Congratulations! Your company is now fully connected with EGYM Wellpass and ready for the next step.',
        usageLabel: 'Current usage',
        usageValue: '{{count}} employees currently use Wellpass',
        budgetLabel: 'Monthly budget',
        budgetValue: '{{amount, euro-float}} / employee',
        registerLink: 'Employee registration link',
        toEmployees: 'Manage employees',
      },
      support: {
        title: 'Do you have questions about EGYM Wellpass?',
        description:
          'For questions about EGYM Wellpass, please contact EGYM Wellpass Support by email at <email>info@egym-wellpass.com</email>. You can also find more information about EGYM Wellpass under <site>www.egym-wellpass.com</site> or in the <faq>FAQs</faq>.',
        button: 'Contact Support',
      },
    },
  },
  common: {
    clearFilter: 'Clear filter',
    fileToBig: 'The selected file is too big. Maximum is {{maxKB}}KB.',
    moreInformation: 'More Information',
    noImageSelected: 'No image selected',
    countryCode: {
      DE: 'Germany',
      AT: 'Austria',
    },
    role: {
      USER: {
        name: 'Employee',
      },
      TENANT_ADMIN: {
        name: 'Administrator',
      },
      TENANT_ASSISTANT: {
        name: 'Auditor',
      },
    },
    editor: {
      placeholder: 'Write info text here...',
      attachments: 'Attachments',
    },
    customBenefits: 'Custom Benefits',
    moreBenefits: 'More benefits',
    deletedBenefit: 'Deleted Benefit',
    benefit: {
      BAV: 'Company pension plan',
      LUNCH: 'Lunch vouchers',
      MOBILITY: 'Mobility',
      COUPONS: 'Gift cards',
      COUPONS_V2: 'Gift cards flex',
      INTERNET: 'Internet benefit',
      GIFTS: 'Non-cash benefits',
      FITNESS: 'Corporate Fitness',
      WELLPASS: 'Corporate Fitness',
      CUSTOM: 'Create new benefit',
      RECREATION: 'Recovery allowance',
      KITA: 'Daycare Subsidy',
      BIKE: 'Company bike',
      BIKELEASING_V2: 'Bikeleasing',
      BIKELEASING: 'Bikeleasing',
      NEWS: 'Employee communication',
      FLEX: 'Flex Budget',
      CREDIT_CARD: 'Credit Card',
      DISCOUNT_CODE: 'Employee Discounts',
      AT_LUNCH: 'Lunch vouchers',
      AT_MOBILITY: 'Public Transport Ticket',
      CAR: 'JobAuto',
      IT_LEASING: 'IT Leasing',
    },
    userStatus: {
      ACTIVE: 'Active',
      REGISTERED: 'Waiting for unlock',
      PENDING: 'Invited',
      CREATED: 'Not invited',
      INVITATION_CODE: 'Registration code created',
      MAIL_DELIVERY_ERROR: 'Mail delivery error',
      EXPIRED: 'Invitation expired',
      INACTIVE: 'Terminated',
      PLANNED_TERMINATION: 'Termination planned',
    },
    userStatusButton: {
      CREATED: 'Invite',
      REGISTERED: 'Enable',
      EXPIRED: 'Reinvite',
      PENDING: 'Reinvite',
      SCHEDULED: 'Invite immediately',
      tooltip: {
        createdTooltip: 'We will immediately invite the employee',
        registeredTooltip: 'Activate the employee’s access immediately',
        pendingTooltip: 'We will immediately reinvite the employee',
        expiredTooltip: 'We will immediately reinvite the employee',
        scheduledTooltip:
          'Upon pressing the button we will invite the employee immediately, otherwise they will automatically be invited on the {{scheduledInviteDate}}.',
      },
    },
    employeeType: {
      FULL_TIME: 'Full time',
      PART_TIME: 'Part time',
      INTERN: 'Internship',
      STUDENT: 'Student',
      MINI_JOB: 'Mini job',
      APPRENTICESHIP: 'Apprenticeship',
      TRAINING: 'Training',
      EXTERNAL: 'External',
      MISC: 'Other',
    },
    civilStatus: {
      SINGLE: 'single',
      MARRIED: 'married',
      DIVORCED: 'widowed',
      WIDOWED: 'divorced',
      CIVIL_UNION: 'reg. civil union',
      CIVIL_UNION_DECEASED: 'reg. civil union (partner deceased)',
      CIVIL_UNION_CANCELLED: 'reg. civil union cancelled',
    },
    buttons: {
      close: 'Close',
      abort: 'Cancel',
      create: 'Create',
      save: 'Save',
      add: 'Add',
      delete: 'Delete',
      stop: 'Stop',
      discard: 'Discard',
      next: 'Next',
      back: 'Back',
      upload: 'Upload',
      activate: 'Activate',
      orderWithPayment: 'Order now',
      saveDisabledTooltip: 'Please make changes to save',
      requestNow: 'Request now',
      confirm: 'Confirm',
      syncNextToolTip: 'Next synchronization possible: {{date , datetime}}',
      syncNow: 'Sync now',
    },
    money: '{{money, euro}}',
    date: '{{date, date}}',
    dateTime: '{{date, datetime}}',
    confirmation: 'Confirmation',
    confirmationModal: {
      title: 'Confirmation required',
      confirm: 'Confirm',
    },
    unsavedChanges: {
      message: "Your changes haven't been saved yet. Are you sure you want to discard your changes?",
      backToForm: 'Go back',
      discard: 'Discard changes',
    },
    fileUpload: {
      dropHere: 'Drop file here',
      divider: 'or',
      browse: 'Select file to upload',
      acceptTypes: 'Supported file types: <strong> {{acceptTypes}} </strong>',
    },
    loading: 'Loading...',
    completed: '{{ progress }}% completed',
    loadMoreData: 'Loading more entries',
    noResults: 'No results found',
    inputRequired: 'Input required',
    copyToClipboard: 'Copy to clipboard',
    copyToClipboardSuccess: 'Copied',
    noFileSelected: 'No file selected',
    materialReactTableLocale: MRT_Localization_EN,
    yes: 'Yes',
    no: 'No',
    anonymousEmployee: 'New employee',
    anonymousEmployeeWithNumber: 'New employee #{{employeeNumber}}',
    pleaseSelect: 'Please select',
  },
  homePage: {
    title: 'Probonio Dashboard',
    subTitle: 'Welcome {{name}}! 👋🏻',
    support: {
      title: "We're here for you.",
      question: 'Need help?',
      buttonText: 'Request support',
      buttonText2: 'Contact support',
      dialogTitle: 'Contact customer service',
      description:
        'We are happy to assist you with using our portal. Which topic do you need support with? Please select below for useful contact options.',
      whatsApp: 'WhatsApp Chat',
      manual: 'View manual',
      calendar: 'Make a reservation',
      cancel: 'No thanks',
      confirm: 'Yes please',
      general: {
        title: 'General portal administration',
        subtitle: 'Support for account, the employer portal or other questions',
        phone: 'Phone number',
        whatsapp: 'Personal WhatsApp Chat',
        mail: 'E-Mail',
      },
      helpCenter: 'View Helpcenter',
      benefits: {
        title: 'Employee Benefits',
        subtitle: 'Support for all other benefits like discounts, gifts and more',
        generalSupport: 'General support',
        accounting: 'Accounting',
        sales: 'Sales',
        bikeleasing_v2: 'Bikeleasing',
        companyPension: 'Company pension plan',
        daycare: 'Daycare subsidy',
        employeeCommunication: 'Employee communication',
        discountCodes: 'Employee discount',
        flexBudget: 'Flex budget',
        giftCards: 'Gift cards',
        internet: 'Internet benefit',
        itLeasing: 'IT Leasing',
        electricCar: 'Job Car',
        lunch: 'Lunch vouchers',
        mobility: 'Mobility',
        recreation: 'Recovery allowance',
      },
    },
  },
  usersPage: {
    title: 'Employee data',
    addUser: 'Add employee',
    success: 'Successfully added',
    search: 'Search',
    editMode: 'Change benefit configuration',
    closeEditMode: 'Exit edit mode',
    inviteNewUsersButton: {
      buttonText: 'Invite all employees ({{count}})',
      confirmTitle: 'Invite employees',
      confirmText_one: 'Do you really want to invite {{count}} employee?',
      confirmText_other: 'Do you really want to invite {{count}} employees?',
    },
    reinviteUsersButton: {
      buttonText: 'Reinvite all expired employees ({{count}})',
      confirmTitle: 'Reinvite employees',
      confirmText_one: 'Do you really want to reinvite {{count}} expired employee?',
      confirmText_other: 'Do you really want to reinvite {{count}} expired employees?',
    },
    unlockAllEmployees: {
      buttonText: 'Unlock all employees ({{count}})',
      confirmTitle: 'Unlock employees',
      confirmText_one: 'Do you really want to unlock {{count}} employee?',
      confirmText_other: 'Do you really want to unlock {{count}} employees?',
    },
    tab: {
      duplicates: 'Duplicates',
      all: 'All',
      active: 'Active',
      receivedFrom: 'Received from',
      scheduledFor: 'Scheduled for',
      registration: 'Registration',
      inactive: 'Terminated',
    },
  },
  registerPage: {
    redirecting: 'Redirecting',
    pleaseWait: 'You will be redirected to register shortly, please wait...',
    linkExpired: 'The registration link has expired',
    useUpdatedLink: 'You should have already received an e-mail with a new link. If not, please request a new link from your employer.',
  },
  receiptsModule: {
    receiptsNotAllCheckedWarning:
      ' We have not checked all receipts yet. If you proceed to download the report now it will not include the unchecked receipts.',
    periodNotClosed:
      'Caution, the selected billing period is still valid until {{val, date}}. Benefits which have not been used yet will not be listed in the report and cannot be paid out.',
    periodNotClosedReceipts:
      'Caution, the selected billing period is still valid until {{val, date}}, upload of vouchers is still possible until {{periodCloseDay}}. Vouchers that have not yet been uploaded or processed will not be listed in the report and cannot be paid out.',
    lodasParametersMissing: 'Please fill in all relevant wage type fields.',
    salaryConversionParameterMissing:
      'For one or more employees the payroll type "$t(benefitsModule:LUNCH.fields.salaryConversation)" is used. Please store a value in the field "$t(receiptsModule:salaryTypeSalaryReduction)"',
    mobilityTaxableParameterMissing:
      'One or more employees are using the billing method "$t(benefitsModule:MOBILITY.fields.taxationType.TAXABLE)" . Please enter a value in "$t(receiptsModule:salaryTypeMobilityTaxable)".',
    giftsParameterMissing:
      'Gifts were given to one or more employees. Please enter a value in the field „$t(receiptsModule:salaryTypeGifts)“.',
    incentivesParameterMissing:
      'Incentives 37b were issued to one or more employees. Please enter a value in the field „$t(receiptsModule:salaryTypeIncentives)“.',
    employeeNumbersMissing:
      'To create a tax report, all employees need a valid numeric employee number. You can add these in the employee data sheet.',
    lunchTaxFreeParameterMissing:
      'For one or more employees the benefit „$t(benefitsModule:LUNCH.title)“ is used. Please enter a value in „$t(receiptsModule:salaryTypeLunchTaxFree)“.',
    internetParameterMissing:
      'The benefit “$t(benefitsModule:INTERNET.title)” is used for one or more employees. Please provide a value in the field “$t(receiptsModule:salaryTypeInternet)”.',
    fitnessParameterMissing:
      'The benefit “$t(benefitsModule:FITNESS.title)” is used for one or more employees. Please provide a value in the field “$t(receiptsModule:salaryTypeFitness)”.',
    wellpassParameterMissing:
      'The benefit “$t(benefitsModule:WELLPASS.title)” is used for one or more employees. Please provide a value in the field “$t(receiptsModule:salaryTypeWellpass)”.',
    kitaParameterMissing:
      'The benefit “$t(benefitsModule:KITA.title)” is used for one or more employees. Please provide a value in the field “$t(receiptsModule:salaryTypeKita)”.',
    recreationParameterMissing:
      'The benefit “$t(benefitsModule:RECREATION.title)” is used for one or more employees. Please provide a value in the field “$t(receiptsModule:salaryTypeRecreation)”.',
    mobilityTaxFreeParameterMissing:
      'The payroll type “$t(benefitsModule:MOBILITY.fields.taxationType.TAX_FREE)” is used for one or more employees. Please provide a value in the field “$t(receiptsModule:salaryTypeMobilityTaxFree)”.',
    creditCardParameterMissing:
      'The benefit “$t(benefitsModule:CREDIT_CARD.title)” is used for one or more employees. Please provide a value in the field “$t(receiptsModule:salaryTypeCreditCard)”.',
    couponsParameterMissing:
      'The benefit “$t(benefitsModule:COUPONS.title)” is used for one or more employees. Please provide a value in the field “$t(receiptsModule:salaryTypeCoupons)”.',
    payrollExportHistoryModal: {
      title: 'Export history',
      showHistory: 'Show history',
      rateLimit: 'Too many requests',
      successToast: 'Mail has been sent',
      noResults: 'No exports were found',
      createdBy: 'Created by',
      automaticDispatch: 'Automatic dispatch',
      resendExport: 'Resend export',
    },
    cancel: 'Cancel',
    download: 'Download',
    downloadHint: 'Download Report',
    downloadTitle: 'Generate payroll export',
    consultantNumber: 'Consultant number',
    consultantNumberHelper: 'The DATEV consultant number for your local tax office',
    consultantNumberError: 'Only numbers from 1000 to 9999999 are allowed.',
    tenantNumber: 'Mandate number',
    tenantNumberHelper: 'The mandate number given to your company by your local tax office',
    tenantNumberError: 'Only numbers from {{min}} to {{max}} are allowed.',
    salaryTypeLunchTaxFree: 'Wage type: Tax-free',
    salaryTypeLunchTaxFreeHelper: 'This wage type is used to bill the tax-free proportion',
    salaryTypeLunchTaxable: 'Wage type: Flat rate tax',
    salaryTypeLunchTaxableHelper: 'This wage type is used to bill the flat rate tax proportion',
    salaryTypeMobilityTaxFree: 'Wage type: Tax-free',
    salaryTypeMobilityTaxFreeHelper: 'This wage type is used to bill the tax-free proportion',
    salaryTypeMobilityTaxable: 'Wage type: Taxable',
    salaryTypeMobilityTaxableHelper: 'This wage type is used to bill the taxable proportion',
    salaryTypeSalaryReduction: 'Wage type salary reduction',
    salaryTypeSalaryReductionHelper:
      'This wage type is used to display the salary reduction on the payroll. Each month it amounts to the maximum amount configured for the employee',
    salaryTypeCoupons: 'Wage Benefit in Kind 50',
    salaryTypeCouponsHelper: 'This wage type is used for the payroll run for Benefit in Kind 50',
    salaryTypeGifts: 'Wage type gifts',
    salaryTypeGiftsHelper: 'This wage type is used for the accounting of the gifts',
    salaryTypeFitness: 'Wage Corporate Fitness',
    salaryTypeFitnessHelper: 'This wage type is used for the payroll run for Corporate Fitness',
    salaryTypeWellpass: 'Wage Corporate Fitness',
    salaryTypeWellpassHelper: 'This wage type is used for the payroll run for Corporate Fitness',
    salaryTypeCreditCard: 'Wage Corporate Credit Card',
    salaryTypeCreditCardHelper: 'This wage type is used for the payroll run for Credit Card',
    salaryTypeIncentives: 'Wage Type Incentive 37b',
    salaryTypeIncentivesHelper: 'This wage type is used for the accounting of the Incentives 37b',
    salaryTypeInternet: 'Wage type Internet benefit',
    salaryTypeInternetHelper: 'This wage type is used for payroll accounting of the Internet benefit',
    salaryTypeKita: 'Salary type for childcare subsidy',
    salaryTypeKitaHelper: 'This salary type is used for processing the daycare subsidy',
    salaryTypeATLunch: 'Lunch vouchers Wage Type',
    salaryTypeATLunchHelper: 'This wage type is used for payroll processing of the lunch vouchers',
    salaryTypeATMobilityHelper: 'This wage type is used for payroll processing of the public transport allowance',
    salaryTypeATMobility: 'Wage type public transport ticket',
    salaryTypeRecreation: 'Wage type recreation allowance',
    salaryTypeRecreationHelper: 'This wage type is used for payroll accounting of the recreation allowance',
    exportType: 'Export as...',
    exportMonth: 'Billing period',
    exportMonthHelper:
      'The billing period, which will be shown in the report. You only need to change the predetermined month in exceptions.',
    exportSwitchInterval: 'Switch selection to a time period',
    exportSwitchMonth: 'Switch selection to one month',
    processingMonth: 'Processing month',
    processingMonthDescription: 'Changes only necessary in special cases',
    processingMonthHelper: 'The processing month, which is specified in the export. Normally this is the billing period + 1',
    flexBenefitsToInclude: 'Benefits to export',
    selectExportDialog: {
      title: 'Integration for payroll accounting',
      introText: 'What software do you or your local tax office use for payroll accounting?',
      warning:
        'Are you sure you want to use {{tenant}} as your payroll accounting software? This may only be changeable again by contacting support!',
      infoTestAccount:
        "As exports aren't available in the test-version, we will contact you shortly regarding further development and completing the integration.",
      close: 'Close',
      confirm: 'Confirm',
    },
    salaryConversion: 'Salary conversion',
    salaryConversionDescription: 'Inputs required only in case of salary conversion',
    settingsForm: {
      title: 'Payroll export settings',
      info: 'Configure here the parameters for the payroll data export of the $t(benefitsModule:{{benefit}}.title) benefit.',
      section: {
        automatedReports: {
          title: 'Automatic report delivery',
          info: 'Choose whether you want to receive the payroll data reports automatically by email each month.',
        },
        tenantConsultantNumber: {
          title: 'Data of the tax office',
          info: 'Configure your client data that identifies your company to your tax office.',
        },
        salaryTypes: {
          title: 'Salary types',
          info: 'Salary types are used to account for individual benefits in payroll.',
          infoFreeText:
            'Salary types are used to account for individual benefits in payroll. Please enter the salary types exactly as they are named in your payroll accounting system.',
        },
      },
    },
    imageView: {
      receipt: 'Receipt',
      rotate: 'Rotate 90°',
      centerView: 'Center view',
      downloadImage: 'Download image',
      downloadPDF: 'Download PDF',
    },
    receiptDetails: {
      drawerTitle: 'Receipt: $t(common:benefit.{{benefit}}) of {{date,date}}',
      employee: 'Employee',
      date: 'Date',
      total: 'Total',
      status: 'Status',
      comment: 'Comment',
      postalCode: 'Zip code of the retailer',
      auditedBy: 'Audited by',
      probonioAudit: 'Probonio Receipt Audit',
      refund: 'Refund',
      potentialRefund: 'Potential refund',
      lunch: {
        refundTaxable: 'Flat rate taxed amount',
        refundTaxFree: 'Tax-free amount',
      },
      atLunch: {
        refund: {
          title: 'Allowed Receipts',
          SUPERMARKET: 'Supermarket, Snack Bar',
          RESTAURANT: 'Restaurant, Eatery',
        },
      },
      mobility: {
        refundTaxable: 'Refund (fully taxed)',
        refundTaxFree: 'Refund (tax-free)',
        mobilityType: {
          title: 'Mobility type',
          LOCAL_TRAIN: {
            title: 'Public transport',
          },
          LOCAL_BUS: {
            title: 'Local bus',
          },
          LONG_DISTANCE_TRAIN: {
            title: 'Long distance train',
          },
          LONG_DISTANCE_BUS: {
            title: 'Long distance bus',
          },
          CAR: {
            title: 'Car',
          },
          SHARING_OFFERS: {
            title: 'Sharing offers',
          },
          TAXI: {
            title: 'Taxi',
          },
          BIKE: {
            title: 'Bike',
          },
          TIME_CARD: {
            title: 'Time Card',
          },
          DEUTSCHLAND_TICKET: {
            title: 'Deutschland ticket',
          },
          OTHERS: {
            title: 'Other',
          },
        },
        travelPurpose: {
          title: 'Travel purpose',
          WORK: {
            title: 'Commute',
          },
          PRIVATE: {
            title: 'Private trip',
          },
        },
        validity: 'Validity',
        validityMonths: '{{validityMonths}} months starting {{validityStartDate,monthAndYear}}',
      },
      atMobility: {
        validityStartMonth: 'Start',
        durationMonths: 'Duration',
      },
      recreation: {
        refund: 'Refund (flat rate taxed)',
      },
    },
    receiptChecklist: {
      LUNCH: [
        {
          checklistText: 'Receipt is complete and legible',
        },
        {
          checklistText: 'Name and address of the issuing company are included',
        },
        {
          checklistText: 'Receipt is from Germany',
        },
        {
          checklistText: 'Selected date matches the receipt',
        },
        {
          checklistText: 'Amount entered matches the receipt',
        },
        {
          checklistText: 'No alcohol, no non-food items',
        },
      ],
      MOBILITY: [
        {
          checklistText: 'Receipt is complete and legible',
        },
        {
          checklistText: 'Selected date matches the receipt',
        },
        {
          checklistText: 'Selected date matches the receipt (monthly pass)',
        },
        {
          checklistText: 'Receipt is from local or long-distance traffic',
        },
        {
          checklistText: 'Amount entered matches the receipt',
        },
      ],
      RECREATION: [
        {
          checklistText: 'Receipt is complete and legible',
        },
        {
          checklistText: 'Name and address of the issuing company are included',
        },
        {
          checklistText: 'Amount entered matches the receipt',
        },
        {
          checklistText: 'Receipt is from this year',
        },
        {
          checklistText: 'Receipt meets the criteria for a recreation allowance',
        },
      ],
      AT_LUNCH: [
        {
          checklistText: 'Receipt is complete and easy to read',
        },
        {
          checklistText: 'Name and address of the issuing company are included',
        },
        {
          checklistText: 'Receipt was issued in Austria',
        },
        {
          checklistText: 'Selected date matches the receipt',
        },
        {
          checklistText: 'Entered amount matches the receipt',
        },
        {
          checklistText: 'No alcohol and no non-food items',
        },
        {
          checklistText: 'If applicable, compliance with preparation in a restaurant or by a delivery service has been verified',
        },
      ],
      AT_MOBILITY: [
        {
          checklistText: 'Receipt is complete and legible',
        },
        {
          checklistText: 'Receipt was issued in Austria',
        },
        {
          checklistText: 'Amount entered matches the receipt',
        },
        {
          checklistText: 'Validity period matches the receipt',
        },
      ],
    },
    auditForm: {
      approvalCriteria: 'Acceptance criteria',
      rejectionReason: 'Rejection reason',
      receiptAudit: 'Receipt audit',
      receiptAuditInfo: 'If you would like to explicitly accept or reject the current receipt, please select the corresponding option.',

      revokeApprove: 'Revoke approval',
      revokeReject: 'Revoke rejection',
      approveTitle: 'Do you really want to accept the receipt?',
      approveText: 'The Probonio audit is discarded and the refund amount is paid out.',
      approve: 'Accept',
      rejectTitle: 'Do you really want to reject the receipt?',
      rejectText: 'The employee is informed, the reason for rejection can be viewed and the refund amount is not paid out.',
      reject: 'Reject',
      updateSuccess: 'Receipt has been updated',
      approvedByTenant: 'The receipt has been accepted by the employer. If you wish to change your decision, click on "Revoke approval".',
    },
  },

  couponsModule: {
    reportDialogMessage:
      'This report contains all information about when and how much Benefit in Kind remuneration was granted to the employee. You can download the report if required or save it as an attachment to your payroll account.',
    successDeleteGift: 'Present successfully deleted',
    giftAlreadyCompleted: 'Present has already been sent',
    budgetPage: {
      breadcrumbTitle: 'Gift cards & presents',
      breadcrumbTitleCoupons: 'Gift cards',
      breadcrumbTitleGifts: 'Presents',
      subTitle: 'Orders',
    },
    employeeTabs: {
      fitness: 'Corporate Fitness',
      creditCards: 'Credit Cards',
      wellpass: 'Wellpass',
      couponOrders: 'Gift card purchases',
      gifts: 'Presents',
      incentives: 'Benefits in Kind 37b',
      budgetOrders: 'Budget top-ups',
      allTransactions: 'All transactions',
    },
    orderHistoryList: {
      search: 'Search',
      includeCouponOrders: 'Show gift card purchases',
      includeGiftCouponOrders: 'Show presents',
      export: 'Export orders as CSV file',
    },
    ordersTable: {
      header: {
        date: 'Date',
        employee: 'Employees',
        amount: 'Total',
        details: 'Details',
      },
      row: {
        couponOrder: 'Gift card order',
        giftOrder: 'Present: $t(couponsModule:giftOccasions.{{occasion}})',
        incentiveOrder: 'Benefit in Kind 37b: $t(couponsModule:giftOccasions.{{occasion}})',
        budgetOrder: {
          COMPLETED: 'Budget top-up',
        },
        budgetCorrection: 'Budget correction',
      },
      noResultsFitness: 'No Corporate Fitness orders found.',
      noResultsCreditCard: 'No Credit Card orders found.',
      noResultsWellpass: 'No Corporate Fitness orders found.',
      noResults: 'No budget top-ups or gift card orders found.',
      pendingBudgetOrders_one: 'Budget top-up in progress',
      pendingBudgetOrders_other: 'Budget top-ups in progress',
      pendingBudgetOrderItem: '<strong>{{amount, euro}}</strong> (ordered on {{orderDate,date}} by {{firstName}} {{lastName}})',
      futureBudgetOrderItem: '<strong>{{amount, euro}}</strong> (will be ordered as of {{orderDate,date}} by {{firstName}} {{lastName}})',
      pendingBudgetOrderItemAdmin: '<strong>{{amount, euro}}</strong> (ordered on {{orderDate,date}})',
      futureBudgetOrderItemAdmin: '<strong>{{amount, euro}}</strong> (will be ordered as of {{orderDate,date}})',
    },
    budgetPanel: {
      tabs: {
        expenses: 'Expenses',
        balance: 'Balance',
      },
      expectedExpenses: 'Current month',
      budget: 'Total balance:',
      orderButton: 'Top up',
      previousExpenses: 'Last month',
      upcomingExpenses: 'Next month',
      automaticTopUpActive: 'Automatic top-up enabled',
    },
    budgetPanelIncreaseBudgetDialog: {
      title: 'Budget top-up',
      billingAddressPanel: {
        title: 'Billing address',
        alert: 'Please enter a valid billing address.',
        saveButton: 'Save',
        editButton: 'Edit',
      },
      tabs: {
        manual: {
          title: 'Budget top-up',
          amount: 'Top-up amount',
          amountHelperText: 'Minimum top-up amount: {{money, euro-float}}',
          currentBudget: 'Current Budget',
          newBudget: 'New Budget',
          expectedExpenses: 'Expected costs',
          integerRequired: 'The amount must be an integer',
          actions: {
            cancel: 'Cancel',
            confirm: 'Pay and order',
          },
        },
        automatic: {
          title: 'Automatic top-up',
          enableAutomaticRefill: 'Automatic top-up',
          infoText:
            'Should you deactivate the automatic top-up, you will have to register credit top-ups manually in the employer portal and only then will you receive an invoice for the amount you have freely chosen.',
          warnText:
            'Even with automatic charging activated, the bank transfer must still be made manually. To change payment method, open the <0>Company Settings page</0>.',
          actions: {
            cancel: 'Cancel',
            confirm: 'Confirm',
          },
          enabledSuccessMessage: 'Automatic top-up successfully activated',
          disabledSuccessMessage: 'Automatic top-up successfully deactivated',
        },
      },
      successDialog: {
        title: 'Thank you',
        orderCompleted: 'Order successfully received!',
        orderCompletedInfo: 'You will receive the receipt with payment details at {{tenantMail}} shortly',
        orderCompletedInfoSepa:
          '\nThe amount {{money, euro-float}} will be debited from the following account {{tenantIban}} within the next few days via SEPA. \n\nPlease keep in mind that the budget will be charged in the employer portal only after the payment has been received on our business account.',
        transferData: {
          title: 'Payment details:',
          recipient: 'Recipient:',
          iban: 'IBAN:',
          bic: 'BIC:',
          usage: 'Reason:',
          amount: 'Total:',
          probonioData: {
            recipient: 'Probonio GmbH',
            iban: 'DE04 7435 0000 0021 1788 36',
            bic: 'BYLADEM1LAH',
          },
        },
        actions: {
          cancel: 'Cancel',
          confirm: 'Close Message',
        },
      },
    },
    benefitSettings: {
      title: 'Gift card purchase settings',
      info: 'Here you have the possibility to make various settings to manage the voucher process for your employees. On the current page you can define when and how employees receive vouchers, set the default voucher and configure automatic selection.',
      orderDeadline: {
        title: 'Day of month until selection of gift cards is possible for employees',
        info: 'Select the latest day by which your employees must select a voucher in the Probonio-app. If no selection is made by the selected day, the employee will automatically receive the specified default voucher. This also influences the dispatch date of the wage data export.',
      },
      fallbackCoupon: {
        title: 'Automatically selected gift card',
        info: 'Your employees can independently select in the Probonio-app which voucher they want to receive automatically each month. The voucher selected here will be issued if your employee does not make their own selection.',
      },
      automationBehaviour: {
        title: 'Behavior of the automatic gift card inflow',
        info: 'Set a different behavior from the default for the automatic gift card inflow.',
      },
      fields: {
        couponFallbackDefinitionId: {
          hint: 'Select a gift card here that will be automatically selected for your employees on the day above if they have not made a selection in the Probonio-app by then.',
          disabledTooltip: 'Only possible if automatic gift card purchase is not disabled ($t(benefitsModule:settingsDialog.moreSettings))',
          emptyPlaceholder: 'None selected',
        },

        couponOrderAutomate: {
          DEFAULT: {
            label: 'Ensure gift card purchase for active users',
            hint: 'If an active employee has not yet selected a gift card until {{couponOrderDeadline}}, they will automatically receive the gift card they selected for automatic selection. If no automatic selection has been made,',
            hintWithFallback: ' he receives a {{couponFallback}} (See above) gift card.',
            hintWithoutFallback: ' he will not receive a gift card for this month!',
          },
          FOR_ALL_USERS: {
            label: 'Ensure gift card purchase',
            hint: 'A gift card will automatically be selected at the {{couponOrderDeadline}} for all employees who have not yet selected a gift card, even if they have never registered. Users who have been set to inactive by you or have not yet been released are excluded from this.',
            disabledTooltip: 'If you want to force the gift card inflow, select an automatically selected gift card.',
          },
          DISABLED: {
            label: 'Disable automatic gift card purchase',
            hint: 'Your employees can no longer make an automatic selection. If employees have not selected a gift card by the {{couponOrderDeadline}}, they will not receive a gift card for that month. A subsequent choice or even the inflow of a gift card for a past month is not possible for tax purposes!',
            disabledTooltip:
              'If you want to prevent the automatic gift card purchase, select “$t(couponsModule:benefitSettings.fields.couponFallbackDefinitionId.emptyPlaceholder)” as the “$t(couponsModule:benefitSettings.fallbackCoupon.title)”.',
          },
        },
      },
      couponOrderDeadline: {
        first: 'Beginning of month',
        perDay: '{{value}}th day of month',
        last: 'End of month',
      },
      couponOrderDeadlineHint: {
        first:
          'At the beginning of each month, the employee will receive the previously selected default gift card. Report will be emailed to you on the following day.',
        perDay:
          'At the chosen day, the employee will receive a default gift card if none has been selected to this date. Report will be emailed to you on the following day ({{reportDay}}th day of month).',
        last: 'At the end of each month, the employee will receive a default gift card if none has been selected to this date. Report will be emailed to you at the beginning of the following month.',
      },
      successToast: 'Settings have been saved',
    },
    giftOccasions: {
      BIRTHDAY: 'Birthday',
      SAINTS_DAY: "Saint's day",
      CHILD_BIRTH: 'Childbirth',
      CHILD_BAPTISM: 'Child baptism',
      CHILD_COMMUNION: 'Holy communion',
      CHILD_CONFIRMATION_CATH: 'Holy confirmation',
      CHILD_CONFIRMATION_PROT: 'Protestant confirmation',
      WEDDING: 'Wedding',
      WEDDING_SILVER: 'Silver wedding',
      WEDDING_GOLD: 'Golden wedding',
      PASSED_EXAM: 'Exam passed',
      COMPANY_ANNIVERSARY: 'Round company anniversary',
      ACHIEVEMENT: 'Goal Achievement',
      WORK_PERFORMANCE: 'Work Performance',
      COMMITMENT: 'Special Commitment',
      PROPOSAL: 'Improvement Proposal',
      RECRUITMENT: 'Employee Recruitment',
      ATTENDANCE: 'Attendance Bonus',
      SIGNING_BONUS: 'Signing Bonus',
      PROFIT_SHARING: 'Profit Sharing',
      OTHER: 'Other',
      description: {
        BIRTHDAY: 'Your Probonio team wishes you the best for your birthday!',
        SAINTS_DAY: "Your Probonio team wishes you the best for your saint's day!",
        CHILD_BIRTH: 'Your Probonio team wishes you the best for the birth of your child!',
        CHILD_BAPTISM: "Your Probonio team wishes you the best for your child's baptism!",
        CHILD_COMMUNION: "Your Probonio team wishes you the best for your child's communion!",
        CHILD_CONFIRMATION_CATH: "Your Probonio team wishes you the best for your child's holy confirmation!",
        CHILD_CONFIRMATION_PROT: "Your Probonio team wishes you the best for your child's protestant confirmation!",
        WEDDING: 'Your Probonio team wishes you a beautiful, long lasting marriage with lots of happiness!',
        WEDDING_SILVER: 'Your Probonio team congratulates your silver wedding and wishes you continuous joy in your life!',
        WEDDING_GOLD: 'Your Probonio team congratulates your golden wedding and wishes you continuous joy in your life!',
        PASSED_EXAM: 'Your probonio team wishes you the best for your passed exam!',
        COMPANY_ANNIVERSARY: 'Your Probonio team wishes you the best for your round company anniversary!',
        ACHIEVEMENT:
          'Congratulations, {{firstName}} {{lastName}}, on achieving your goals. Your Probonio team celebrates your milestones with you!',
        WORK_PERFORMANCE: 'A big praise and recognition to you, {{firstName}} {{lastName}}, for your impressive work performance!',
        COMMITMENT: 'The Probonio team congratulates you, {{firstName}} {{lastName}}, on your exceptional commitment!',

        PROPOSAL: 'Thank you, {{firstName}} {{lastName}}, for your valuable improvement proposal.',

        RECRUITMENT: 'We would like to sincerely thank you, {{firstName}} {{lastName}}, for your support in employee recruitment!',

        ATTENDANCE: 'Thank you for your consistent attendance, {{firstName}} {{lastName}}!',

        SIGNING_BONUS:
          'Congratulations, {{firstName}} {{lastName}}, on receiving the signing bonus. Wishing you success in your new position from your Probonio team!',

        PROFIT_SHARING: 'Congratulations, {{firstName}} {{lastName}}, on the profit sharing!',
        OTHER: 'Your Probonio team wishes you the best!',
      },

      info: {
        COMPANY_ANNIVERSARY: 'A round company anniversary is any year ending with a 5 or 0',
        OTHER: "There has to be a good reason. Bank holidays like christmas don't count here!",
      },
    },

    giftsPage: {
      subTitle: 'Presents',
      goToEmployee: 'Go to employee',
      tab: {
        pending: 'Planned',
        delivered: 'Delivered',
      },
    },

    incentivesPage: {
      subTitle: 'Benefit in Kind 37b',
      tab: {
        pending: 'Planned',
        delivered: 'Delivered',
      },
    },
  },
  couponsV2Module: {
    reportDialogMessage:
      'This report contains all information about when and how much non-cash benefit remuneration was granted to the employee. You can download the report if required or save it as an attachment to your payroll account.',
    successUpdate: 'Benefit successfully changed.',
    successDeleteGift: 'Benefit successfully deleted',
    successCreate: 'Benefit successfully created.',
    giftAlreadyCompleted: 'Present has already been sent',
    budgetPage: {
      breadcrumbTitle: 'Gift cards flex',
      subTitle: 'Transactions',
      alertMessage: 'Invoices and vouchers will only be created or issued once SEPA is fully activated.',
    },
    employeeTabs: {
      couponOrders: 'All transactions',
    },
    orderHistoryList: {
      search: 'Search',
      export: 'Export orders as CSV file',
    },
    ordersTable: {
      header: {
        date: 'Date',
        deliveryDate: 'Delivery date',
        employee: 'Employees',
        amount: 'Total',
        status: 'Status',
        details: 'Details',
      },
      row: {
        couponOrder: 'Non-cash benefit',
        giftOrder: {
          GIFT: 'Gift: $t(couponsModule:giftOccasions.{{occasion}})',
          INCENTIVE: 'Incentive 37b: $t(couponsModule:giftOccasions.{{occasion}})',
        },
        // c.f. CouponsV2TokenOrderStatus
        status: {
          INITIAL: 'Creating invoice',
          INVOICE_CREATED: 'Payment pending',
          PAID: 'paid',
          AVAILABLE: 'to be claimed',
          DELIVERED: 'delivered',
          PAYMENT_FAILED: 'cancelled',
          PENDING_REFUND: 'to be refunded',
          CANCELLED: 'cancelled',
        },
        deliveryDate: {
          notDelivered: 'Pending',
        },
        employee: {
          deleted: 'Deleted employee',
        },
      },
      orderTypeFilter: {
        label: 'Transaction type',
        all: 'All',
        BENEFIT_ACTIVATION: 'Non-cash benefit',
        GIFT: 'Gift',
        INCENTIVE: 'Incentive 37b',
      },
      orderStatusFilter: {
        label: 'Status',
      },
      noResults: 'No gift card orders found.',
    },
    gifts: {
      title: 'Presents',
      payouts: 'Payouts',
      active: 'Next payment on {{val, date}}',
      deleteConfirmation: 'Are you sure you want to cancel the scheduled payout?',
      dialogCreateTitle: {
        GIFT: 'Add new gift',
        INCENTIVE: 'Add new Incentive 37b',
      },
      dialogEditTitle: {
        GIFT: 'Edit gift',
        INCENTIVE: 'Edit Incentive 37b',
      },
      fields: {
        payoutDate: 'Payout date',
        giftType: 'Type',
        occasion: 'Occasion',
        recurringText: 'This present should repeat yearly',
        recurring: 'Recurring',
        amount: 'Amount',
        status: 'Status',
        inviteWhenActive: 'Invite employees as soon as the grant becomes due.',
        sendMailWhenActive: 'Inform employees also by mail',
        employee: 'Employee',
      },
      addIncentive: 'Add Incentive 37b',
      addGift: 'Add gift',
      giftType: {
        GIFT: 'Gift',
        INCENTIVE: 'Incentive 37b',
      },
      maxIncentiveAmountWarning:
        'A maximum of €10,000 per year and employee can be distributed. This payout would exceed the maximum amount per year.',
      placeHolderInput: {
        inputLabel: 'Message to your employee',
        firstNameChipLabel: 'First name',
        lastNameChipLabel: 'Last name',
      },
      // c.f. CouponsV2GiftStatus
      status: {
        PENDING: 'planned',
        INVOICE_CREATED: 'billed',
        DELIVERED: 'completed',
        AVAILABLE: 'completed',
      },
    },
    giftsPage: {
      breadcrumbTitle: 'Gift cards flex',
      subTitle: 'Presents',
      goToEmployee: 'Go to employee',
      tab: {
        pending: 'Planned',
        delivered: 'Delivered',
      },
    },
  },
  placeholderInput: {
    tooltip: 'This placeholder is automatically filled with the {{label}} of the employee',
  },
  customBenefitsModule: {
    title: 'More benefits',
    subTitle: 'Configuration',
    configureTab: 'Configuration',
    configureButton: 'Configure benefit',
    configureButtonHint: 'This is a self-managed benefit.',
    createDialog: {
      title: 'Activate {{customBenefit}} in Employer Portal?',
      description:
        'Do you want to activate this benefit in Employer Portal? After activation you may configure this benefit individually for your company. The benefit will only be displayed to your employees after unlocking it individually.',
      support: 'For question about benefit {{customBenefit}} or to find an appropriate partner, do not hesitate to contact us personally.',
      confirm: 'Configure now',
      cancel: 'Cancel',
    },

    benefits: {
      JOB_BIKE: {
        title: 'Company bike',
        description:
          'Do you want to keep employees healthy and simultaneously do something for the environment? With JobRad you can lease any desired bike for your employees - completely as salary conversion or with a grant. Whether for work, sports or vacation: JobRad is a continuous companion that shows appreciation daily.',
      },
      URBAN_SPORTS_CLUB: {
        title: 'Urban Sports Club',
        description:
          'Supporting the health of your employees with flexible choices of sports? Urban Sports Club makes it possible! Provide your employees the free choice of thousands of sport offers. With this you won’t just enhance productivity, but also strengthen employee engagement and amenity for new applicants',
        infoTextFull: `You can support your employees with a contribution to the company fitness provider Urban Sports Club. Via the non-cash benefit, employers have the opportunity to finance their employees' contribution to Urban Sports Club tax-free.
          <br><br>
          Your employees have the option to activate, manage and also use the Urban Sports Club membership directly via the app. In addition, the module for non-cash vouchers can also be activated in parallel with Urban Sports Club, giving employees the flexibility to choose between the vouchers and Urban Sports Club.
          <br><br>
          <strong>In order to use the tax benefits of Urban Sports Club as a benefit, the following rules apply:</strong>
          <ul>
          <li>The exemption limit of €50 per month per employee may not be exceeded. Other non-cash benefits such as tax-free vouchers or health budgets may also have to be taken into account here.</li>
          <li>The gym allowance may not take the form of a salary conversion and is granted in addition to the salary owed anyway.</li>
          <li>No payment of the gym subsidy may be made in the form of cash wages.</li>
          <li>The membership contract must be cancellable on a monthly basis. </li>
          <li>The membership contract must be between the employer and the company fitness provider.</li>
          </ul>`,
        infoTextVideos: {
          managerSpecific: 'Employer Portal Urban Sports Club',
          appSpecific: 'App Urban Sports Club',
        },
      },
      INTERNET_FLAT_RATE: {
        title: 'Internet subsidy',
        description:
          'With the internet subsidy you can disburse a tax-free amount of up to €50 directly via salary or also refund one-time costs - tax-deductible with a flat-rate tax of 25%. In doing so setup costs and ongoing fees for a flat-rate as well as technological devices are completely refundable.',
      },
      EMPLOYEE_OFFERS: {
        title: 'Employee offers',
        description:
          'An exciting employee program: with Corporate Benefits you can give your employees access to attractive employee offers. Your employees can redeem the diverse discount codes at brands like Apple, Adidas or Jochen Schweizer directly in the store or online.',
      },
      EMPLOYEE_ACTIONS: {
        title: 'Mitarbeiteraktionen.de',
        description:
          'Do you want to do something good for your employees and not take the fun out of it? With employee actions you can notify your employees about discounted events, experiences, restaurants or online shopping possibilities in a much more organised fashion whilst strengthening the employer branding.',
      },
      HEALTH_BUDGET: {
        title: 'Health budget',
        description:
          "For many it's not easy to combine family and job. Support your employees with daycare tax-free and free of charge and present yourself as a family-friendly company.",
        infoTextFull: `You can purchase company health insurance for your employees, which provides your employees with an annual health budget. The health budget provides additional protection in the event of illness or injury. The contributions to the company health insurance can be settled tax-free via the €50 benefit 37b.
        <br><br>
        Contributions to company health insurance can be deducted tax-free via the €50 non-cash benefit allowance.
        <br><br>
        <strong>To take advantage of the tax benefits of the health budget as a benefit, the following rules apply:</strong>
          <ul>
          <li>The exemption limit of €50 per month per employee may not be exceeded. Other non-cash benefits such as tax-free vouchers or gym memberships may also have to be taken into account here.</li>
          <li>The health budget may not take the form of a salary conversion and is granted in addition to the salary owed anyway.</li>
          <li>The health budget may not be paid out in the form of cash wages.</li>
          </ul>`,
      },
      CAPITAL_FORMING_BENEFITS: {
        title: 'Capital forming benefits',
        description:
          'Saving money is an important topic for almost all people. Support your employees with saving plans such as construction financing or ETF saving plans through capital-forming benefits and tie them to your company.',
      },
      RECOVERY_SUPPORT: {
        title: 'Recovery support',
        description:
          'The relaxation and wellbeing of employees take a high priority nowadays - also for their performance. Relieve your employees with an annual grant for relaxing activities, such as holiday trips or visits at spa hotels.',
      },
      CUSTOM_BENEFIT: {
        title: 'Custom benefit',
        description: 'Do you have more benefits in your company? Via this button you can flexibly integrate those into Probonio.',
      },
    },

    customBenefitCreatorPage: {
      deleteCustomBenefit: 'Delete benefit',
      successDelete: 'Benefit successfully deleted',
    },
    customBenefitDeletionModal: {
      title: 'Delete benefit',
      text: 'Do you really want to permanently delete the benefit {{title}}? Deletion is only possible if the benefit is not currently activated.',
      conflict: 'The benefit {{title}} cannot be deleted as it is still activated for {{employees}} employee(s).',
    },
    customBenefitCreator: {
      successUpdate: 'Benefit successfully changed.',
      successCreate: 'Benefit successfully created.',
      subTitle: 'Other benefit',
      menu: {
        switch_active: 'Benefit active',
      },
      content: {
        backButton: 'Back',
        nextButton: 'Next',
        confirmButton: 'Save benefit',
        summaryStep: {
          title: 'Tile in the app',
          description: 'In the first step you configure the name, the icon and the cover image of the new benefit',
          confirmButton: 'Next',
          nonCashRelevant: 'Relevant for Benefit in Kind',
          nonCashRelevantHelperText:
            "Is this benefit relevant for Benefit in Kind (e.g. gym, insurances)? If it's active you will be warned if the overall cap of €50 for the user is exceeded.",
          switch: 'Show as tile in the employee app',
          switchHelperText:
            'Should colleagues be able to view this benefit in the employee app? Employees see the tiles only after the benefit has been unlocked for them individually',
          inputTitle: 'Title',
          inputHelperText: 'Decide for a title for your benefit, the title will appear for employees in your app',
          selectPictureText: 'Choose cover image',
          selectPictureHelperText: 'Upload a suitable cover image for the tiles',
          muiIconsHelperText: 'Choose a suitable icon for the benefit',
        },
        textStep: {
          title: 'Benefit description',
          description: 'Now write a text for the benefit. The text will be displayed for employees in the app',
        },
        visualisationStep: {
          benefitAmountThisMonth: 'Receive this month',
          benefitAmountOverall: 'Receive overall',
          availableAmount: 'of possible {{amount, euro-float}}',
          amount: '{{amount, euro-float}}',
          title: 'More settings',
          description: 'In the last step you configure the visualisation and compensation of the benefit',
          switch: 'Show in monetary visualisation',
          switchHelperText:
            'Should the benefit be visualized for employees in the app? Only recommended for monetary benefits. Employees see the grant of benefits only in the visualisation after the benefit has been unlocked for them individually.',
          inputTitle: 'Usual grant',
          inputHelperText:
            'Set the usual monthly grant for this benefit. This value will be used as the default value if you unlock the benefit for an employee',
        },
      },
    },
    topPanel: {
      description: 'If you offer other benefits in your company, you may display these in Probonio and inform your employees about them.',
    },
    changeMode: {
      lightMode: 'Light Mode',
      darkMode: 'Dark Mode',
    },
  },

  tenantModule: {
    title: 'Company settings',
    tab: {
      paymentSettings: 'Payment settings',
      hrSystem: 'Personio integration',
      kombo: 'HR system integration',
      benefitTemplates: 'Templates',
      customisationSettings: 'Corporate Design',
      benefitSorting: 'Benefit Sorting',
      invoices: 'Invoices',
      fiscalYear: 'Fiscal Year & Deadlines',
    },
    imageError: 'Please use an Image File.',
    paymentSettings: {
      title: 'Payment settings',
      info: 'Manage your settings for the invoice information here. The settings can be adjusted independently by you.',
      address: {
        title: 'Company data',
        info: 'Please enter your company address and email address here. You can change this at any time. All invoices will be sent to this email address. You can enter a different company name in the corresponding field, which should appear on the generated invoice.',
      },
      successToast: 'Changes have been saved',
      errorToast: 'Changes could not be saved',
      notMoreThanFive: 'Please do not enter more than five e-mail addresses!',
      rlvLocked:
        'Company name cannot be changed while an active Framework Contract (RLV) exists. Please reach out to <0>rlv@bikeleasing.de</0>.',
      fields: {
        tenantName: 'Company name',
        tenantInvoiceName: 'Different company name on invoices',
        tenantStreet: 'Street and house number',
        tenantStreetLine2: 'Address line 2 (optional)',
        tenantRegion: 'State/Province',
        tenantAddressCountry: 'Country',
        tenantPostalCode: 'Postal Code',
        tenantCountryCode: 'Company Country',
        tenantCity: 'City',
        tenantLegalForm: 'Legal Form',
        tenantImage: 'Company logo (horizontal)',
        tenantMail: 'Contact e-mail',
        invoiceEmailList: 'Invoice e-mail addresses',
        isAddressEnabled: 'Address',
      },
    },
    paymentMethods: {
      title: 'Payment methods',
      info: 'Our payment options are simple and secure. Payment is possible via bank transfer prepayment and SEPA corporate direct debit.',
      active: 'Active',
      inProgress: 'In progress',
      activate: 'Select',
      bankTransfer: 'Bank transfer',
      bankTransferInfo:
        'Pay your invoices via bank transfer in advance. Choose yourself between a one-time transfer or a real-time transfer.',
      sepa: 'SEPA corporate direct debit',
      sepaInfo:
        'Pay your bills conveniently and easily via SEPA direct debit. The advantage: You always pay on time because the money is collected automatically. Set up your company direct debit mandate now in just five steps.',
      methodName: 'Payment method: $t(tenantModule:paymentMethods.{{paymentMethod}})',
      sepaInProgress: '(SEPA corporate direct debit in progress)',
      changeHint: 'To change payment method, open the <0>Company Settings page</0>.',
      fileConflict: {
        uploadMandate: 'Please sign the document before upload',
        uploadConfirmation: 'Please upload the bank confirmation here',
      },
      maxFileSize: 'Max. file size: 5 MB',
      confirmChange: {
        title: 'Change payment method',
        text: 'You currently pay conveniently via SEPA corporate direct debit. Are you sure you want to switch back to the manual transfer process?',
      },
      sepaStepper: {
        invalidIban: 'Please enter a valid IBAN of countries {{countries}}',
        step: {
          bankInfo: {
            title: 'Banking details',
            header: 'Banking details',
            info: 'Enter your IBAN here and download the mandate template. In the template, all relevant information such as IBAN, BIC, your credit institution as well as your mandate reference number will be automatically entered for you.',
            downloadButton: 'Download template',
            pleaseDownload: 'Please download the mandate template to continue',
            field: {
              iban: 'IBAN',
              ibanHelper: 'Valid countries: {{countries}}',
              bic: 'BIC',
              bankName: 'Bank name',
            },
          },
          uploadMandate: {
            title: 'Direct debit mandate',
            header: 'Upload direct debit mandate',
            info: 'Please fill out the document you have just downloaded, sign it and then upload it here. In addition, send the completed document to your bank and ask for written confirmation of the deposit, which you will submit to us in the next step. Please note that with the SEPA corporate variant, the direct debit will be checked by your bank before it is cashed. It is therefore necessary to submit a copy of the direct debit mandate to your bank. Confirmation of the deposit must be submitted in the next step. For example, we accept both a completed template from your bank and a bank stamp on your completed direct debit mandate.',
            downloadLink: 'Uploaded direct debit mandate',
            clearTitle: 'Delete direct debit mandate',
            clearText:
              'Are you sure you want to reset the uploaded direct debit mandate? You will then need to upload this document and the bank confirmation again.',
          },
          submitMandate: {
            title: 'Submit mandate',
            info: 'Please send the completed and signed SEPA direct debit mandate to your bank and ask for written confirmation of the deposit. You can upload this confirmation to us in the next step.',
            checkBox: 'I hereby confirm that I have submitted the mandate to my bank.',
          },

          uploadConfirmation: {
            title: 'Confirmation SEPA',
            header: 'Upload bank confirmation',
            info: 'As soon as your bank has confirmed the deposit of the SEPA corporate direct debit mandate in writing, you can upload the confirmation here. Without the confirmation of your bank, we can unfortunately not use the mandate for collection.',
            downloadLink: 'Uploaded bank confirmation',
            clearTitle: 'Delete bank confirmation',
            clearText: 'Are you sure you want to reset the uploaded bank confirmation? You will then need to upload this document again.',
            infoAccountingReference: 'Upload the signed mandate with the reference <strong>{{accountingCustomerNumber}}</strong>',
            customerConfirmation: {
              title: 'Confirmation without Bank Verification',
              subtitle: 'Please confirm the following points to proceed without bank verification',
              label: 'My bank cannot issue a confirmation',
              confirmedByUser: 'The SEPA direct debit mandate was self-confirmed by <0>{{firstName}} {{lastName}}</0>',
              confirmedByFormerUser:
                'The SEPA direct debit mandate was self-confirmed, but the confirming person is no longer employed at the company',
              chargebackFees: 'I acknowledge that any chargeback fees resulting from failed debits will be borne by me.',
              sepaDeactivation:
                'In the event of repeated chargebacks, Probonio reserves the right to permanently disable the SEPA direct debit payment method. In this case, payment will only be possible via bank transfer.',
              fraudProtection:
                'I acknowledge that for fraud protection reasons, the first credit booking after a SEPA debit will be delayed: The credit will be booked no earlier than five banking days after the debit. For all subsequent debits, the credit will be booked within approximately 24 hours.',
              mandatoryMandateSubmission:
                '<strong>Mandatory mandate submission:</strong> In order for the SEPA direct debit to be technically successful, the SEPA mandate must be actively submitted by you to your bank and stored there (regardless of whether you upload a bank confirmation). If this submission is not made, the bank will reject the direct debit, resulting in a return debit note.',
            },
          },

          complete: {
            title: 'Complete',
            headerInProgress: 'We currently review your documents',
            headerComplete: 'SEPA corporate direct debit active',
            infoInProgress:
              'The change from bank transfer to SEPA corporate direct debit is completed as soon as we have checked your mandate documents.',
            infoComplete:
              'Your SEPA corporate direct debit is now active. From now on, you always pay your invoices automatically and on time. The collection takes 1-3 bank working days.',
            accountingReference: 'Mandate Reference: ',
          },
        },
      },
    },
    invoices: {
      downloadFile: 'Probonio_Invoice_{{date}}.pdf',
      invoice: {
        date: 'Date',
        type: {
          title: 'Invoice type',
          COUPON_BUDGET: 'Coupon-budget top-up',
          SERVICE_FEE: 'Service fee',
          AUTOMATED_ORDER: 'Automatic coupon-budget top-up',
          COUPONS_V2_TOKENS: 'Probonio-voucher budget top-up',
          URBAN_SPORTS_CLUB: 'Probonio X Urban Sports Club',
          CREDIT_CARD_ORDER_FEE: 'Credit card order fee',
        },
        accountingInvoiceNumber: 'Invoice number',
        amount: 'Total',
        status: {
          title: 'Status',
          DRAFT: 'Processing',
          OPEN: 'Open',
          PAID: 'Paid',
          VOIDED: 'Voided',
        },
        actions: {
          pending: 'Invoice processing',
          download: 'Download invoice',
        },
      },
    },
    hrSystem: {
      title: 'Personio integration',
      info: "This integration allows you to transfer your employees' master data directly from Personio to Probonio",
      guide: {
        title: 'Instructions',
        info: '<p>Simply connect Probonio with Personio and avoid having to manually enter employee data multiple times. The integration automatically transfers employees created in Personio to Probonio. Synchronization takes place every night, but you can also start it manually at any time.</p><p><i>Deactivated employees in Personio are also deactivated in Probonio.</i></p>',
        section1Title: 'How to connect your Personio application with Probonio',
        section1Text: `<p>Follow these simple steps to connect Personio to Probonio:</p>
          <ol>
            <li>
              <strong>Search for Probonio on the Personio Marketplace:</strong>
            </li>
            <ul>
              <li>
                Log in to your Personio account.
              </li>
              <li>
                Go to <strong>Marketplace</strong> in the main menu.
                <br />
                <strong>Notice</strong>: If you do not see this tab you do not have the required rights to connect the integration.
              </li>
              <li>
                Enter <strong>Probonio</strong> in the search field.
              </li>
            </ul>
            <li>
              <strong>Connect Probonio with Personio:</strong>
            </li>
            <ul>
              <li>
                Click on the <strong>Connect</strong> button next to <strong>Probonio</strong>.
              </li>
              <li>
                This will start the integration setup process.
              </li>
            </ul>
            <li>
              <strong>Create API credentials:</strong>
            </li>
            <ul>
              <li>
                After connecting, you will be asked to generate API credentials.
              </li>
              <li>
                Click on <strong>Generate API credentials</strong> to get your <strong>Client ID</strong> and <strong>Client Secret</strong>.
              </li>
              <li>
                Copy both values as they are required for the connection.
              </li>
            </ul>
            <li>
              <strong>Enter login data in Probonio:</strong>
            </li>
            <ul>
              <li>
                Go to the Probonio platform and navigate to the Integration Settings.
              </li>
              <li>
                Enter the <strong>Client ID</strong> and <strong>Client Secret</strong> in the appropriate fields.
              </li>
              <li>
                Save the changes by pressing the <strong>Connect</strong> button to complete the connection.
              </li>
            </ul>
          </ol>
          <p>
          Once these steps are completed, Probonio is successfully connected to Personio and employees are copied to Probonio automatically.
          <br />
          If you only want to synchronize certain employees from Personio to Probonio, you can always contact our support team for more detailed information.
          </p>`,
        advancedTutorial: `The following tutorial will guide you through the integration process. In case you would like a more detailed instruction, click here for <0>instructions with pictures</0>.`,
      },
      status: {
        connected: 'Integration active',
        lastSync: '<strong>Last synchronization:</strong> {{date,datetime}}',
        syncError: 'Synchronization failed: {{message}}',
      },
      form: {
        apiKey: 'Client ID',
        apiSecret: 'Client Secret',
        connect: 'Connect',
        syncNow: 'Sync now',
        disconnect: 'Disconnect',
        connectionError: 'Personio API authentication failed',
      },
      instantInvite: {
        label: 'Invite new employees through this interface immediately',
        successToast: 'Change successfully saved',
      },
    },
    kombo: {
      title: 'Integration HR System',
      info: 'This integration allows you to transfer your employees’ master data directly from the selected HR system to Probonio.',
      guide: {
        title: 'Guide',
        info: '<p>Easily connect Probonio with your HR system and avoid manually entering employee data multiple times. With this integration, employees created in the HR system are automatically transferred to Probonio. Synchronization happens automatically in the background, but you can also start it manually at any time.</p><p><i>Disabled employees in the HR system will also be disabled in Probonio.</i></p>',
        advancedTutorial: `The following tutorial walks you through the integration process. If you need a more detailed guide, click here for the <0>Guide with images</0>.`,
      },
      status: {
        connected: 'Integration active since {{createdAt,date}}',
        lastSync: '<strong>Last synchronization:</strong> {{date,datetime}}',
        syncError: 'Synchronization error: $t(tenantModule:kombo.syncError.{{errorKey}})',
        setupIncomplete: 'Configuration incomplete',
        inactive: 'Integration deactivated, please contact support',
        initial: 'Waiting for initial synchronization',
        syncPlanned: 'Synchronization in progress...',
      },
      syncError: {
        AUTHENTICATION_FAILED: 'Authentication failed, reconnection required',
        FAILED: 'Unknown error, please contact support',
        PARTIALLY_FAILED: 'Import incomplete, please try again',
      },
      form: {
        connect: 'Start setup',
        reconnect: 'Reconnect',
        syncNow: 'Schedule sync',
        setup: 'Change configuration',
        disconnect: 'Disconnect',
        confirmDisconnect:
          'Are you sure you want to disconnect from your HR system? This will stop employee synchronization, but already transferred data will not be deleted.',
      },
      toast: {
        connectionCreated: 'Connection established',
        syncQueued: 'Synchronization scheduled',
        syncAlreadyQueued: 'Synchronization already scheduled',
        integrationDisconnected: 'Connection disconnected',
      },
      syncModal: {
        title: 'Schedule sync',
        info: '<p>Would you like to schedule a complete data synchronization of your employee data from {{hrSystemName}} to Probonio? </p><p>This is only necessary if the interface configuration has changed. Regular data synchronization takes place automatically several times a day.</p><p>Employees who are no longer transferred will be deactivated in Probonio.</p><p><b>Synchronization takes place in the background and can take several minutes</b>.</p>',
      },
    },
    benefitTemplates: {
      title: 'Benefit Templates',
      info: 'Create templates for benefit configurations here, which you can then assign to your employees.',
      createTemplate: 'Create new Benefit Template',
      editTemplate: 'Edit Benefit Template',
      confirmDelete: 'Do you really want to remove the selected template? This has no effect on already configured employees.',
      benefitsTitle: 'Benefits in this template',
      benefitsInfo:
        'The benefits configured here are activated for the selected employee as soon as the template is applied. Subsequent changes have no influence on already configured employees.',
      successCreate: 'Template created',
      successUpdate: 'Template updated',
      successDelete: 'Template deleted',
      noBenefitsWarning: 'There are no benefits configured in this template yet. Please add at least one benefit.',
      benefitUnavailable: 'Benefit no longer available',
      defaultTemplate: 'Default',
      editItemDialog: {
        title: 'Configure benefit',
        info: 'Enter the parameters you want to include in the template here. These will be applied once the template is used for one or more employees. Subsequent changes will not affect employees who have already been configured.',
        warning: 'Subsequent changes have no influence on already configured employees',
      },

      field: {
        name: 'Name',
        isDefault: 'Automatically apply this template to new employees (also applies for automated interfaces *)',
        isDefaultHint: '* Only if no template is set via the HR interface',
      },
      action: {
        newTemplate: 'Create new template...',
        delete: 'Delete template',
        addBenefit: 'Add benefit',
        editItem: 'Configure benefit',
        deleteItem: 'Delete benefit',
      },
    },
    customisationSettings: {
      title: 'Corporate Design',
      info: 'Change the design of your app here. The settings can be adjusted independently by you.',
      logo: {
        title: 'Logo',
        info: 'You have the option to brand all applications with your company logo. The logo will be displayed in both your employer portal and the employee app after uploading.',
      },
      generatePreview: 'Generate preview',
      getDefault: 'Reset to default values',
      uploadLogo: 'Light company logo',
      uploadLogoDark: 'Dark company logo',
      changeImageTooltip: 'Change light company logo',
      changeImageTooltipDark: 'Change dark company logo',
      successToast: 'Company data updated successfully',
      guide: {
        backgroundColor: {
          title: 'Background color',
          info: 'For the app background, we recommend using light colors for the light design and darker ones for the dark design to ensure important icons remain visible.',
        },
        accentColor: {
          title: 'Accent color',
          info: "This color is used for the app's icons (e.g. for the tiles). We recommend using a relatively light color for this.",
        },
        buttonColor: {
          title: 'Button color',
          info: 'This color is used for all buttons in the app. This color should be darker than the icon color.',
        },
        lightTheme: 'Light theme',
        darkTheme: 'Dark theme',
      },
      warning:
        '<strong>Caution:</strong> This preview uses real data. <br /> If you have registered with a Microsoft account, you need to log in to the <0>app</0> first.',
    },
    benefitSorting: {
      title: 'Benefit Sorting',
      info: 'Change the order of displayed benefits in your app here. The settings can be adjusted independently by you.',
      subTitle: 'Order of Visualized Benefits',
      subInfo: `
    <p>Customize the order of benefits in the app individually via drag and drop.</p>
    <ul>
      <li>Changes are applied live in the app immediately after saving.</li>
      <li>Users only see the benefits that are activated.</li>
      <li>Flex benefits are automatically grouped under the "Flex" category. Flex benefits can not be sorted</li>
      <li>The benefit "Employee Communication" will only be displayed when articles are published.</li>
    </ul>`,
      successToast: 'Sorting successfully updated',
    },
    fiscalYear: {
      title: 'Fiscal Year & Deadlines',
      info: 'Manage the end of the fiscal year and receipt upload deadlines here.',
      successToast: 'Settings saved successfully',
      fiscalYearEnd: {
        title: 'End of fiscal year',
        info: 'This date can be relevant for receipt uploads and payouts. If nothing else is configured, 31 December applies as the end of the fiscal year.',
      },
      closeDelay: {
        title: 'Receipt upload deadline',
        info: 'Define how many days after the end of the billing period employees can still submit receipts.',
        unit: 'Days',
      },
    },
  },
  tutorialModule: {
    title: 'Set up Probonio Dashboard',
    buttonDone: 'Mark as completed',
    buttonOpenDrawer: 'Quick Start',
    steps: {
      tenantImage: {
        label: 'Company logo',
        description:
          'Upload your company logo as an image file. It will then be displayed both in your employer portal and in the employee app.',
        help: 'To do this, click on the "$t(tenantModule:customisationSettings.uploadLogo)" field in the "Corporate Design" tab in the company settings and upload your logo.',
        buttonText: 'Go to settings',
      },
      firstEmployee: {
        label: 'Create first employee',
        description: 'Now add your employees. You can enter multiple email addresses at once.',
        help: 'Click on the blue button "$t(usersPage:addUser)" at the top right of the "Employee Master data" page. To access the employee Master data, simply select "Employee Master data" from the overview column.',
        buttonText: 'Go to Master data',
      },
      configureBenefits: {
        label: 'Configure benefits',
        description: `Configure the benefits for your employees according to your preferences. Decide which benefits you want to offer and who will receive them, and in what amount.`,
        help: `Click on the desired benefit in the overview column. Select "Employees" in the submenu and then go to the "$t(benefitsModule:employeeTabs.tab.inactive)" tab. Tick the boxes next to the email addresses of the employees you want to select. Then click on the "$t(benefitsModule:employeeTabs.configureNow, {"count": 0})" button to make adjustments and activate the benefit. Alternatively, you can also enable benefits directly in a person's employee profile. To do this, open the Master data and select the relevant employee. Then click on the "Add benefit" button.`,
        buttonText: 'Go to Master data',
      },
      orderCouponBudget: {
        label: 'Order gift card budget',
        description: `For the benefit "Gift Cards", top up your required voucher budget. This budget must cover at least the total amount of vouchers that should be available to employees in the first month. 
        Vouchers can only be selected and handed out to employees if the budget is sufficiently covered.`,
        help: 'To top up the budget, click on the "$t(benefitsModule:COUPONS.title)" benefit in the overview column. Select the "Orders" tab in the submenu and then the "Balance" option at the top right. Then click on the "$t(couponsModule:budgetPanel.orderButton)" button. Alternatively, you can also wait for the automatic invoice that is sent to you according to your configurations, as long as "automatic top-up" is activated.',
        buttonText: 'Go to Budget Top-Up',
      },
      informEmployees: {
        label: 'Inform employees',
        description: 'Next, we recommend informing your employees about the start of your collaboration with Probonio.',
        help: "We're happy to assist you with a guide, which you can find under the 'Documents' section in your activated benefit.",
      },
      informEmployeesWithBls: {
        label: 'Inform employees',
        description: 'Next, we recommend informing your employees about the start of your collaboration with Probonio.',
        help: `We're happy to assist you with a guide, which you can find under the "Documents" section in your activated benefit, or under "Information & Support" for Bikeleasing.`,
      },

      inviteEmployee: {
        label: 'Invite employees',
        description: 'Now invite your employees to use the app after you have configured all the benefits and topped up the budget.',
        help: `To do this, click on the green "$t(common:userStatusButton.CREATED)" button behind the corresponding e-mail addresses. You'll find this under "Master data" in the "Registration" tab. Alternatively, you can invite all employees at once using the green button with the envelope icon at the top right. Please note: The benefits are only displayed in the app as soon as their start date has been reached.`,
        buttonText: 'Go to Master data',
      },

      sepaPayment: {
        label: 'Set up SEPA business-to-business direct debit (optional)',
        description: 'Set up your company direct debit mandate in just five steps.',
        help: 'Navigate to your company settings and click the blue "Select" button next to the SEPA B2B Direct Debit option to start the setup process. Follow the on-screen instructions to enter your company details and bank information. Please note that your mandate will only be activated once you receive a confirmation email. Until then, all invoices must still be paid via bank transfer.',
        buttonText: 'Go to settings',
      },
      employmentContract: {
        label: 'Supplementary agreement employment contract',
        description: 'If you wish, you can conclude a supplementary agreement to the employment contract with your employees',
        help: 'You can find a free template under the "Documents" section for each benefit.',
      },
      payrollReport: {
        label: 'Settlement of benefits',
        description: 'Now set up the integration with your payroll accounting and the payment and settlement of your benefits.',
        help: 'To do this, navigate to the desired benefit in the left-hand overview column and select the "Payroll export" option in the submenu. Please note that the payroll data export must be set up separately for each benefit. You can obtain information on your payroll accounting system and the wage types (de: Lohnarten) from your tax office or payroll accounting department.',
      },
      integrationHRSystem: {
        label: 'HR system integration',
        description:
          'Connect your HR system with Probonio so that employee data is automatically synchronised and always up to date. If desired, new employees can also be automatically invited to Probonio after being created in the HR system. No manual effort, more time for what matters.',
        buttonText: 'Go to HR integration',
      },
    },
  },
  bavModule: {
    contractActive: 'Start of contract on {{startDate, date}}',
    contractIntro:
      'The data of the concluded bAV contract is displayed below. Submit change requests to your responsible Probonio contact using the edit function.',
    contractAppInfo: 'Some of the data can be viewed by the employee in the Probonio-app.',
    requestChange: 'Request change',
    changesHint: '<0>*</0> = Changes in this field have not yet been submitted',
    requestContact: 'Contact request',
    enterComment:
      'Please enter your request below. We will process it immediately and contact you if we have any queries. Your contact details and the contract data are automatically transmitted as well.',
    sendContactRequest: 'Send',
    contactSuccessToast: 'Contact request submitted',
    submitChanges: 'Submit changes',
    confirmSubmit:
      'Do you really want to submit the changes marked with * ?\n\nThe changes will be sent to your responsible Probonio contact and will only be visible after processing.',
    submitSuccessToast: 'Changes have been submitted',
    pleaseUseContactForm: 'Please use the contact form to request a premium change',
    netValue: '~{{money, euro}} net',
    periodPremium: '{{money, euro}} payable',
    contractFields: {
      status: 'Status',
      salutation: 'Salutation',
      title: 'Title',
      firstName: 'First name',
      lastName: 'Last name',
      mail: 'Email',
      dateOfBirth: 'Date of birth',
      street: 'Street',
      city: 'City',
      postalCode: 'Postal code',
      country: 'Country',
      employeePremiumGross: 'Employee premium (gross)',
      employerGrant: 'Employer grant',
      vwlConversion: 'VWL conversion',
      totalPremium: 'Monthly total premium',
      startDate: 'Start date',
      mainDueDate: 'Main due date',
      paymentPeriod: 'Payment period',
      insuranceIBAN: 'IBAN of insurance',
      insuranceBIC: 'BIC of insurance',
      insuranceBankName: 'Bank name',
      insuranceName: 'Insurance name',
      insuranceType: 'Implementation path',
      contractNumber: 'Contract number',
      tariffName: 'Tariff name',
    },
    section: {
      user: 'Contractor',
      address: 'Address',
      contract: 'Contract data',
      billing: 'Billing',
      documents: 'Documents',
    },
    status: {
      ACTIVE: 'Active',
      SUSPENDED: 'Suspended',
      CANCELLED: 'Cancelled',
      TRANSFERRED: 'Transferred',
    },
    insuranceType: {
      DIRECT_INSURANCE: 'Direct insurance',
      PENSION_POOL: 'Pension pool',
      PENSION_FUND: 'Pension fund',
      RELIEF_FUND: 'Relief fund',
      DIRECT_COMMITMENT: 'Direct commitment',
    },
    paymentPeriod: {
      MONTHLY: 'Monthly',
      QUARTERLY: 'Quarterly',
      YEARLY: 'Yearly',
    },
    terms: {
      title: 'Supply Regulations',
      info: 'This integration allows you to integrate your supply regulations into the app and make them accessible to your employees.',
      submitSuccessToast: 'Changes have been applied',
      defaultTemplate: `<p><em>Haftungsausschluss: </em></p><p><em>Bitte beachten Sie, dass dieser Text ein unverbindliches Muster darstellt und im konkreten Einzelfall gegebenenfalls ergänzt und an die individuellen Gegebenheiten des Betriebs angepasst werden muss. Es kann in verschiedenen Fällen nicht geeignet sein, den gewünschten Zweck zu erzielen und ersetzt nicht die anwaltliche Leistung. Wir haben das Muster nach bestem Wissen erstellt. Eine Haftung für die Inhalte wird nicht übernommen. Das Musterschreiben erhebt keinen Anspruch auf Richtigkeit und Aktualität; es dient lediglich als Anregung und Hilfe für Formulierungen. </em></p><h1>Versorgungsordnung über betriebliche Altersversorgung</h1><p>der Muster GmbH </p><p>Musterallee 1 99999 </p><p>Musterhausen </p><p></p><p>(im Nachfolgenden „Arbeitgeber“ genannt) </p><p>Die Mitarbeiterinnen und Mitarbeiter werden im Nachfolgenden geschlechtsneutral „Arbeitnehmer“ genannt. </p><h2>A. Einleitung </h2><h3>1. Regelungsgegenstand </h3><p>Diese Versorgungsordnung regelt die Durchführung der betrieblichen Altersversorgung. Nach § 1a Abs. 1 Satz 1 des Betriebsrentengesetzes (BetrAVG) können Arbeitnehmer die Umwandlung künftiger Entgeltbestandteile zur Finanzierung einer Betriebsrente beanspruchen (Entgeltumwandlung). Der gesetzliche Anspruch auf Entgeltumwandlung ist auf 4 % der jeweiligen Beitragsbemessungsgrenze in der allgemeinen Rentenversicherung begrenzt. </p><h3>2. Teilnahmeberechtigter Personenkreis </h3><p>Das Angebot zur betrieblichen Altersversorgung durch Entgeltumwandlung richtet sich an alle Arbeitnehmer des Arbeitgebers. Voraussetzung ist eine Pflichtversicherung in der gesetzlichen Rentenversicherung. Die Teilnahme an der Entgeltumwandlung erfolgt auf Antrag des Arbeitnehmers. </p><h3>3. Durchführungsweg und Versorgungsträger </h3><p>Die Durchführung der betrieblichen Altersversorgung erfolgt ausschließlich über den Durchführungsweg Direktversicherung.</p><p>Der Arbeitgeber bietet dem Arbeitnehmer mittels Probonio die kostenfreie Möglichkeit für einen Versicherungsvergleich und ein Beratungsgespräch bei einem freien Versicherungsvermittler an, um eine passende Versicherung für seine individuelle Situation zu finden. Der Arbeitnehmer kann diese Versicherung als Vorschlag dem Arbeitgeber vorlegen. Der Arbeitgeber behält sich vor, Vorschläge des Arbeitnehmers zu prüfen, ist jedoch grundsätzlich offen für verschiedene Versicherungen.</p><p>Etwaige Altzusagen in anderen Durchführungswegen und von anderen Versorgungsträgern bleiben davon unberührt.</p><h3>4. Leistungsarten </h3><p>Als Leistungsarten kommen in Betracht: </p><ul><li><span>Altersversorgung</span></li><li><span>vorzeitige Altersversorgung </span></li><li><span>Hinterbliebenenversorgungsleistungen </span></li><li><span>Beitragsbefreiung bei Berufsunfähigkeit </span></li><li><span>Berufsunfähigkeitsrente </span></li><li><span>Grundfähigkeitsrente </span></li></ul><p>Die Festlegung der Leistungsarten erfolgt in einer individuellen Vereinbarung. </p><h2>B. Durchführungsweg Direktversicherung </h2><h3>1. Definition </h3><p>Bei einer Direktversicherung handelt es sich um eine Lebensversicherung (Rentenversicherung), die der Arbeitgeber auf das Leben seines Arbeitnehmers abschließt. Der Arbeitnehmer bzw. seine Hinterbliebenen erhalten ein Bezugsrecht und haben bei Eintritt des Versorgungsfalles einen direkten Rechtsanspruch auf die Leistungen gegen den Versorgungsträger. </p><h3>2. Versorgungszusage </h3><p>Die Versorgungszusagen werden auf Veranlassung des Arbeitgebers als beitragsorientierte Leistungszusage gem. § 1 Abs. 2 Nr. 1 BetrAVG erteilt. Der Arbeitnehmer erhält nach Vertragsabschluss vom Versorgungsträger eine schriftliche Versorgungszusage (Versicherungsschein), in der Art, Höhe und Voraussetzung der Versorgungsleistungen dokumentiert sind. </p><h3>3. Versorgungsleistungen </h3><p>Art und Höhe der Versorgungsleistungen richten sich nach dem Versorgungsbeitrag, den versicherungsmathematischen Grundlagen des gewählten Versicherungstarifs, dem Alter des Arbeitnehmers bei Versicherungsbeginn und der Versicherungsdauer. Weitere Einzelheiten über die Versorgungsleistungen und die maßgeblichen Versicherungsbedingungen sind im Versicherungsschein (Versorgungszusage) festgelegt. Die Versorgungsleistung wird bei Fälligkeit an den Arbeitnehmer bzw. dessen steuerlich zulässige Hinterbliebene direkt ausgezahlt. </p><h3>4. Mindest- und Höchstgrenzen </h3><p>Soweit eine Entgeltumwandlung durchgeführt wird, muss der Arbeitnehmer gem. § 1a Abs. 1 Satz 4 BetrAVG einen Betrag in Höhe von mindestens 1/160 der Bezugsgröße nach § 18 SGB IV jährlich umwandeln (in 2019: 233,63 EUR jährlich bzw. 19,47 EUR monatlich). Die Förderung durch steuerfreie Beiträge richtet sich nach § 3 Nr. 63 EStG. Hierbei haben arbeitgeberfinanzierte Beiträge Vorrang vor Beiträgen, die durch Entgeltumwandlung des Arbeitnehmers finanziert werden. Zusätzlich können unter den jeweiligen Voraussetzungen des § 100 und der §§ 10a, 82 Abs. 2 EStG die steuerlichen Begünstigungen (z. B. Steuerfreistellung) genutzt werden. Beiträge, die nach § 3 Nr. 63 EStG steuerfrei gestellt werden, sind bis zur Höhe von 4 % der BBG in der Rentenversicherung der Arbeiter und Angestellten nicht dem sozialversicherungspflichtigen Arbeitsentgelt zugeordnet (§ 1 Abs. 1 Nr. 9 SvEV). Die Leistungen der Direktversicherung sind steuer- und ggf. sozialabgabenpflichtig. </p><h3>5. Vorzeitiges Ausscheiden </h3><p>Scheidet der Arbeitnehmer vor Eintritt des Versorgungsfalles mit unverfallbaren Ansprüchen nach D.1. dieser Versorgungsordnung aus den Diensten des Arbeitgebers aus, so bleibt sein unverfallbarer Anspruch nach D.1. dieser Versorgungsordnung erhalten. Der Arbeitgeber beantragt innerhalb der gesetzlichen Fristen die Übertragung der Versicherungsnehmereigenschaft auf den ausgetretenen Arbeitnehmer und informiert diesen darüber (versicherungsvertragliches Verfahren). Die Versorgung kann entweder privat oder über einen neuen Arbeitgeber mit weiteren Beiträgen fortgeführt werden. Der Arbeitnehmer kann auch eine Übertragung nach § 4 Abs. 3 BetrAVG (Portabilität) verlangen. Wurde bei Ausscheiden das versicherungsvertragliche Verfahren durchgeführt, so kann er vom Versorgungsträger der Direktversicherung eine Übertragung innerhalb eines Jahres nach Beendigung des Arbeitsverhältnisses verlangen. </p><h3>6. Anpassung laufender Leistungen </h3><p>Bei der beitragsorientierten Leistungszusage werden sämtliche ab Rentenbeginn auf den Versicherungsvertrag entfallenden Überschussanteile zur Erhöhung der Versorgungsleistung verwendet. Eine zusätzliche Erhöhung durch den Arbeitgeber ist ausgeschlossen (§ 16 Abs. 3 Nr. 2 BetrAVG). </p><h2>C. Versorgungsbeitrag</h2><p>Der Versorgungsbeitrag setzt sich aus den folgenden Komponenten zusammen. </p><h3>1. Entgeltumwandlung </h3><p>Im Rahmen der Entgeltumwandlung kann jeder Arbeitnehmer entsprechend seiner wirtschaftlichen Leistungsfähigkeit Beträge aus seinem monatlichen Bruttoverdienst in den Grenzen des § 3 Nr. 63 EStG umwandeln. Dabei darf der Entgeltumwandlungsbetrag zusammen mit den Arbeitgeberleistungen zur betrieblichen Altersversorgung gemäß C.2. den Höchstbetrag von 8 % der Beitragsbemessungsgrenze der allgemeinen Rentenversicherung (West) nicht überschreiten. Die Höhe des Umwandlungsbetrages wird in einer separaten Entgeltumwandlungsvereinbarung festgelegt. Der vereinbarte Betrag kann nur jährlich, erstmalig nach Ablauf eines vollen Jahres ab Versorgungsbeginn, verändert werden. Der Antrag auf Entgeltumwandlung muss einen Monat vor Entgeltumwandlungsbeginn schriftlich bei einer geeigneten Stelle des Arbeitgebers vorliegen. Beispiel: Entgeltumwandlung aus dem Oktoberentgelt; Vereinbarung muss im September dem Arbeitgeber vorliegen. Soweit das Entgelt Bemessungsgrundlage für sonstige Vergütungen ist, wird der Arbeitnehmer, der sich für die Entgeltumwandlung entschieden hat, so gestellt, als ob eine Umwandlung nicht erfolgt wäre. Diese Regelung gilt auch für den Fall, dass Gehaltserhöhungen einheitlich oder nach Gruppen gestaffelt in bestimmter Relation zu den vorherigen Bezügen festgesetzt werden. </p><h3>2. Arbeitgeberzuschuss </h3><p>Macht der Arbeitnehmer von der Möglichkeit der Entgeltumwandlung Gebrauch, leistet der Arbeitgeber für die Dauer der Entgeltumwandlung einen Zuschuss in Höhe von 15,0 % des umgewandelten Betrages. Der Zuschuss wird für ab dem 01.01.2019 geschlossene Entgeltumwandlungsvereinbarungen sofort gewährt; für bis einschließlich 31.12.2018 geschlossene Entgeltumwandlungsvereinbarungen gewährt der Arbeitgeber den o. g. Zuschuss frühestens ab Inkrafttreten dieser Versorgungsordnung. Dieser Arbeitgeberzuschuss wird nur für Entgeltumwandlungsbeiträge gewährt, die im Rahmen von § 3 Nr. 63 EStG geleistet werden. Darüber liegende Entgeltumwandlungsbeträge werden von der Bezuschussung nicht umfasst. Erhält der Arbeitnehmer bereits vor Inkrafttreten dieser Versorgungsordnung zu einer Entgeltumwandlung einen freiwilligen Arbeitgeberzuschuss wird dieser auf den o. g. Zuschuss angerechnet, sofern es sich um den gleichen Durchführungsweg handelt. Der o. g. Zuschuss resultiert insbesondere aus der Sozialversicherungsersparnis, die der Arbeitgeber durch die vollständige oder anteilige Sozialversicherungsfreiheit von Entgeltumwandlungsbeträgen erzielt. Der gesetzlich verpflichtende Arbeitgeberzuschuss gemäß § 1a Abs. 1a BetrAVG wird auf den o. g. Zuschuss, der im Rahmen des versicherungsförmigen Durchführungswegs vom Arbeitgeber geleistet wird, angerechnet. Für entgeltfreie Beschäftigungszeiten gelten die Regelungen nach D.2. dieser Versorgungsordnung. Eine unmittelbare Auszahlung des Arbeitgeberzuschusses zur Entgeltumwandlung ist nicht zulässig. </p><h2>D. Allgemeiner Teil </h2><h3>1. Unverfallbarkeit bei vorzeitigem Ausscheiden </h3><h3>a. Unverfallbarkeit dem Grunde nach </h3><p>Endet das Arbeitsverhältnis vor Eintritt des Versorgungsfalles, so erwirbt der Arbeitnehmer entsprechend den Vorschriften des Betriebsrentengesetzes eine unverfallbare Anwartschaft auf Versorgungsleistungen, sofern die hierfür erforderlichen Fristen erfüllt sind. Der Anspruch auf Anwartschaft aus Entgeltumwandlung ist ab Beginn unverfallbar. Für die Anwartschaft aus dem Arbeitgeberzuschuss gem. C.2. dieser Versorgungsordnung gilt ebenfalls die sofortige Unverfallbarkeit. </p><h3>b. Unverfallbarkeit der Höhe nach </h3><p>Scheidet der Arbeitnehmer vor Erreichen des Versorgungsfalles aus dem Unternehmen aus, gilt bei der Zusageform der beitragsorientierten Leistungszusage Folgendes: Bei vorzeitigem Ausscheiden des Arbeitnehmers aus den Diensten des Arbeitgebers bleibt sein Anspruch aus der Direktversicherung gem. § 2 Abs. 2 BetrAVG erhalten. </p><h3>2. Entgeltfreie Zeiten </h3><p>Der Versorgungsbeitrag wird vom Arbeitgeber nur solange und soweit in der vereinbarten Höhe an den Versorgungsträger gezahlt, wie er zur Zahlung der Entgelte aus dem Arbeitsverhältnis verpflichtet gewesen ist. Besteht das Beschäftigungsverhältnis ohne Entgeltanspruch fort (z. B. längere Krankheit, Elternzeit, unbezahlter Urlaub), entrichtet der Arbeitgeber keinen Versorgungsbeitrag mehr an den Versorgungsträger. Dadurch reduziert sich der Leistungsanspruch des Arbeitnehmers. Der Arbeitnehmer hat jedoch innerhalb des in § 3 Nr. 63 EStG beschriebenen Durchführungswegs Direktversicherung die Möglichkeit, den Versorgungsbeitrag aus eigenen Mitteln weiter zu zahlen. </p><h3>3. Versorgungsausgleich</h3><p>Erfolgt nach einer Scheidung oder Auflösung einer eingetragenen Lebenspartnerschaft ein Urteil über den Versorgungsausgleich, so werden die Versorgungsansprüche real geteilt. Die weiteren Regelungen zum Versorgungsausgleich richten sich nach der jeweils gültigen Teilungsordnung des betreffenden Versorgungsträgers. Haftungsausschluss: Bitte beachten Sie, dass dieser Text ein unverbindliches Muster darstellt und im konkreten Einzelfall gegebenenfalls ergänzt und an die individuellen Gegebenheiten des Betriebs angepasst werden muss. Es kann in verschiedenen Fällen nicht geeignet sein, den gewünschten Zweck zu erzielen und ersetzt nicht die anwaltliche Leistung. Wir haben das Muster nach bestem Wissen erstellt. Eine Haftung für die Inhalte wird nicht übernommen. Das Musterschreiben erhebt keinen Anspruch auf Richtigkeit und Aktualität; es dient lediglich als Anregung und Hilfe für Formulierungen.  </p><h3>4. Mitwirkungspflichten des Arbeitnehmers </h3><p>Der Arbeitnehmer ist im Rahmen der Antragstellung auf betriebliche Altersversorgung verpflichtet, beim Zustandekommen des Versorgungsvertrages mitzuwirken. Insbesondere muss er dem Abschluss eines Versicherungsvertrages auf sein Leben zustimmen. Unterlässt er dies, so entsteht kein Anspruch auf die Versorgungsleistungen. Ansprüche aus der betrieblichen Altersversorgung dürfen vom Arbeitnehmer weder abgetreten, verpfändet noch beliehen werden. Kommt der Arbeitnehmer seinen Mitwirkungspflichten nicht nach, so kann es zu einem Ruhen des Anspruchs kommen. Dies ist insbesondere der Fall, wenn der Arbeitnehmer bei Fälligkeit der Leistungen die zur Auszahlung der Leistungen notwendigen Unterlagen nicht beibringt. Eine Nachzahlung der Leistungen erfolgt - unter Berücksichtigung von Verjährungsfristen - erst nach Erfüllung der Mitwirkungspflichten. </p><h3>5. Beratung und Versicherungsauswahl</h3><p>Die Probonio GmbH ist mit der Beratung und Betreuung zur betrieblichen Altersversorgung beauftragt. Ziel ist es, den Arbeitnehmern einen transparenten Vergleich verschiedener Versicherungsanbieter anzubieten und eine umfassende digitale Beratung zu gewährleisten. Über die Benefit-App von Probonio kann der Arbeitnehmer einen Beratungstermin vereinbaren. Auch nach dem Abschluss einer Versicherung bleibt Probonio als Ansprechpartner zur Verfügung. Die bAV Berater von Probonio sind sowohl über die Support-Hotline als auch per E-Mail unter <a href="mailto:support@probonio.de" target="_blank">support@probonio.de</a> erreichbar.</p><h3>6. Salvatorische Klausel und Schlusserklärung </h3><p>Ergänzend finden die Regelungen des Betriebsrentengesetzes (BetrAVG) Anwendung. Sollten eine oder mehrere Bestimmungen dieser Versorgungsordnung unwirksam sein oder werden, bleiben die übrigen Bestimmungen dieser Versorgungsordnung gleichwohl wirksam. </p><h3>7. Laufzeit </h3><p>Diese Versorgungsordnung tritt mit ihrer Unterzeichnung in Kraft. Diese Versorgungsordnung ist für unbestimmte Dauer gültig und mit einer Frist von 3 Monaten kündbar. Die Kündigung bedarf der Schriftform. Der Arbeitgeber macht die Kündigung an geeigneter Stelle im Unternehmen bekannt oder informiert die Arbeitnehmer persönlich über die erfolgte Kündigung. Die Nachwirkung dieser Versorgungsordnung wird ausgeschlossen.</p>`,
      showToEmployees: 'Display Supply Regulations to Employees',
      integration: 'Integration of Your Supply Regulations',
      saveTooltip: 'Please modify your supply regulation settings to save a new version',
      guide: {
        title: 'Company Pension Scheme Supply Regulations',
        info: "<p>A <strong>Supply Regulation</strong> in company pension schemes is a crucial instrument for retirement planning in businesses. It ensures transparency and legal certainty for employers and employees by defining clear conditions for the granting of company pension benefits, including eligibility requirements, contribution levels, and calculation methods for pension benefits, as well as other benefits such as survivor and disability pensions. This <strong>Supply Regulation</strong> forms the basis for retirement planning and allows for individual adaptation to the company's requirements.</p>",
        section1Title: 'How to Integrate Your Custom Supply Regulation',
        section1Text: `
      <ol>
        <li><strong>Customize the template</strong> using the editor according to your requirements.</li>
        <li><strong>Save your template</strong> and edit it at any time.</li>
        <li><strong>Enable "Display Supply Regulations to Employees"</strong> to publish it in the employee app.</li>
      </ol>
    `,
      },
    },
    documentType: {
      POLICY: 'Policy',
      DEFERRED_COMPENSATION: 'Deferred compensation',
      CHANGE_REQUEST: 'Change request',
      REQUEST: 'Request',
      OTHER: 'Other',
    },
    contractsTable: {
      header: {
        status: 'Status',
        name: 'Employee',
        startDate: 'Start of contract',
        totalPremium: 'Total premium',
        insuranceName: 'Insurance name',
        contractNumber: 'Contract number',
      },
      noResults: 'No pension plan contracts found',
    },
  },
  bikeModule: {
    contractActive: 'Contract concluded',
    contractActiveDate: 'Start of contract on {{startDate, date}}',
    contractIntro:
      'The data of the concluded company bike contract is displayed below. For changes, please upload the contract data again on the overview page of the company bike benefit.',
    contractAppInfo: 'The data can be viewed by the employee in the Probonio-app.',
    importTab: 'Import',
    importButton: 'Import contract data',
    importButtonHint: 'Import the contract data from your provider portal here regularly.',
    contractFields: {
      contractNumber: 'Contract number',
      contractDate: 'Contract date',
      status: 'Status',
      leasingStartDate: 'Start of leasing',
      leasingEndDate: 'End of leasing',
      priceNet: 'Bike price (net)',
      monthlyServiceRate: 'Service rate',
      monthlyInsuranceRate: 'Insurance rate',
      employerGrant: 'Employer grant',
      bikeSupplier: 'Supplier / Dealer',
      bikeProduct: 'Product',
      bikeType: 'Type',
    },
    section: {
      contract: 'Contract information',
      leasing: 'Leasing',
      bike: 'Bike data',
      benefits: 'Your services',
    },
    contractsTable: {
      header: {
        status: 'Status',
        name: 'Employee',
        leasingStartDate: 'Start of leasing',
        leasingEndDate: 'End of leasing',
        contractNumber: 'Contract number',
      },
      noResults: 'No company bike contracts found',
      deleteButton: 'Delete contract',
      confirmDelete: 'Are you sure you want to delete the selected contract?',
      deleteContractSuccess: 'Contract deleted',
    },
    importDialog: {
      title: 'Company bike contract data import',
      dataPrivacyAlert:
        'Attention! You are about to import all records with personal data of your employees. Please make sure that all data is correct before proceeding.',
      templateInfo:
        'Download the import template and fill it with the data of your company wheel contracts. The JobRad Management Portal export format is also directly supported.',
      downloadTemplate: 'Download template',
      state: {
        initial: 'Select the completed template to begin importing',
        ready: '{{count}} records ready for import',
        success: '{{count}} records imported successfully',
        error: 'Import failed due to line number: {{lineNumber}}',
      },
      conflictError: `Employee with number {{employeeNumber}} not found`,
    },
  },
  dashboardModule: {
    employeesPanel: {
      title: 'Employees',
      createdAt: 'Added on {{date,date}}',
      recentCreatedAt: 'Recently added',
      totalCount: 'Total count: {{count}}',
      goToEmployeeList: 'To employee list',
      activeEmployeesCount: '{{count}} employees',
      activeStatusText: 'active',
      legendLabel: 'Employees',
    },
    benefitsPanel: {
      title: 'Benefits',
      disclaimer:
        '<p>Savings in the last 6 months compared to distribution of the same net amount via regular salary payments.</p><p>The calculated values always depend on factors such as tax class, marital status or the federal state. The calculation serves as a guide only and does not constitute tax or legal advice.</p><i>Our calculation basis: year 2023; €4,000 gross salary; tax class I; no children; CV 14.6%; Additional contribution 1.1%; KiSt 8%; State of Bavaria, employer takes over insurance and inspection.</i>',
      pauseCarouselTooltip: 'Pause slideshow',
      resumeCarouselTooltip: 'Resume slideshow',
      totalSavings: 'Savings: <0>{{amount, euro}}</0> / 6 months',
      totalGrantTitle: 'Distributions',
      totalGrant: 'Distributions: <0>{{amount, euro}}</0> / 6 months',
      benefitGrant: '<0>{{amount, euro}}</0> distributed',
      benefitSavings: '<0>{{amount, euro}}</0> saved',
      upsellingRate: '<0>{{value,percent}}</0> of our customers are using this benefit.',
      bookNow: 'Book now',
      benefitContent: {
        BAV: '<0>{{count}}</0> active contracts',
        BAV_one: '<0>{{count}}</0> active contract',
        BIKE: '<0>{{count}}</0> active contracts',
        BIKE_one: '<0>{{count}}</0> active contract',
        NEWS: '<0>{{count}}</0> active articles',
        NEWS_one: '<0>{{count}}</0> active article',
      },
      chart: {
        scaleFormat: '{{value, euro-float}}',
        tooltipItem: '{{label}}: {{value, euro-float}}',
        tooltipTotal: 'Total: {{value, euro-float}}',
        emptyTitle: 'No data',
        emptyText: 'Benefits at a glance shows your distributions over the last few months. Use our benefits to view your statistics.',
      },
    },
  },

  equivalenceCalculator: {
    title: 'Salary concept',
    subtitle: 'Evaluation',
    inputFields: {
      title: 'Gross Equivalence Calculator',
      fixed: 'Fixed',
      standard: 'Standard',
      withCompany: 'With Probonio',
      grossPerYear: 'Gross Salary per Year',
      netPerYear: 'Net Salary per Year',
      tenantCostPerYear: 'Employer Costs per Year',
      grossCalculated: 'Gross Equivalence: €{{value}}',
      netCalculatedMore: 'Employee receives: €{{value}} more',
      netCalculatedSame: 'Net Salary remains the same',
      tenantCalculatedMore: 'Employer saves €{{value}} in costs',
      tenantCalculatedSame: 'Employer saves no costs',
      calculateButton: 'Calculate',
    },
    graph: {
      title: 'Distribution',
      NET_INCOME: 'Net Income',
      AGE_INSURANCE: 'Unemployment Insurance',
      CARE_INSURANCE: 'Long-term Care Insurance',
      RENT_INSURANCE: 'Pension Insurance',
      SICK_INSURANCE: 'Health Insurance',
      WAGE_TAX: 'Income Tax',
      SOLIDARITY_TAX: 'Solidarity Surcharge',
      BENEFITS: 'Benefits',
    },
    personalConfiguration: {
      title: 'Employee Configuration',
      taxClass: 'Tax Class',
      insuranceType: {
        title: 'Health Insurance',
        lawful: 'Legal',
        private: 'Private',
      },
      privateInsuranceAmount: 'Monthly Contribution',
      children: 'Children',
      church: 'Church',
    },
    benefitConfiguration: {
      title: 'Benefit Configuration',
      lunchPerMonth: 'Tax-free Meal Allowance Quantity per Month',
      voucherPerMonth: 'Non-cash Benefit per Month',
      giftsPerYear: 'Gifts (Each €60) per Year',
      mobilityPerMonth: 'Tax-free Mobility per Month',
      internetPerMonth: 'Internet Allowance per Month',
      recreationPerMonth: 'Recreation Aid per Additional Child per Month',
    },
    warningToast: 'Calculation currently not available',
    infoTextFull: `Using the Gross Equivalence Calculator, you can get a rough idea of possible salaries and tax deductions.
    <br><br>
    Some things to consider when using it:
    <ul>
    <li>The salary calculator is solely intended to give the user an approximate idea of possible salaries and tax deductions.</li>
    <li>Please note that the calculated values do not constitute individual tax advice. Every tax situation is unique and requires an individual analysis by a qualified tax advisor.</li>
    <li>Use of the salary calculator is at your own risk. We do not take responsibility for any damages or losses arising from the use of the calculated information.</li>
    </ul>`,
  },
  benefitInfoText: {
    videosTitle: 'Helpful Resources',
    videosMoreTitle: 'More about',
    videosDescription:
      'We have prepared helpful video tutorials to provide you and your employees with optimum support during setup and use.',
    managerGeneral: 'Employer Portal General',
    appGeneral: 'App General',
  },
  legalTerms: {
    title: 'Legal Notices',
    longTitle: 'Legal Notices and Regulatory Documents',
    guardTitle: 'Welcome to Probonio!',
    guardSubtitle: 'In order to use our services, <br />we require your confirmation of the Probonio Terms of Service.',
    version: 'Version {{version}}',
    activeSince: 'Active since $t(common:date)',
    actions: {
      accept: 'Accept',
      skip: 'Skip for now',
      viewDocument: 'View Document',
    },
    labels: {
      checkTOS:
        'By using the services, I accept the terms and conditions set out in the <0>Terms of Service</0> and, as the responsible party, conclude a <1>Data Processing Agreement DPA</1> with the provider in accordance with Art. 28 DSGVO.',
      checkPrivacy: 'You can find out how we process your data in our <0>Privacy Policy</0>.',
      additionalDocuments: 'Additional Documents',
    },
    types: {
      PRIVACY: 'Privacy Policy',
      TENANT_TERMS: 'Terms of Service and Data Processing Agreement',
      DATA_PROCESSING_AGREEMENT: 'Data Processing Agreement (DPA)',
      TECHNICAL_ORGANIZATIONAL_MEASURES: 'Technical and Organizational Measures (TOM)',
      SERVICE_DESCRIPTION: 'Service Description',
    },
  },
  tenantHierarchy: {
    employeesCount: '{{count}} Employees',
    benefitsCount: '{{count}} Benefits',
    activeTenant: 'Selected',
  },
  userNotFoundPage: {
    title: 'No active account found',
    details:
      'No active account was found for your login credentials. Please complete the registration first to use Probonio. <p>Would you like to become a Probonio customer? Register at <0>{{targetDomain}}</0> or contact us for more information.</p>',
    register: 'Complete Registration',
    logout: 'Logout',
  },
};

export default translation;
